var __dispose = Symbol.dispose || /* @__PURE__ */ Symbol.for("Symbol.dispose");
var __asyncDispose = Symbol.asyncDispose || /* @__PURE__ */ Symbol.for("Symbol.asyncDispose");
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function")
      throw TypeError('Object expected to be assigned to "using" declaration');
    var dispose;
    if (async)
      dispose = value[__asyncDispose];
    if (dispose === undefined)
      dispose = value[__dispose];
    if (typeof dispose !== "function")
      throw TypeError("Object not disposable");
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  }, fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e), next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0])
          return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError)
      throw error;
  };
  return next();
};

// src/audio/audio.tsx
import { useMemo as useMemo3, useState as useState3 } from "react";
import {
  Freeze,
  Internals as Internals20,
  Interactive,
  Sequence,
  useRemotionEnvironment as useRemotionEnvironment2,
  useVideoConfig as useVideoConfig2
} from "remotion";

// src/show-in-timeline.ts
import { Internals } from "remotion";
var getLoopDisplay = ({
  loop,
  mediaDurationInSeconds,
  playbackRate,
  trimAfter,
  trimBefore,
  sequenceDurationInFrames,
  compFps
}) => {
  if (!loop || !mediaDurationInSeconds) {
    return;
  }
  const durationInFrames = Internals.calculateMediaDuration({
    mediaDurationInFrames: mediaDurationInSeconds * compFps,
    playbackRate,
    trimAfter,
    trimBefore
  });
  const maxTimes = sequenceDurationInFrames / durationInFrames;
  return {
    numberOfTimes: maxTimes,
    startOffset: 0,
    durationInFrames
  };
};

// src/validate-tone-frequency.ts
var validateToneFrequency = ({
  toneFrequency,
  component
}) => {
  if (toneFrequency === undefined) {
    return;
  }
  if (typeof toneFrequency !== "number" || !Number.isFinite(toneFrequency) || toneFrequency < 0.01 || toneFrequency > 2) {
    throw new TypeError(`The \`toneFrequency\` prop of <${component}> must be a finite number between 0.01 and 2, but got ${String(toneFrequency)}.`);
  }
};

// src/video/get-video-sequence-duration.ts
var getMediaTrimAfter = ({
  durationInFrames,
  trimAfter,
  trimBefore
}) => {
  const durationAsTrimAfter = durationInFrames === undefined ? undefined : (trimBefore ?? 0) + durationInFrames;
  if (durationAsTrimAfter === undefined) {
    return trimAfter;
  }
  return trimAfter === undefined ? durationAsTrimAfter : Math.min(trimAfter, durationAsTrimAfter);
};
var getVideoSequenceDuration = ({
  durationInFrames,
  loop,
  playbackRate,
  trimAfter,
  trimBefore
}) => {
  if (loop) {
    return;
  }
  const trimDuration = trimAfter === undefined ? Infinity : trimAfter - (trimBefore ?? 0);
  const contentDuration = Math.min(durationInFrames ?? Infinity, trimDuration);
  return contentDuration === Infinity ? undefined : contentDuration / playbackRate;
};

// src/audio/audio-for-preview.tsx
import { useContext as useContext2, useEffect, useMemo, useRef as useRef2, useState } from "react";
import {
  Internals as Internals10,
  Audio as RemotionAudio,
  useBufferState,
  useCurrentFrame,
  useVideoConfig
} from "remotion";

// src/get-time-in-seconds.ts
import { Internals as Internals2 } from "remotion";
var getTimeInSeconds = ({
  loop,
  mediaDurationInSeconds,
  unloopedTimeInSeconds,
  src,
  trimAfter,
  trimBefore,
  fps,
  playbackRate,
  ifNoMediaDuration
}) => {
  if (mediaDurationInSeconds === null && loop && ifNoMediaDuration === "fail") {
    throw new Error(`Could not determine duration of ${src}, but "loop" was set.`);
  }
  const loopDurationInFrames = loop ? Internals2.calculateMediaDuration({
    trimAfter,
    mediaDurationInFrames: mediaDurationInSeconds ? mediaDurationInSeconds * fps : Infinity,
    playbackRate: 1,
    trimBefore
  }) : Infinity;
  const sourceFrames = unloopedTimeInSeconds * playbackRate * fps;
  const loopedFrames = sourceFrames % loopDurationInFrames;
  const roundingTolerance = Number.EPSILON * Math.max(1, Math.abs(sourceFrames)) * 4;
  const timeInSeconds = loop ? (Math.abs(loopDurationInFrames - loopedFrames) < roundingTolerance ? 0 : loopedFrames) / fps : unloopedTimeInSeconds * playbackRate;
  if ((trimAfter ?? null) !== null && !loop) {
    const time = (trimAfter - (trimBefore ?? 0)) / fps;
    if (timeInSeconds >= time) {
      return null;
    }
  }
  return timeInSeconds + (trimBefore ?? 0) / fps;
};
var calculateEndTime = ({
  mediaDurationInSeconds,
  ifNoMediaDuration,
  src,
  trimAfter,
  trimBefore,
  fps
}) => {
  if (mediaDurationInSeconds === null && ifNoMediaDuration === "fail") {
    throw new Error(`Could not determine duration of ${src}, but "loop" was set.`);
  }
  const mediaDuration = Internals2.calculateMediaDuration({
    trimAfter,
    mediaDurationInFrames: mediaDurationInSeconds ? mediaDurationInSeconds * fps : Infinity,
    playbackRate: 1,
    trimBefore
  }) / fps;
  return mediaDuration + (trimBefore ?? 0) / fps;
};

// src/looped-frame.ts
var frameForVolumeProp = ({
  behavior,
  loop,
  assetDurationInSeconds,
  fps,
  frame,
  startsAt,
  playbackRate,
  trimBefore,
  trimAfter
}) => {
  if (!loop) {
    return frame + startsAt;
  }
  if (behavior === "extend") {
    return frame + startsAt;
  }
  const sourceTime = getTimeInSeconds({
    loop: true,
    mediaDurationInSeconds: assetDurationInSeconds,
    unloopedTimeInSeconds: frame / fps,
    src: "",
    trimBefore,
    trimAfter,
    playbackRate,
    fps,
    ifNoMediaDuration: "infinity"
  });
  const frameInLoop = (sourceTime * fps - (trimBefore ?? 0)) / playbackRate;
  return Math.max(0, Math.min(frame + startsAt, frameInLoop));
};

// src/media-player.ts
import { Internals as Internals7 } from "remotion";

// src/audio-iterator-manager.ts
import {
  AudioBufferSink,
  InputDisposedError
} from "mediabunny";
import { Internals as Internals3 } from "remotion";

// src/audio/audio-preview-iterator.ts
var makeAudioIterator = ({
  startFromSecond,
  iterator,
  scheduleChunk,
  unscheduleAudioNode
}) => {
  let destroyed = false;
  const queuedAudioNodes = new Map;
  let queuedFrom = Infinity;
  let queuedUntil = -Infinity;
  const onEnded = (event) => {
    const node = event.currentTarget;
    queuedAudioNodes.delete(node);
    node.disconnect();
  };
  let mostRecentTimestamp = -Infinity;
  const clearSchedule = () => {
    for (const node of queuedAudioNodes.keys()) {
      node.removeEventListener("ended", onEnded);
      node.disconnect();
      if (unscheduleAudioNode(node) === true) {
        continue;
      }
      try {
        node.stop();
      } catch {}
    }
    queuedAudioNodes.clear();
    queuedFrom = Infinity;
    queuedUntil = -Infinity;
  };
  const scheduleAudio = (chunk) => {
    if (destroyed)
      return;
    const node = scheduleChunk(chunk);
    if (!node)
      return;
    queuedFrom = Math.min(queuedFrom, chunk.timestamp);
    queuedUntil = Math.max(queuedUntil, chunk.timestamp + chunk.sourceDurationInSeconds);
    queuedAudioNodes.set(node, chunk);
    node.addEventListener("ended", onEnded, { once: true });
  };
  const getNextFn = async () => {
    const next = await iterator.next();
    if (next.value) {
      mostRecentTimestamp = Math.max(mostRecentTimestamp, next.value.timelineTimestamp + next.value.sourceDurationInSeconds);
    }
    return next;
  };
  return {
    destroy: () => {
      clearSchedule();
      destroyed = true;
      iterator.return().catch(() => {
        return;
      });
    },
    getNextFn,
    isDestroyed: () => {
      return destroyed;
    },
    scheduleAudio,
    rescheduleAudio: (time) => {
      const queued = [...queuedAudioNodes.values()];
      if (destroyed || !queued.some((item) => time >= item.timestamp && time < item.timestamp + item.sourceDurationInSeconds)) {
        return false;
      }
      clearSchedule();
      for (const chunk of queued)
        scheduleAudio(chunk);
      return true;
    },
    guessNextTimestamp: () => {
      return !Number.isFinite(mostRecentTimestamp) ? startFromSecond : mostRecentTimestamp;
    },
    getQueuedPeriod: () => {
      const from = queuedFrom;
      const until = queuedUntil;
      if (!Number.isFinite(from) || !Number.isFinite(until)) {
        return null;
      }
      return {
        from,
        until
      };
    }
  };
};
var isAlreadyQueued = (time, queuedPeriod) => {
  if (!queuedPeriod) {
    return false;
  }
  return time >= queuedPeriod.from && time < queuedPeriod.until;
};

// src/audio/get-scheduled-time.ts
var getScheduledTime = ({
  mediaTimestamp,
  targetTime,
  currentTime,
  sequenceStartTime
}) => {
  const needsTrimStart = mediaTimestamp < sequenceStartTime;
  const offsetBecauseOfTrim = needsTrimStart ? sequenceStartTime - mediaTimestamp : 0;
  const offsetBecauseOfTooLate = targetTime < 0 ? -targetTime : 0;
  const offset = offsetBecauseOfTrim + offsetBecauseOfTooLate;
  const scheduledTime = targetTime + currentTime + offset;
  return scheduledTime;
};
var getDurationOfNode = ({
  sourceDurationInSeconds,
  sourceOffsetInSeconds,
  offset
}) => {
  return Math.max(0, sourceDurationInSeconds - (offset - sourceOffsetInSeconds));
};
var getTrimStartForAudioNode = ({
  mediaTimestamp,
  targetTime,
  sequenceStartTime,
  combinedPlaybackRate,
  sourceStartOffsetInSeconds
}) => {
  const needsTrimStart = mediaTimestamp < sequenceStartTime;
  const offsetBecauseOfTrim = needsTrimStart ? sequenceStartTime - mediaTimestamp : 0;
  const offsetBecauseOfTooLate = targetTime < 0 ? -targetTime * combinedPlaybackRate : 0;
  return sourceStartOffsetInSeconds + offsetBecauseOfTrim + offsetBecauseOfTooLate;
};

// src/audio/streaming-pitch-shifter.ts
var REFERENCE_SAMPLE_RATE = 48000;
var REFERENCE_HOP_SIZE = 512;
var makePlanarAudio = (numberOfChannels, length) => {
  return new Array(numberOfChannels).fill(null).map(() => new Float32Array(length));
};
var ensurePlanarCapacity = ({
  buffers,
  requiredLength
}) => {
  if (buffers[0].length >= requiredLength) {
    return buffers;
  }
  let newLength = buffers[0].length;
  while (newLength < requiredLength) {
    newLength *= 2;
  }
  return buffers.map((buffer) => {
    const expanded = new Float32Array(newLength);
    expanded.set(buffer);
    return expanded;
  });
};

class PlanarAudioQueue {
  chunks = [];
  length = 0;
  push(audio) {
    if (audio[0].length === 0) {
      return;
    }
    this.chunks.push(audio);
    this.length += audio[0].length;
  }
  take(numberOfFrames, numberOfChannels) {
    const framesToTake = Math.min(numberOfFrames, this.length);
    const result = makePlanarAudio(numberOfChannels, framesToTake);
    let written = 0;
    while (written < framesToTake) {
      const first = this.chunks[0];
      const available = first[0].length;
      const count = Math.min(available, framesToTake - written);
      for (let channel = 0;channel < numberOfChannels; channel++) {
        result[channel].set(first[channel].subarray(0, count), written);
      }
      if (count === available) {
        this.chunks.shift();
      } else {
        this.chunks[0] = first.map((channel) => channel.subarray(count));
      }
      written += count;
      this.length -= count;
    }
    return result;
  }
  getLength() {
    return this.length;
  }
}

class StreamingTimeStretcher {
  numberOfChannels;
  factor;
  hopSize;
  windowSize;
  searchRadius;
  analysisHop;
  input;
  inputLength = 0;
  output;
  outputLength = 0;
  analysisPosition = 0;
  synthesisPosition = 0;
  initialized = false;
  finalized = false;
  totalInputFrames = 0;
  totalOutputFrames = 0;
  constructor({
    numberOfChannels,
    sampleRate,
    factor
  }) {
    this.numberOfChannels = numberOfChannels;
    this.factor = factor;
    this.hopSize = Math.max(32, Math.round(REFERENCE_HOP_SIZE * sampleRate / REFERENCE_SAMPLE_RATE));
    this.windowSize = this.hopSize * 2;
    this.searchRadius = this.hopSize;
    this.analysisHop = this.hopSize / factor;
    this.input = makePlanarAudio(numberOfChannels, 65536);
    this.output = makePlanarAudio(numberOfChannels, 65536);
  }
  append(audio) {
    if (this.finalized) {
      throw new Error("Cannot append audio after the time stretcher was finalized.");
    }
    const { length } = audio[0];
    this.input = ensurePlanarCapacity({
      buffers: this.input,
      requiredLength: this.inputLength + length
    });
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.input[channel].set(audio[channel], this.inputLength);
    }
    this.inputLength += length;
    this.totalInputFrames += length;
    this.process();
    return this.drainFinalizedOutput();
  }
  findBestAnalysisPosition({
    expectedPosition,
    nextSynthesisPosition
  }) {
    const minimum = Math.max(0, Math.floor(expectedPosition - this.searchRadius));
    const maximum = Math.min(this.inputLength - this.windowSize, Math.ceil(expectedPosition + this.searchRadius));
    let bestPosition = minimum;
    let bestCorrelation = -Infinity;
    for (let candidate = minimum;candidate <= maximum; candidate += 4) {
      let dotProduct = 0;
      let previousEnergy = 0;
      let candidateEnergy = 0;
      for (let channel = 0;channel < this.numberOfChannels; channel++) {
        const previous = this.output[channel];
        const incoming = this.input[channel];
        for (let frame = 0;frame < this.hopSize; frame += 2) {
          const previousValue = previous[nextSynthesisPosition + frame];
          const candidateValue = incoming[candidate + frame];
          dotProduct += previousValue * candidateValue;
          previousEnergy += previousValue * previousValue;
          candidateEnergy += candidateValue * candidateValue;
        }
      }
      const correlation = dotProduct / (Math.sqrt(previousEnergy * candidateEnergy) || Number.EPSILON);
      if (correlation > bestCorrelation) {
        bestCorrelation = correlation;
        bestPosition = candidate;
      }
    }
    const fineMinimum = Math.max(minimum, bestPosition - 4);
    const fineMaximum = Math.min(maximum, bestPosition + 4);
    for (let candidate = fineMinimum;candidate <= fineMaximum; candidate++) {
      let dotProduct = 0;
      let previousEnergy = 0;
      let candidateEnergy = 0;
      for (let channel = 0;channel < this.numberOfChannels; channel++) {
        const previous = this.output[channel];
        const incoming = this.input[channel];
        for (let frame = 0;frame < this.hopSize; frame++) {
          const previousValue = previous[nextSynthesisPosition + frame];
          const candidateValue = incoming[candidate + frame];
          dotProduct += previousValue * candidateValue;
          previousEnergy += previousValue * previousValue;
          candidateEnergy += candidateValue * candidateValue;
        }
      }
      const correlation = dotProduct / (Math.sqrt(previousEnergy * candidateEnergy) || Number.EPSILON);
      if (correlation > bestCorrelation) {
        bestCorrelation = correlation;
        bestPosition = candidate;
      }
    }
    return bestPosition;
  }
  process() {
    if (!this.initialized) {
      if (this.inputLength < this.windowSize + this.searchRadius) {
        return;
      }
      for (let channel = 0;channel < this.numberOfChannels; channel++) {
        this.output[channel].set(this.input[channel].subarray(0, this.windowSize));
      }
      this.outputLength = this.windowSize;
      this.initialized = true;
    }
    while (true) {
      const expectedPosition = this.analysisPosition + this.analysisHop;
      if (expectedPosition + this.searchRadius + this.windowSize > this.inputLength) {
        break;
      }
      const nextSynthesisPosition = this.synthesisPosition + this.hopSize;
      this.output = ensurePlanarCapacity({
        buffers: this.output,
        requiredLength: nextSynthesisPosition + this.windowSize
      });
      const bestPosition = this.findBestAnalysisPosition({
        expectedPosition,
        nextSynthesisPosition
      });
      for (let channel = 0;channel < this.numberOfChannels; channel++) {
        for (let frame = 0;frame < this.hopSize; frame++) {
          const fadeIn = 0.5 - 0.5 * Math.cos(Math.PI * (frame + 1) / (this.hopSize + 1));
          const outputIndex = nextSynthesisPosition + frame;
          this.output[channel][outputIndex] = this.output[channel][outputIndex] * (1 - fadeIn) + this.input[channel][bestPosition + frame] * fadeIn;
        }
        this.output[channel].set(this.input[channel].subarray(bestPosition + this.hopSize, bestPosition + this.windowSize), nextSynthesisPosition + this.hopSize);
      }
      this.analysisPosition = expectedPosition;
      this.synthesisPosition = nextSynthesisPosition;
      this.outputLength = nextSynthesisPosition + this.windowSize;
    }
  }
  drainFinalizedOutput() {
    if (!this.initialized) {
      return makePlanarAudio(this.numberOfChannels, 0);
    }
    const finalizedLength = Math.max(0, this.synthesisPosition + this.hopSize);
    const result = this.output.map((channel) => channel.slice(0, finalizedLength));
    this.totalOutputFrames += finalizedLength;
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.output[channel].copyWithin(0, finalizedLength, this.outputLength);
    }
    this.outputLength -= finalizedLength;
    this.synthesisPosition -= finalizedLength;
    const inputFramesToDiscard = Math.max(0, Math.floor(this.analysisPosition) - this.searchRadius);
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.input[channel].copyWithin(0, inputFramesToDiscard, this.inputLength);
    }
    this.inputLength -= inputFramesToDiscard;
    this.analysisPosition -= inputFramesToDiscard;
    return result;
  }
  finalize() {
    if (this.finalized) {
      throw new Error("The time stretcher has already been finalized.");
    }
    this.finalized = true;
    const targetLength = Math.round(this.totalInputFrames * this.factor);
    const padding = makePlanarAudio(this.numberOfChannels, this.windowSize + this.searchRadius * 2);
    this.input = ensurePlanarCapacity({
      buffers: this.input,
      requiredLength: this.inputLength + padding[0].length
    });
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.input[channel].set(padding[channel], this.inputLength);
    }
    this.inputLength += padding[0].length;
    this.process();
    const finalized = this.drainFinalizedOutput();
    const remaining = Math.max(0, targetLength - this.totalOutputFrames + finalized[0].length);
    if (finalized[0].length >= remaining) {
      return finalized.map((channel) => channel.slice(0, remaining));
    }
    const result = makePlanarAudio(this.numberOfChannels, remaining);
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      result[channel].set(finalized[channel]);
    }
    return result;
  }
}

class StreamingLinearResampler {
  numberOfChannels;
  step;
  input;
  inputLength = 0;
  position = 0;
  constructor({
    numberOfChannels,
    step
  }) {
    this.numberOfChannels = numberOfChannels;
    this.step = step;
    this.input = makePlanarAudio(numberOfChannels, 65536);
  }
  append(audio) {
    this.input = ensurePlanarCapacity({
      buffers: this.input,
      requiredLength: this.inputLength + audio[0].length
    });
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.input[channel].set(audio[channel], this.inputLength);
    }
    this.inputLength += audio[0].length;
    return this.process(false);
  }
  process(finalizing) {
    const outputLength = Math.max(0, Math.floor((this.inputLength - (finalizing ? 0 : 1) - this.position) / this.step) + 1);
    const result = makePlanarAudio(this.numberOfChannels, outputLength);
    for (let outputFrame = 0;outputFrame < outputLength; outputFrame++) {
      const leftIndex = Math.floor(this.position);
      const rightIndex = Math.min(leftIndex + 1, this.inputLength - 1);
      const fraction = this.position - leftIndex;
      for (let channel = 0;channel < this.numberOfChannels; channel++) {
        const left = this.input[channel][leftIndex];
        const right = this.input[channel][rightIndex];
        result[channel][outputFrame] = left + (right - left) * fraction;
      }
      this.position += this.step;
    }
    const discard = Math.min(Math.floor(this.position), this.inputLength);
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      this.input[channel].copyWithin(0, discard, this.inputLength);
    }
    this.inputLength -= discard;
    this.position -= discard;
    return result;
  }
  finalize() {
    return this.process(true);
  }
}

class StreamingPitchShifter {
  numberOfChannels;
  stretcher;
  resampler;
  outputQueue = new PlanarAudioQueue;
  totalInputFrames = 0;
  totalOutputFrames = 0;
  constructor({
    numberOfChannels,
    sampleRate,
    toneFrequency
  }) {
    this.numberOfChannels = numberOfChannels;
    this.stretcher = new StreamingTimeStretcher({
      numberOfChannels,
      sampleRate,
      factor: toneFrequency
    });
    this.resampler = new StreamingLinearResampler({
      numberOfChannels,
      step: toneFrequency
    });
  }
  append(audio) {
    this.totalInputFrames += audio[0].length;
    const stretched = this.stretcher.append(audio);
    this.outputQueue.push(this.resampler.append(stretched));
    return this.takeAvailableOutput();
  }
  takeAvailableOutput() {
    const availableInputFrames = this.totalInputFrames - this.totalOutputFrames;
    const framesToTake = Math.min(availableInputFrames, this.outputQueue.getLength());
    const result = this.outputQueue.take(framesToTake, this.numberOfChannels);
    this.totalOutputFrames += framesToTake;
    return result;
  }
  finalize() {
    this.outputQueue.push(this.resampler.append(this.stretcher.finalize()));
    this.outputQueue.push(this.resampler.finalize());
    const remaining = this.totalInputFrames - this.totalOutputFrames;
    const available = this.outputQueue.take(Math.min(remaining, this.outputQueue.getLength()), this.numberOfChannels);
    const result = makePlanarAudio(this.numberOfChannels, remaining);
    for (let channel = 0;channel < this.numberOfChannels; channel++) {
      result[channel].set(available[channel]);
    }
    this.totalOutputFrames += remaining;
    return result;
  }
}

// src/audio/pitch-shift.ts
var getPlanarSlice = (slice) => {
  const { buffer } = slice.buffer;
  const startFrame = Math.max(0, Math.round(slice.sourceOffsetInSeconds * buffer.sampleRate));
  const numberOfFrames = Math.min(buffer.length - startFrame, Math.round(slice.sourceDurationInSeconds * buffer.sampleRate));
  return new Array(buffer.numberOfChannels).fill(null).map((_, channel) => buffer.getChannelData(channel).slice(startFrame, startFrame + numberOfFrames));
};
var makeAudioBufferSlice = ({
  audio,
  timelineTimestamp,
  sampleRate
}) => {
  const buffer = new AudioBuffer({
    length: audio[0].length,
    numberOfChannels: audio.length,
    sampleRate
  });
  for (let channel = 0;channel < audio.length; channel++) {
    buffer.copyToChannel(new Float32Array(audio[channel]), channel);
  }
  const duration = audio[0].length / sampleRate;
  return {
    buffer: { buffer, timestamp: timelineTimestamp, duration },
    timelineTimestamp,
    sourceOffsetInSeconds: 0,
    sourceDurationInSeconds: duration
  };
};
async function* pitchShiftAudioIterator({
  iterator,
  toneFrequency
}) {
  if (toneFrequency === 1) {
    yield* iterator;
    return;
  }
  let shifter = null;
  let sampleRate = 0;
  let numberOfChannels = 0;
  let segmentStart = 0;
  let segmentInputFrames = 0;
  let segmentOutputFrames = 0;
  const flush = () => {
    if (!shifter) {
      return null;
    }
    const audio = shifter.finalize();
    const slice = audio[0].length === 0 ? null : makeAudioBufferSlice({
      audio,
      timelineTimestamp: segmentStart + segmentOutputFrames / sampleRate,
      sampleRate
    });
    shifter = null;
    return slice;
  };
  for await (const slice of iterator) {
    const planar = getPlanarSlice(slice);
    if (planar[0].length === 0) {
      continue;
    }
    const nextSampleRate = slice.buffer.buffer.sampleRate;
    const nextNumberOfChannels = slice.buffer.buffer.numberOfChannels;
    const expectedTimestamp = segmentStart + segmentInputFrames / sampleRate;
    const startsNewSegment = !shifter || nextSampleRate !== sampleRate || nextNumberOfChannels !== numberOfChannels || Math.abs(slice.timelineTimestamp - expectedTimestamp) > 1.5 / nextSampleRate;
    if (startsNewSegment) {
      const previousSegmentFinalSlice = flush();
      if (previousSegmentFinalSlice) {
        yield previousSegmentFinalSlice;
      }
      sampleRate = nextSampleRate;
      numberOfChannels = nextNumberOfChannels;
      segmentStart = slice.timelineTimestamp;
      segmentInputFrames = 0;
      segmentOutputFrames = 0;
      shifter = new StreamingPitchShifter({
        numberOfChannels,
        sampleRate,
        toneFrequency
      });
    }
    if (!shifter) {
      throw new Error("Pitch shifter was not initialized.");
    }
    segmentInputFrames += planar[0].length;
    const output = shifter.append(planar);
    if (output[0].length > 0) {
      yield makeAudioBufferSlice({
        audio: output,
        timelineTimestamp: segmentStart + segmentOutputFrames / sampleRate,
        sampleRate
      });
      segmentOutputFrames += output[0].length;
    }
  }
  const finalSlice = flush();
  if (finalSlice) {
    yield finalSlice;
  }
}

// src/audio/sort-by-priority.ts
class StaleWaiterError extends Error {
  constructor() {
    super("Waiter became stale before it got its turn");
    this.name = "StaleWaiterError";
  }
}
var CONCURRENCY = 1;
var waiters = [];
var running = 0;
var horizonTimer = null;
var runningEntry = null;
var processNext = () => {
  if (horizonTimer !== null) {
    clearTimeout(horizonTimer);
    horizonTimer = null;
  }
  if (running >= CONCURRENCY) {
    if (runningEntry?.waiter.getPriority() === null) {
      runningEntry.cancel();
    } else {
      return;
    }
  }
  const staleWaiters = [];
  for (let i = waiters.length - 1;i >= 0; i--) {
    if (waiters[i].getPriority() === null) {
      const [stale] = waiters.splice(i, 1);
      staleWaiters.push(stale);
    }
  }
  for (const stale of staleWaiters) {
    stale.onError(new StaleWaiterError);
  }
  if (waiters.length === 0) {
    return;
  }
  let bestIndex = 0;
  let bestPriority = waiters[0].getPriority();
  if (bestPriority === null) {
    throw new Error("Stale waiter should have been removed");
  }
  for (let i = 1;i < waiters.length; i++) {
    const priority = waiters[i].getPriority();
    if (priority === null) {
      throw new Error("Stale waiter should have been removed");
    }
    if (priority < bestPriority) {
      bestPriority = priority;
      bestIndex = i;
    }
  }
  if (bestPriority > 2) {
    horizonTimer = setTimeout(processNext, Math.max(1, Math.ceil((bestPriority - 2) * 1000)));
    return;
  }
  const [next] = waiters.splice(bestIndex, 1);
  running++;
  let settled = false;
  let cancelled = false;
  const entry = {
    waiter: next,
    cancel: () => {
      cancelled = true;
      entry.settle();
    },
    settle: () => {
      if (settled) {
        return;
      }
      settled = true;
      running--;
      if (runningEntry === entry) {
        runningEntry = null;
      }
    }
  };
  runningEntry = entry;
  next.fn().then((value) => {
    entry.settle();
    if (cancelled) {
      return;
    }
    next.onDone(value, processNext);
  }, (err) => {
    entry.settle();
    if (cancelled) {
      return;
    }
    next.onError(err);
  });
};
var waitForTurn = ({
  getPriority,
  fn,
  onDone,
  onError
}) => {
  waiters.push({
    getPriority,
    fn,
    onDone,
    onError
  });
  processNext();
};

// src/is-type-of-error.ts
function isNetworkError(error) {
  if (error.message.includes("Failed to fetch") || error.message.includes("Load failed") || error.message.includes("NetworkError when attempting to fetch resource")) {
    return true;
  }
  return false;
}
function isUnsupportedConfigurationError(error) {
  return error.message.includes("Unsupported configuration");
}
function isCodecReclaimedError(error) {
  return error instanceof DOMException && error.name === "QuotaExceededError" && /reclaimed/i.test(error.message);
}

// src/make-iterator-with-priming.ts
var AUDIO_PRIMING_SECONDS = 0.5;
async function* makeIteratorWithPriming({
  audioSink,
  timeToSeek,
  maximumTimestamp
}) {
  let resumeTime = timeToSeek;
  let retriedWithoutProgress = false;
  while (true) {
    const iterator = audioSink.buffers(Math.max(0, resumeTime - AUDIO_PRIMING_SECONDS), maximumTimestamp);
    try {
      for await (const buffer of iterator) {
        const sourceStart = Math.max(buffer.timestamp, resumeTime);
        const sourceEnd = Math.min(buffer.timestamp + buffer.duration, maximumTimestamp);
        if (sourceEnd <= sourceStart) {
          continue;
        }
        resumeTime = sourceEnd;
        retriedWithoutProgress = false;
        yield {
          buffer,
          timelineTimestamp: sourceStart,
          sourceOffsetInSeconds: sourceStart - buffer.timestamp,
          sourceDurationInSeconds: sourceEnd - sourceStart
        };
      }
      return;
    } catch (error) {
      if (!isCodecReclaimedError(error) || retriedWithoutProgress) {
        throw error;
      }
      retriedWithoutProgress = true;
    } finally {
      await iterator.return().catch(() => {
        return;
      });
    }
  }
}
async function* makeLoopingIterator({
  audioSink,
  seekTimeInSeconds,
  loopStartInSeconds,
  segmentEndInSeconds,
  maximumContinuousTimestamp
}) {
  if (segmentEndInSeconds <= loopStartInSeconds) {
    throw new Error(`Cannot loop audio over an empty range (${loopStartInSeconds} to ${segmentEndInSeconds})`);
  }
  let passStartInSeconds = seekTimeInSeconds;
  let passBaseTimestamp = seekTimeInSeconds;
  while (true) {
    let yieldedInPass = false;
    for await (const item of makeIteratorWithPriming({
      audioSink,
      timeToSeek: passStartInSeconds,
      maximumTimestamp: segmentEndInSeconds
    })) {
      yieldedInPass = true;
      const timelineTimestamp = passBaseTimestamp + (item.timelineTimestamp - passStartInSeconds);
      const sourceDurationInSeconds = Math.min(item.sourceDurationInSeconds, maximumContinuousTimestamp - timelineTimestamp);
      if (sourceDurationInSeconds <= 0) {
        return;
      }
      yield {
        ...item,
        timelineTimestamp,
        sourceDurationInSeconds
      };
      if (timelineTimestamp + sourceDurationInSeconds >= maximumContinuousTimestamp) {
        return;
      }
    }
    if (!yieldedInPass && passStartInSeconds === loopStartInSeconds) {
      return;
    }
    passBaseTimestamp += segmentEndInSeconds - passStartInSeconds;
    passStartInSeconds = loopStartInSeconds;
  }
}

// src/audio-iterator-manager.ts
var MINIMUM_AUDIO_BUFFERING_TIME_SECONDS = 0.1;
var hasEnoughAudioToStartPlayback = (bufferedDuration) => {
  return bufferedDuration >= MINIMUM_AUDIO_BUFFERING_TIME_SECONDS;
};
var anchorToContinuousTime = ({
  anchor,
  unloopedTimeInSeconds,
  playbackRate
}) => {
  return anchor.mediaStartInSeconds + (unloopedTimeInSeconds - anchor.unloopedStartInSeconds) * playbackRate;
};
var audioIteratorManager = ({
  audioTrack,
  delayPlaybackHandleIfNotPremounting,
  sharedAudioContext,
  getSequenceEndTimestamp,
  getSequenceDurationInSeconds,
  getMediaEndTimestamp,
  getStartTime,
  getIsPlaying,
  initialMuted,
  initialVolume,
  toneFrequency,
  drawDebugOverlay,
  onError
}) => {
  let muted = initialMuted;
  let currentVolume = Math.max(0, initialVolume);
  let currentToneFrequency = toneFrequency;
  let currentSeek = null;
  const gainNode = sharedAudioContext.audioContext.createGain();
  gainNode.gain.value = muted ? 0 : currentVolume;
  gainNode.connect(sharedAudioContext.gainNode);
  const audioSink = new AudioBufferSink(audioTrack);
  let audioBufferIterator = null;
  let currentAnchor = null;
  let audioIteratorsCreated = 0;
  let totalAudioScheduledInSeconds = 0;
  let currentDelayHandle = null;
  const unblockCurrentDelayHandle = () => {
    if (currentDelayHandle) {
      currentDelayHandle.unblock();
      currentDelayHandle = null;
    }
  };
  const destroyIterator = () => {
    audioBufferIterator?.destroy();
    audioBufferIterator = null;
    currentAnchor = null;
    currentSeek = null;
    unblockCurrentDelayHandle();
  };
  const getIteratorTime = (newTime, unloopedTime, localPlaybackRate, loop) => {
    return loop && currentAnchor ? anchorToContinuousTime({
      anchor: currentAnchor,
      unloopedTimeInSeconds: unloopedTime,
      playbackRate: localPlaybackRate
    }) : newTime;
  };
  const pendingScheduleWaiters = [];
  const notifyNodeScheduled = () => {
    for (let i = pendingScheduleWaiters.length - 1;i >= 0; i--) {
      const waiter = pendingScheduleWaiters[i];
      waiter.remaining--;
      if (waiter.remaining <= 0) {
        waiter.resolve();
        pendingScheduleWaiters.splice(i, 1);
      }
    }
  };
  const waitForNScheduledNodes = (n) => {
    if (n <= 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      pendingScheduleWaiters.push({ remaining: n, resolve });
    });
  };
  const onAudioChunk = ({
    buffer,
    iterator
  }) => {
    if (muted) {
      return;
    }
    const startTime = getStartTime();
    const sequenceEndTime = getSequenceEndTimestamp();
    if (buffer.timelineTimestamp + buffer.sourceDurationInSeconds <= startTime) {
      return;
    }
    if (buffer.timelineTimestamp >= sequenceEndTime) {
      return;
    }
    const scheduledStart = Math.max(buffer.timelineTimestamp, startTime);
    const scheduledEnd = Math.min(buffer.timelineTimestamp + buffer.sourceDurationInSeconds, sequenceEndTime);
    totalAudioScheduledInSeconds += Math.max(0, scheduledEnd - scheduledStart);
    iterator.scheduleAudio({
      buffer: buffer.buffer.buffer,
      timestamp: buffer.timelineTimestamp,
      originalUnloopedMediaTimestamp: buffer.buffer.timestamp,
      sourceOffsetInSeconds: buffer.sourceOffsetInSeconds,
      sourceDurationInSeconds: Math.min(buffer.sourceDurationInSeconds, sequenceEndTime - buffer.timelineTimestamp)
    });
    drawDebugOverlay();
  };
  const proceedScheduling = ({
    iterator,
    nonce,
    getTargetTime,
    playbackRate,
    onScheduled,
    onDestroyed,
    onDone,
    currentTime,
    startFromSecond,
    getAudioContextCurrentTimeMockedInTest
  }) => {
    waitForTurn({
      getPriority: () => {
        if (iterator.isDestroyed()) {
          onDestroyed();
          return null;
        }
        const guessedNextTimestamp = iterator.guessNextTimestamp();
        const targetTime = getTargetTime(guessedNextTimestamp, currentTime);
        if (targetTime === null) {
          return null;
        }
        const scheduledTime = getScheduledTime({
          mediaTimestamp: guessedNextTimestamp,
          targetTime,
          currentTime,
          sequenceStartTime: getStartTime()
        });
        const preparedDuration = guessedNextTimestamp - startFromSecond;
        if (getIsPlaying()) {
          return scheduledTime - getAudioContextCurrentTimeMockedInTest();
        }
        return hasEnoughAudioToStartPlayback(preparedDuration) ? preparedDuration / playbackRate : 0;
      },
      fn: () => iterator.getNextFn(),
      onDone: (result, next) => {
        if (iterator.isDestroyed()) {
          next();
          onDestroyed();
          return;
        }
        if (!result.value) {
          next();
          onDone();
          return;
        }
        onScheduled(result.value.sourceDurationInSeconds, result.value.timelineTimestamp);
        notifyNodeScheduled();
        onAudioChunk({
          buffer: result.value,
          iterator
        });
        proceedScheduling({
          iterator,
          nonce,
          getTargetTime,
          playbackRate,
          onScheduled,
          onDestroyed,
          onDone,
          currentTime,
          startFromSecond,
          getAudioContextCurrentTimeMockedInTest
        });
        next();
      },
      onError: (e) => {
        if (e instanceof InputDisposedError) {
          onDestroyed();
          return;
        }
        if (e instanceof StaleWaiterError) {
          onDestroyed();
          return;
        }
        if (iterator.isDestroyed()) {
          onDestroyed();
          return;
        }
        iterator.destroy();
        onDestroyed();
        onError(e);
      }
    });
  };
  const startAudioIterator = ({
    nonce,
    playbackRate,
    startFromSecond,
    unloopedStartFromSecond,
    scheduleAudioNode,
    getTargetTime,
    logLevel,
    loop,
    unscheduleAudioNode,
    getAudioContextCurrentTimeMockedInTest
  }) => {
    if (muted || playbackRate <= 0) {
      return;
    }
    const maximumTimestamp = loop || currentToneFrequency !== 1 ? getMediaEndTimestamp() : Math.min(getMediaEndTimestamp(), getSequenceEndTimestamp());
    if (startFromSecond >= maximumTimestamp) {
      return;
    }
    audioBufferIterator?.destroy();
    unblockCurrentDelayHandle();
    const delayHandle = delayPlaybackHandleIfNotPremounting();
    currentDelayHandle = delayHandle;
    currentAnchor = {
      unloopedStartInSeconds: unloopedStartFromSecond,
      mediaStartInSeconds: startFromSecond
    };
    const maximumContinuousTimestamp = startFromSecond + getSequenceDurationInSeconds() * playbackRate;
    const unshiftedSource = loop ? makeLoopingIterator({
      audioSink,
      seekTimeInSeconds: startFromSecond,
      loopStartInSeconds: getStartTime(),
      segmentEndInSeconds: maximumTimestamp,
      maximumContinuousTimestamp
    }) : makeIteratorWithPriming({
      audioSink,
      timeToSeek: startFromSecond,
      maximumTimestamp
    });
    const source = pitchShiftAudioIterator({
      iterator: unshiftedSource,
      toneFrequency: currentToneFrequency
    });
    const iterator = makeAudioIterator({
      startFromSecond,
      iterator: source,
      unscheduleAudioNode,
      scheduleChunk: (chunk) => {
        if (muted)
          return null;
        const node = sharedAudioContext.audioContext.createBufferSource();
        node.buffer = chunk.buffer;
        node.playbackRate.value = playbackRate;
        node.connect(gainNode);
        const started = scheduleAudioNode(node, chunk.timestamp, chunk.originalUnloopedMediaTimestamp, chunk.sourceOffsetInSeconds, chunk.sourceDurationInSeconds);
        if (started.type === "not-started") {
          Internals3.Log.verbose({ logLevel, tag: "audio-scheduling" }, "not started, disconnected: %s %s", chunk.timestamp.toFixed(3), chunk.buffer.duration.toFixed(3));
          node.disconnect();
          return null;
        }
        return node;
      }
    });
    audioIteratorsCreated++;
    audioBufferIterator = iterator;
    let bufferedUntil = startFromSecond;
    let hasUnblockedPlayback = false;
    const unblockPlayback = () => {
      if (hasUnblockedPlayback) {
        return;
      }
      hasUnblockedPlayback = true;
      delayHandle.unblock();
    };
    proceedScheduling({
      iterator,
      nonce,
      getTargetTime,
      playbackRate,
      onScheduled: (sourceDurationInSeconds, timelineTimestamp) => {
        bufferedUntil = Math.max(bufferedUntil, timelineTimestamp + sourceDurationInSeconds);
        const bufferedDuration = bufferedUntil - startFromSecond;
        if (hasEnoughAudioToStartPlayback(bufferedDuration)) {
          unblockPlayback();
        }
      },
      onDestroyed: () => {
        unblockPlayback();
      },
      onDone: () => {
        unblockPlayback();
      },
      currentTime: sharedAudioContext.audioContext.currentTime,
      startFromSecond,
      getAudioContextCurrentTimeMockedInTest
    });
  };
  const seek = ({
    newTime,
    unloopedNewTime,
    nonce,
    playbackRate,
    localPlaybackRate,
    scheduleAudioNode,
    getTargetTime,
    logLevel,
    loop,
    trimBefore,
    trimAfter,
    sequenceOffset,
    sequenceDurationInFrames,
    fps,
    getAudioContextCurrentTimeMockedInTest
  }) => {
    if (nonce.isStale()) {
      return;
    }
    if (currentSeek !== null && currentSeek.time === newTime && currentSeek.playbackRate === playbackRate && currentSeek.trimBefore === trimBefore && currentSeek.trimAfter === trimAfter && currentSeek.sequenceOffset === sequenceOffset && currentSeek.sequenceDurationInFrames === sequenceDurationInFrames && currentSeek.loop === loop && currentSeek.fps === fps && (muted || audioBufferIterator && !audioBufferIterator.isDestroyed())) {
      return;
    }
    currentSeek = {
      time: newTime,
      playbackRate,
      trimBefore,
      trimAfter,
      sequenceOffset,
      sequenceDurationInFrames,
      loop,
      fps
    };
    if (muted) {
      return;
    }
    if (audioBufferIterator && !audioBufferIterator.isDestroyed()) {
      const timeToCheck = getIteratorTime(newTime, unloopedNewTime, localPlaybackRate, loop);
      const queuedPeriod = audioBufferIterator.getQueuedPeriod();
      const anchorTolerance = Internals3.getAudioSyncAnchorTolerance(sharedAudioContext.audioContext) * Math.abs(playbackRate);
      const queuedPeriodMinusLatency = queuedPeriod ? {
        from: queuedPeriod.from - anchorTolerance,
        until: queuedPeriod.until
      } : null;
      const currentTimeIsAlreadyQueued = isAlreadyQueued(timeToCheck, queuedPeriodMinusLatency);
      if (currentTimeIsAlreadyQueued) {
        processNext();
        return;
      }
      const currentIteratorTimestamp = audioBufferIterator.guessNextTimestamp();
      const iteratorHasAdvancedThroughSilence = loop && currentAnchor !== null && unloopedNewTime >= currentAnchor.unloopedStartInSeconds && currentIteratorTimestamp >= timeToCheck;
      if (iteratorHasAdvancedThroughSilence || currentIteratorTimestamp < timeToCheck && Math.abs(currentIteratorTimestamp - timeToCheck) < 1) {
        processNext();
        return;
      }
    }
    startAudioIterator({
      nonce,
      playbackRate,
      startFromSecond: newTime,
      unloopedStartFromSecond: unloopedNewTime,
      scheduleAudioNode,
      getTargetTime,
      logLevel,
      loop,
      unscheduleAudioNode: sharedAudioContext.unscheduleAudioNode,
      getAudioContextCurrentTimeMockedInTest
    });
  };
  return {
    startAudioIterator,
    rescheduleAudio: (newTime, unloopedTime, localPlaybackRate, loop) => {
      if (newTime !== null && audioBufferIterator?.rescheduleAudio(getIteratorTime(newTime, unloopedTime, localPlaybackRate, loop))) {
        processNext();
        return true;
      }
      destroyIterator();
      return false;
    },
    getAudioBufferIterator: () => audioBufferIterator,
    getCurrentAnchor: () => currentAnchor,
    destroyIterator,
    seek,
    getAudioIteratorsCreated: () => audioIteratorsCreated,
    getTotalAudioScheduledInSeconds: () => totalAudioScheduledInSeconds,
    setMuted: (newMuted) => {
      if (muted === newMuted) {
        return;
      }
      muted = newMuted;
      gainNode.gain.value = muted ? 0 : currentVolume;
      if (muted) {
        destroyIterator();
      }
      currentSeek = null;
    },
    setVolume: (volume) => {
      currentVolume = Math.max(0, volume);
      gainNode.gain.value = muted ? 0 : currentVolume;
    },
    setToneFrequency: (newToneFrequency) => {
      currentToneFrequency = newToneFrequency;
    },
    waitForNScheduledNodes
  };
};

// src/debug-overlay/preview-overlay.ts
var drawPreviewOverlay = ({
  context,
  audioTime,
  audioContextState,
  audioSyncAnchor,
  playing,
  audioIteratorManager,
  videoIteratorManager,
  playbackRate
}) => {
  const anchorValue = audioSyncAnchor?.value ?? 0;
  const lines = [
    "Debug overlay",
    `Video iterators created: ${videoIteratorManager?.getVideoIteratorsCreated()}`,
    `Audio iterators created: ${audioIteratorManager?.getAudioIteratorsCreated()}`,
    `Audio scheduled: ${(audioIteratorManager?.getTotalAudioScheduledInSeconds() ?? 0).toFixed(3)}s`,
    `Frames rendered: ${videoIteratorManager?.getFramesRendered()}`,
    `Audio context state: ${audioContextState}`,
    audioTime ? `Audio time: ${((audioTime - anchorValue) * playbackRate).toFixed(3)}s` : null
  ].filter(Boolean);
  if (audioIteratorManager) {
    const queuedPeriod = audioIteratorManager.getAudioBufferIterator()?.getQueuedPeriod();
    if (queuedPeriod) {
      const aheadText = audioTime ? ` (${(queuedPeriod.until - (audioTime - anchorValue) * playbackRate).toFixed(3)}s ahead)` : "";
      lines.push(`Audio queued until ${queuedPeriod.until.toFixed(3)}s${aheadText}`);
    }
    lines.push(`Playing: ${playing}`);
  }
  const lineHeight = 30;
  const boxPaddingX = 10;
  const boxPaddingY = 10;
  const boxLeft = 20;
  const boxTop = 20;
  const boxWidth = 600;
  const boxHeight = lines.length * lineHeight + 2 * boxPaddingY;
  context.fillStyle = "rgba(0, 0, 0, 1)";
  context.fillRect(boxLeft, boxTop, boxWidth, boxHeight);
  context.fillStyle = "white";
  context.font = "24px sans-serif";
  context.textBaseline = "top";
  for (let i = 0;i < lines.length; i++) {
    context.fillText(lines[i], boxLeft + boxPaddingX, boxTop + boxPaddingY + i * lineHeight);
  }
};

// src/get-shared-input.ts
import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import { Internals as Internals5 } from "remotion";

// src/get-duration-or-compute.ts
var getDurationOrCompute = async (input) => {
  return await input.getDurationFromMetadata(undefined, {
    skipLiveWait: true
  }) ?? input.computeDuration(undefined, { skipLiveWait: true });
};

// src/max-cache-size.ts
import { cancelRender, Internals as Internals4 } from "remotion";
var getUncachedMaxCacheSize = (logLevel) => {
  if (typeof window !== "undefined" && window.remotion_mediaCacheSizeInBytes !== undefined && window.remotion_mediaCacheSizeInBytes !== null) {
    if (window.remotion_mediaCacheSizeInBytes < 240 * 1024 * 1024) {
      cancelRender(new Error(`The minimum value for the "mediaCacheSizeInBytes" prop is 240MB (${240 * 1024 * 1024}), got: ${window.remotion_mediaCacheSizeInBytes}`));
    }
    if (window.remotion_mediaCacheSizeInBytes > 20000 * 1024 * 1024) {
      cancelRender(new Error(`The maximum value for the "mediaCacheSizeInBytes" prop is 20GB (${20000 * 1024 * 1024}), got: ${window.remotion_mediaCacheSizeInBytes}`));
    }
    Internals4.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set using "mediaCacheSizeInBytes": ${(window.remotion_mediaCacheSizeInBytes / 1024 / 1024).toFixed(1)} MB`);
    return window.remotion_mediaCacheSizeInBytes;
  }
  if (typeof window !== "undefined" && window.remotion_initialMemoryAvailable !== undefined && window.remotion_initialMemoryAvailable !== null) {
    const value = window.remotion_initialMemoryAvailable / 2;
    if (value < 500 * 1024 * 1024) {
      Internals4.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on minimum value of 500MB (which is more than half of the available system memory!)`);
      return 500 * 1024 * 1024;
    }
    if (value > 20000 * 1024 * 1024) {
      Internals4.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on maximum value of 20GB (which is less than half of the available system memory)`);
      return 20000 * 1024 * 1024;
    }
    Internals4.Log.verbose({ logLevel, tag: "@remotion/media" }, `Using cache size set based on available memory (50% of available memory): ${(value / 1024 / 1024).toFixed(1)} MB`);
    return value;
  }
  return 1000 * 1000 * 1000;
};
var cachedMaxCacheSize = null;
var getMaxVideoCacheSize = (logLevel) => {
  if (cachedMaxCacheSize !== null) {
    return cachedMaxCacheSize;
  }
  cachedMaxCacheSize = getUncachedMaxCacheSize(logLevel);
  return cachedMaxCacheSize;
};
var MIN_SOURCE_CACHE_SIZE = 8 * 1024 * 1024;
var MAX_SOURCE_CACHE_SIZE = 64 * 1024 * 1024;
var getMaxSourceCacheSizeFromBudget = (budget) => {
  return Math.min(MAX_SOURCE_CACHE_SIZE, Math.max(MIN_SOURCE_CACHE_SIZE, Math.floor(budget / 16)));
};
var getMaxSourceCacheSize = (logLevel) => {
  return getMaxSourceCacheSizeFromBudget(getMaxVideoCacheSize(logLevel));
};

// src/request-init.ts
var normalizeMediaHeaders = (headers) => {
  if (!headers) {
    return;
  }
  const entries = [];
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      entries.push([key.toLowerCase(), value]);
    });
  } else if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      entries.push([key.toLowerCase(), value]);
    }
  } else {
    for (const [key, value] of Object.entries(headers)) {
      entries.push([key.toLowerCase(), value]);
    }
  }
  entries.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  return entries;
};
var normalizeMediaRequestInit = (requestInit) => {
  if (!requestInit) {
    return;
  }
  const headers = normalizeMediaHeaders(requestInit.headers);
  const normalized = {
    ...requestInit.cache === undefined ? null : { cache: requestInit.cache },
    ...requestInit.credentials === undefined ? null : { credentials: requestInit.credentials },
    ...headers === undefined ? null : { headers },
    ...requestInit.integrity === undefined ? null : { integrity: requestInit.integrity },
    ...requestInit.mode === undefined ? null : { mode: requestInit.mode },
    ...requestInit.redirect === undefined ? null : { redirect: requestInit.redirect },
    ...requestInit.referrer === undefined ? null : { referrer: requestInit.referrer },
    ...requestInit.referrerPolicy === undefined ? null : { referrerPolicy: requestInit.referrerPolicy }
  };
  return Object.keys(normalized).length === 0 ? undefined : normalized;
};
var getMediaRequestInitFingerprint = (requestInit) => {
  const normalized = normalizeMediaRequestInit(requestInit);
  if (!normalized) {
    return null;
  }
  return [
    normalized.cache ?? null,
    normalized.credentials ?? null,
    normalized.integrity ?? null,
    normalized.mode ?? null,
    normalized.redirect ?? null,
    normalized.referrer ?? null,
    normalized.referrerPolicy ?? null,
    normalized.headers ?? null
  ];
};
var resolveRequestInit = ({
  credentials,
  requestInit
}) => {
  if (credentials === undefined) {
    return normalizeMediaRequestInit(requestInit);
  }
  return normalizeMediaRequestInit({
    credentials,
    ...requestInit
  });
};

// src/get-shared-input.ts
var getSharedInputCacheKey = ({
  src,
  credentials,
  requestInit
}) => Internals5.getMediabunnyInputResourceKey({
  src,
  credentials: credentials ?? null,
  requestInitFingerprint: getMediaRequestInitFingerprint(requestInit),
  revision: null
});
var acquireSharedInput = ({
  src,
  credentials,
  requestInit,
  logLevel
}) => {
  const normalizedRequestInit = normalizeMediaRequestInit(requestInit);
  const cacheKey = getSharedInputCacheKey({
    src,
    credentials,
    requestInit: normalizedRequestInit
  });
  const resolvedRequestInit = resolveRequestInit({
    credentials,
    requestInit: normalizedRequestInit
  });
  const lease = Internals5.globalMediaResourceManager.acquire({
    key: cacheKey,
    create: () => {
      const input = new Input({
        source: new UrlSource(src, {
          handleUnhandledError: (error) => {
            Internals5.Log.warn({ logLevel, tag: "@remotion/media" }, `A speculative fetch for "${src}" failed:`, error);
          },
          maxCacheSize: getMaxSourceCacheSize(logLevel),
          ...resolvedRequestInit ? { requestInit: resolvedRequestInit } : undefined
        }),
        formats: ALL_FORMATS
      });
      return { resource: input, dispose: () => input.dispose() };
    }
  });
  return {
    input: lease.resource,
    getDuration: () => lease.getOrCreateValue(Internals5.MEDIABUNNY_DURATION_VALUE_KEY, () => getDurationOrCompute(lease.resource)),
    release: lease.release
  };
};

// src/helpers/resolve-audio-track.ts
var resolveAudioTrack = async ({
  videoTrack,
  audioTracks,
  audioStreamIndex
}) => {
  if (audioStreamIndex !== null) {
    return audioTracks[audioStreamIndex] ?? null;
  }
  if (videoTrack) {
    const primary = await videoTrack.getPrimaryPairableAudioTrack();
    if (!primary || await primary.canDecode()) {
      return primary;
    }
    return await videoTrack.getPrimaryPairableAudioTrack({
      filter: (track) => track.canDecode()
    }) ?? primary;
  }
  for (const track of audioTracks) {
    if (await track.canDecode()) {
      return track;
    }
  }
  return audioTracks[0] ?? null;
};

// src/nonce-manager.ts
var makeNonceManager = () => {
  let nonce = 0;
  const createAsyncOperation = () => {
    nonce++;
    const currentNonce = nonce;
    return {
      isStale: () => nonce !== currentNonce
    };
  };
  return {
    createAsyncOperation
  };
};

// src/premount-aware-delay-playback.ts
class PremountAwareDelayPlayback {
  isPremounting;
  isPostmounting;
  disposed = false;
  activeHandles = new Set;
  delayPlayback;
  constructor({
    bufferState,
    isPremounting,
    isPostmounting
  }) {
    this.delayPlayback = bufferState.delayPlayback;
    this.isPremounting = isPremounting;
    this.isPostmounting = isPostmounting;
  }
  shouldDelayPlayback() {
    return !this.disposed && !this.isPremounting && !this.isPostmounting;
  }
  dispose() {
    this.disposed = true;
    for (const handle of this.activeHandles) {
      handle.dispose();
    }
  }
  syncHandles() {
    for (const handle of this.activeHandles) {
      if (this.shouldDelayPlayback()) {
        handle.arm();
      } else {
        handle.disarm();
      }
    }
  }
  setIsPremounting(isPremounting) {
    this.isPremounting = isPremounting;
    this.syncHandles();
  }
  setIsPostmounting(isPostmounting) {
    this.isPostmounting = isPostmounting;
    this.syncHandles();
  }
  createHandle() {
    let armed = false;
    let unblock = null;
    let disposed = false;
    const arm = () => {
      if (armed || disposed) {
        return;
      }
      unblock = this.delayPlayback().unblock;
      armed = true;
    };
    const disarm = () => {
      if (!armed) {
        return;
      }
      unblock?.();
      unblock = null;
      armed = false;
    };
    const entry = {
      arm,
      disarm,
      dispose: () => {}
    };
    entry.dispose = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      disarm();
      this.activeHandles.delete(entry);
    };
    this.activeHandles.add(entry);
    if (this.shouldDelayPlayback()) {
      arm();
    }
    return {
      unblock: entry.dispose,
      [Symbol.dispose]: entry.dispose
    };
  }
}

// src/video-iterator-manager.ts
import { VideoSampleSink } from "mediabunny";
import { Internals as Internals6 } from "remotion";

// src/canvas-ahead-of-time.ts
var BUFFER_SIZE = 3;
var releaseCanvas = Symbol("releaseCanvas");
var drawSample = Symbol("drawSample");
var effectSource = Symbol("effectSource");
var releaseStableFrame = (frame) => {
  frame?.[releaseCanvas]?.();
};
var drawStableFrame = (context, frame, width, height) => {
  const draw = frame[drawSample];
  if (draw) {
    draw(context, width, height);
  } else {
    context.drawImage(frame.canvas, 0, 0, width, height);
  }
};
var getStableEffectSource = (frame, options) => {
  return frame[effectSource]?.(options) ?? frame.canvas;
};
var makeStableFramePool = () => {
  const availableCanvases = [];
  const makeStableFrame = (frame) => {
    let sampleCanvas = null;
    let sampleReleased = false;
    let effectCanvas = null;
    let retainedSource = null;
    const sampleFrame = {
      timestamp: frame.timestamp,
      duration: frame.duration,
      get canvas() {
        if (!sampleCanvas) {
          sampleCanvas = availableCanvases.pop() ?? new OffscreenCanvas(frame.displayWidth, frame.displayHeight);
          if (sampleCanvas.width !== frame.displayWidth)
            sampleCanvas.width = frame.displayWidth;
          if (sampleCanvas.height !== frame.displayHeight)
            sampleCanvas.height = frame.displayHeight;
          const sampleContext = sampleCanvas.getContext("2d");
          if (!sampleContext)
            throw new Error("Could not create canvas context");
          sampleContext.clearRect(0, 0, sampleCanvas.width, sampleCanvas.height);
          frame.draw(sampleContext, 0, 0, sampleCanvas.width, sampleCanvas.height);
        }
        return sampleCanvas;
      },
      [effectSource]: ({ width, height, allowDirectSource }) => {
        if (width !== frame.displayWidth || height !== frame.displayHeight) {
          effectCanvas ??= availableCanvases.pop() ?? new OffscreenCanvas(width, height);
          if (effectCanvas.width !== width)
            effectCanvas.width = width;
          if (effectCanvas.height !== height)
            effectCanvas.height = height;
          const effectContext = effectCanvas.getContext("2d");
          if (!effectContext)
            throw new Error("Could not create canvas context");
          effectContext.clearRect(0, 0, width, height);
          frame.draw(effectContext, 0, 0, width, height);
          return effectCanvas;
        }
        if (!allowDirectSource || sampleCanvas || frame.rotation !== 0 || frame.flip)
          return sampleFrame.canvas;
        if (!retainedSource) {
          const source = frame.toCanvasImageSource();
          retainedSource = typeof VideoFrame !== "undefined" && source instanceof VideoFrame ? source.clone() : source;
        }
        return retainedSource;
      },
      [drawSample]: (sampleContext, width, height) => {
        frame.draw(sampleContext, 0, 0, width, height);
      },
      [releaseCanvas]: () => {
        if (sampleReleased)
          return;
        sampleReleased = true;
        if (typeof VideoFrame !== "undefined" && retainedSource instanceof VideoFrame)
          retainedSource.close();
        frame.close();
        if (sampleCanvas)
          availableCanvases.push(sampleCanvas);
        if (effectCanvas)
          availableCanvases.push(effectCanvas);
      }
    };
    return sampleFrame;
  };
  return { makeStableFrame };
};
var canvasesAheadOfTime = (videoSink, startTimestamp, resize = null) => {
  const iterator = videoSink.samples(startTimestamp);
  const buffer = [];
  let chaining = false;
  let reachedEnd = false;
  let closed = false;
  let inFlight = null;
  const stableFramePool = makeStableFramePool();
  const unclaimedFrames = new Set;
  const takeFrame = (frame) => {
    const stable = stableFramePool.makeStableFrame(frame);
    unclaimedFrames.add(stable);
    return stable;
  };
  const claimFrame = (frame) => {
    unclaimedFrames.delete(frame);
    if (closed) {
      releaseStableFrame(frame);
      return null;
    }
    return frame;
  };
  const waitForSlot = async (slot) => {
    await slot.promise;
    const resolved = slot.resolved;
    return resolved.done ? null : claimFrame(resolved.value);
  };
  const fillNext = () => {
    if (chaining || reachedEnd || closed)
      return;
    if (buffer.length >= BUFFER_SIZE)
      return;
    chaining = true;
    const nextSample = iterator.next();
    const promise = resize ? nextSample.then(async (result) => {
      if (result.done || closed)
        return result;
      try {
        return {
          done: false,
          value: await result.value.transform(resize)
        };
      } finally {
        result.value.close();
      }
    }) : nextSample;
    const slot = { promise, resolved: null };
    buffer.push(slot);
    inFlight = slot.promise.then((result) => {
      chaining = false;
      inFlight = null;
      if (result.done) {
        slot.resolved = result;
        reachedEnd = true;
        return;
      }
      if (closed) {
        result.value.close();
        slot.resolved = { done: true, value: undefined };
        return;
      }
      slot.resolved = { ...result, value: takeFrame(result.value) };
      fillNext();
    }, () => {
      chaining = false;
      inFlight = null;
    });
  };
  fillNext();
  const next = () => {
    const slot = buffer.shift();
    fillNext();
    if (!slot) {
      if (reachedEnd || closed) {
        return { type: "ready", frame: null };
      }
      const chain = inFlight;
      return {
        type: "pending",
        wait: async () => {
          await chain;
          const next2 = buffer.shift();
          fillNext();
          if (!next2)
            return null;
          if (next2.resolved) {
            return next2.resolved.done ? null : claimFrame(next2.resolved.value);
          }
          return waitForSlot(next2);
        }
      };
    }
    if (slot.resolved) {
      if (slot.resolved.done) {
        return { type: "ready", frame: null };
      }
      return { type: "ready", frame: claimFrame(slot.resolved.value) };
    }
    return { type: "pending", wait: () => waitForSlot(slot) };
  };
  const closeIterator = async () => {
    closed = true;
    unclaimedFrames.forEach(releaseStableFrame);
    unclaimedFrames.clear();
    buffer.length = 0;
    await iterator.return();
  };
  return { next, closeIterator };
};

// src/helpers/round-to-4-digits.ts
var roundTo4Digits = (timestamp) => {
  return Math.round(timestamp * 1000) / 1000;
};

// src/helpers/has-frame-started.ts
var FRAME_START_TOLERANCE = 0.001 + 0.000000001;
var hasFrameStarted = (frameTimestamp, requestedTimestamp) => {
  return frameTimestamp - requestedTimestamp <= FRAME_START_TOLERANCE;
};

// src/prewarm-iterator-for-looping.ts
var makePrewarmedVideoIteratorCache = (videoSink, timeResolution, resize = null) => {
  const prewarmedVideoIterators = new Map;
  const snapToTrackTimeResolution = (timeToSeek) => {
    return Math.floor((timeToSeek + FRAME_START_TOLERANCE) * timeResolution) / timeResolution;
  };
  const prewarmIteratorForLooping = ({ timeToSeek }) => {
    const snapped = snapToTrackTimeResolution(timeToSeek);
    if (!prewarmedVideoIterators.has(snapped)) {
      prewarmedVideoIterators.set(snapped, canvasesAheadOfTime(videoSink, snapped, resize));
    }
  };
  const makeIteratorOrUsePrewarmed = (timeToSeek) => {
    const snapped = snapToTrackTimeResolution(timeToSeek);
    const prewarmedIterator = prewarmedVideoIterators.get(snapped);
    if (prewarmedIterator) {
      prewarmedVideoIterators.delete(snapped);
      return prewarmedIterator;
    }
    return canvasesAheadOfTime(videoSink, snapped, resize);
  };
  const destroy = () => {
    for (const iterator of prewarmedVideoIterators.values()) {
      iterator.closeIterator().catch(() => {
        return;
      });
    }
    prewarmedVideoIterators.clear();
  };
  return {
    prewarmIteratorForLooping,
    makeIteratorOrUsePrewarmed,
    destroy
  };
};

// src/video/video-preview-iterator.ts
var createVideoIterator = async (timeToSeek, cache) => {
  let destroyed = false;
  const iterator = cache.makeIteratorOrUsePrewarmed(timeToSeek);
  let iteratorEnded = false;
  const firstAwait = iterator.next();
  let initialFrame;
  try {
    initialFrame = firstAwait && firstAwait.type === "ready" ? firstAwait.frame : await firstAwait.wait();
  } catch (error) {
    await iterator.closeIterator().catch(() => {
      return;
    });
    throw error;
  }
  let lastReturnedFrame = initialFrame;
  let peekedFrame = null;
  const setLastReturnedFrame = (frame) => {
    if (destroyed) {
      releaseStableFrame(frame);
      return;
    }
    if (lastReturnedFrame !== frame) {
      releaseStableFrame(lastReturnedFrame);
    }
    lastReturnedFrame = frame;
  };
  const setPeekedFrame = (frame) => {
    if (destroyed) {
      releaseStableFrame(frame);
      return null;
    }
    peekedFrame = frame;
    if (peekedFrame === null) {
      iteratorEnded = true;
    }
    return peekedFrame;
  };
  const peekIfReady = () => {
    if (peekedFrame) {
      return { type: "ready", frame: peekedFrame };
    }
    if (iteratorEnded) {
      return { type: "ready", frame: null };
    }
    const next = iterator.next();
    if (next.type === "ready") {
      return { type: "ready", frame: setPeekedFrame(next.frame) };
    }
    return {
      type: "pending",
      wait: next.wait
    };
  };
  const getFrameEndTimestampFromPeek = (frame) => {
    return frame ? frame.timestamp : Infinity;
  };
  const getFrameEndTimestamp = async ({
    pendingFrameBehavior,
    onWait,
    shouldContinue
  }) => {
    const peeked = peekIfReady();
    if (peeked.type === "ready") {
      return {
        type: "ready",
        timestamp: getFrameEndTimestampFromPeek(peeked.frame)
      };
    }
    if (pendingFrameBehavior === "restart-iterator") {
      return { type: "pending" };
    }
    if (!shouldContinue()) {
      return { type: "cancelled" };
    }
    onWait();
    const awaitedPeeked = setPeekedFrame(await peeked.wait());
    if (!shouldContinue()) {
      return { type: "cancelled" };
    }
    return {
      type: "ready",
      timestamp: getFrameEndTimestampFromPeek(awaitedPeeked)
    };
  };
  const getNextOrNullIfNotAvailable = () => {
    if (peekedFrame) {
      const frame = peekedFrame;
      setLastReturnedFrame(frame);
      const retValue = {
        type: "got-frame-or-end",
        frame
      };
      peekedFrame = null;
      return retValue;
    }
    const next = iterator.next();
    if (next.type === "pending") {
      return {
        type: "need-to-wait-for-it",
        waitPromise: async () => {
          const res = await next.wait();
          if (res) {
            setLastReturnedFrame(res);
          } else {
            iteratorEnded = true;
          }
          return res;
        }
      };
    }
    if (next.frame) {
      setLastReturnedFrame(next.frame);
    } else {
      iteratorEnded = true;
    }
    return {
      type: "got-frame-or-end",
      frame: next.frame ?? null
    };
  };
  const destroy = () => {
    destroyed = true;
    releaseStableFrame(lastReturnedFrame);
    if (peekedFrame !== lastReturnedFrame) {
      releaseStableFrame(peekedFrame);
    }
    lastReturnedFrame = null;
    peekedFrame = null;
    iterator.closeIterator().catch(() => {
      return;
    });
  };
  const tryToSatisfySeek = async (time, options) => {
    if (!options.shouldContinue()) {
      return {
        type: "not-satisfied",
        reason: "seek was superseded"
      };
    }
    const timestamp = roundTo4Digits(time);
    if (lastReturnedFrame) {
      const frameTimestamp = roundTo4Digits(lastReturnedFrame.timestamp);
      if (timestamp < frameTimestamp) {
        const lastFrameWasInitialFrame = lastReturnedFrame === initialFrame;
        const firstFrameDoesSatisfy = lastFrameWasInitialFrame && roundTo4Digits(time) >= roundTo4Digits(timeToSeek);
        if (firstFrameDoesSatisfy) {
          return {
            type: "satisfied",
            frame: lastReturnedFrame
          };
        }
        return {
          type: "not-satisfied",
          reason: `iterator is too far, most recently returned ${frameTimestamp}`
        };
      }
      const frameEndTimestamp = await getFrameEndTimestamp({
        onWait: options.onWait,
        pendingFrameBehavior: options.pendingFrameBehavior,
        shouldContinue: options.shouldContinue
      });
      if (frameEndTimestamp.type === "cancelled") {
        return {
          type: "not-satisfied",
          reason: "seek was superseded"
        };
      }
      if (frameEndTimestamp.type === "pending") {
        return {
          type: "not-satisfied",
          reason: "iterator did not have next frame ready"
        };
      }
      if (hasFrameStarted(lastReturnedFrame.timestamp, time) && !hasFrameStarted(frameEndTimestamp.timestamp, time)) {
        return {
          type: "satisfied",
          frame: lastReturnedFrame
        };
      }
    }
    if (iteratorEnded) {
      if (lastReturnedFrame) {
        return {
          type: "satisfied",
          frame: lastReturnedFrame
        };
      }
      return {
        type: "not-satisfied",
        reason: "iterator ended"
      };
    }
    while (true) {
      if (!options.shouldContinue()) {
        return {
          type: "not-satisfied",
          reason: "seek was superseded"
        };
      }
      let frame = getNextOrNullIfNotAvailable();
      if (frame.type === "need-to-wait-for-it") {
        if (options.pendingFrameBehavior === "restart-iterator") {
          return {
            type: "not-satisfied",
            reason: "iterator did not have frame ready"
          };
        }
        options.onWait();
        const awaitedFrame = await frame.waitPromise();
        if (!options.shouldContinue()) {
          return {
            type: "not-satisfied",
            reason: "seek was superseded"
          };
        }
        frame = { type: "got-frame-or-end", frame: awaitedFrame };
      }
      if (frame.type === "got-frame-or-end") {
        if (frame.frame === null) {
          iteratorEnded = true;
          if (lastReturnedFrame) {
            return {
              type: "satisfied",
              frame: lastReturnedFrame
            };
          }
          return {
            type: "not-satisfied",
            reason: "iterator ended and did not have frame ready"
          };
        }
        const frameEndTimestamp = await getFrameEndTimestamp({
          onWait: options.onWait,
          pendingFrameBehavior: options.pendingFrameBehavior,
          shouldContinue: options.shouldContinue
        });
        if (frameEndTimestamp.type === "cancelled") {
          return {
            type: "not-satisfied",
            reason: "seek was superseded"
          };
        }
        if (frameEndTimestamp.type === "pending") {
          return {
            type: "not-satisfied",
            reason: "iterator did not have next frame ready"
          };
        }
        if (hasFrameStarted(frame.frame.timestamp, time) && !hasFrameStarted(frameEndTimestamp.timestamp, time)) {
          return {
            type: "satisfied",
            frame: frame.frame
          };
        }
        continue;
      }
      throw new Error("Unreachable");
    }
  };
  return {
    destroy,
    initialFrame,
    isDestroyed: () => {
      return destroyed;
    },
    tryToSatisfySeek
  };
};

// src/video-iterator-manager.ts
var { runEffectChain } = Internals6;
var isSequentialMediaTimeAdvance = ({
  previousTime,
  newTime,
  fps,
  playbackRate,
  isPlaying
}) => {
  if (!isPlaying || newTime < previousTime) {
    return false;
  }
  const maximumSequentialAdvance = Math.abs(playbackRate) / fps;
  return roundTo4Digits(newTime - previousTime) <= roundTo4Digits(maximumSequentialAdvance);
};
var videoIteratorManager = async ({
  getIsDisposed,
  delayPlaybackHandleIfNotPremounting,
  canvas,
  context,
  drawDebugOverlay,
  logLevel,
  getOnVideoFrameCallback,
  videoTrack,
  getLoopSegmentMediaEndTimestamp,
  getStartTime,
  getIsLooping,
  getEffects,
  getEffectChainState,
  maxCanvasSinkFrameSize
}) => {
  let videoIteratorsCreated = 0;
  let videoFrameIterator = null;
  let framesRendered = 0;
  let currentDelayHandle = null;
  let lastDrawnFrame = null;
  let lastDrawnMediaTime = null;
  let lastPlainFrameTime;
  let currentSeek = null;
  let lastPaintedEffects = [];
  let lastPaintedEffectChainState = null;
  let lastCompletedEffectPaint = null;
  let destroyed = false;
  let pendingEffectRun = null;
  const clearLastDrawnFrame = () => {
    lastDrawnFrame = null;
    lastDrawnMediaTime = null;
    if (lastPlainFrameTime !== undefined) {
      lastPlainFrameTime = null;
    }
    lastCompletedEffectPaint = null;
  };
  const displayWidth = await videoTrack.getDisplayWidth();
  const displayHeight = await videoTrack.getDisplayHeight();
  if (canvas && !getIsDisposed()) {
    if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
      lastPlainFrameTime = null;
      canvas.width = displayWidth;
      canvas.height = displayHeight;
    }
  }
  const maxWidth = maxCanvasSinkFrameSize?.width ?? Infinity;
  const maxHeight = maxCanvasSinkFrameSize?.height ?? Infinity;
  const frameSize = maxWidth / displayWidth < Math.min(1, maxHeight / displayHeight) ? { width: maxWidth, fit: "fill" } : maxHeight < displayHeight ? { height: maxHeight, fit: "fill" } : {};
  const videoSink = new VideoSampleSink(videoTrack);
  const prewarmedVideoIteratorCache = makePrewarmedVideoIteratorCache(videoSink, await videoTrack.getTimeResolution(), Object.keys(frameSize).length === 0 ? null : frameSize);
  const paintFrame = async (frame, mediaTime) => {
    if (destroyed) {
      return;
    }
    if (context && canvas) {
      lastPlainFrameTime = null;
      const effects = getEffects();
      lastPaintedEffects = effects;
      const chainState = getEffectChainState(canvas.width, canvas.height);
      lastPaintedEffectChainState = chainState;
      if (effects.length > 0 && chainState && canvas instanceof HTMLCanvasElement) {
        if (lastCompletedEffectPaint?.frame === frame && lastCompletedEffectPaint.effects === effects && lastCompletedEffectPaint.chainState === chainState) {
          return;
        }
        lastCompletedEffectPaint = null;
        const firstEnabledEffect = effects.find((effect) => !effect.params.disabled);
        const source = getStableEffectSource(frame, {
          width: canvas.width,
          height: canvas.height,
          allowDirectSource: !firstEnabledEffect || firstEnabledEffect.definition.backend === "2d"
        });
        const run = { state: chainState, runId: chainState.currentRunId + 1 };
        pendingEffectRun = run;
        try {
          const completed = await runEffectChain({
            state: chainState,
            source,
            effects,
            output: canvas,
            width: canvas.width,
            height: canvas.height
          });
          if (completed && pendingEffectRun === run) {
            lastCompletedEffectPaint = { frame, effects, chainState };
          }
        } finally {
          if (pendingEffectRun === run) {
            pendingEffectRun = null;
          }
        }
      } else {
        lastCompletedEffectPaint = null;
        context.clearRect(0, 0, canvas.width, canvas.height);
        drawStableFrame(context, frame, canvas.width, canvas.height);
        if (effects.length === 0) {
          lastPlainFrameTime = mediaTime;
        }
      }
    }
  };
  const drawFrame = async (frame, mediaTime) => {
    await paintFrame(frame, mediaTime);
    if (destroyed) {
      return;
    }
    lastDrawnFrame = frame;
    lastDrawnMediaTime = mediaTime;
    framesRendered++;
    drawDebugOverlay();
    const callback = getOnVideoFrameCallback();
    if (callback) {
      callback(frame.canvas);
    }
    Internals6.Log.trace({ logLevel, tag: "@remotion/media" }, `[MediaPlayer] Drew frame ${frame.timestamp.toFixed(3)}s`);
  };
  const redrawCurrentFrame = async () => {
    if (!lastDrawnFrame) {
      return;
    }
    await paintFrame(lastDrawnFrame, lastDrawnMediaTime);
    if (destroyed) {
      return;
    }
    drawDebugOverlay();
    const callback = getOnVideoFrameCallback();
    if (callback) {
      callback(lastDrawnFrame.canvas);
    }
    Internals6.Log.trace({ logLevel, tag: "@remotion/media" }, `[MediaPlayer] Redrew frame ${lastDrawnFrame.timestamp.toFixed(3)}s with updated effects`);
  };
  const startVideoIterator = async (timeToSeek, nonce, retryReclaimed) => {
    let __stack = [];
    try {
      clearLastDrawnFrame();
      videoFrameIterator?.destroy();
      const delayHandle = __using(__stack, delayPlaybackHandleIfNotPremounting(), 0);
      currentDelayHandle = delayHandle;
      currentSeek = timeToSeek;
      let iterator;
      try {
        iterator = await createVideoIterator(timeToSeek, prewarmedVideoIteratorCache);
      } catch (error) {
        videoFrameIterator = null;
        if (!retryReclaimed || !isCodecReclaimedError(error)) {
          throw error;
        }
        prewarmedVideoIteratorCache.destroy();
        currentSeek = null;
        if (destroyed || nonce.isStale()) {
          return;
        }
        iterator = await createVideoIterator(timeToSeek, prewarmedVideoIteratorCache);
        currentSeek = timeToSeek;
      }
      videoIteratorsCreated++;
      if (destroyed) {
        iterator.destroy();
        return;
      }
      videoFrameIterator = iterator;
      if (iterator.isDestroyed()) {
        return;
      }
      if (nonce.isStale()) {
        if (!videoFrameIterator.isDestroyed() && iterator.initialFrame) {
          await drawFrame(iterator.initialFrame, timeToSeek);
        }
        return;
      }
      if (videoFrameIterator.isDestroyed()) {
        return;
      }
      if (!iterator.initialFrame) {
        return;
      }
      await drawFrame(iterator.initialFrame, timeToSeek);
    } catch (_catch) {
      var _err = _catch, _hasErr = 1;
    } finally {
      __callDispose(__stack, _err, _hasErr);
    }
  };
  const seek = async ({
    newTime,
    nonce,
    fps,
    playbackRate,
    isPlaying,
    continuousPlayback
  }) => {
    if (!videoFrameIterator) {
      if (!destroyed) {
        await startVideoIterator(newTime, nonce, true);
      }
      return;
    }
    if (currentSeek !== null && roundTo4Digits(currentSeek) === roundTo4Digits(newTime)) {
      const effects = getEffects();
      const chainState = canvas ? getEffectChainState(canvas.width, canvas.height) : null;
      if ((effects !== lastPaintedEffects || chainState !== lastPaintedEffectChainState) && (effects.length > 0 || lastPaintedEffects.length > 0)) {
        await redrawCurrentFrame();
      }
      return;
    }
    const previousTime = currentSeek;
    if (getIsLooping()) {
      if (getLoopSegmentMediaEndTimestamp() - newTime < 1) {
        prewarmedVideoIteratorCache.prewarmIteratorForLooping({
          timeToSeek: getStartTime()
        });
      }
    }
    const pendingFrameBehavior = previousTime !== null && newTime >= previousTime && (continuousPlayback ?? isSequentialMediaTimeAdvance({
      previousTime,
      newTime,
      fps,
      playbackRate,
      isPlaying
    })) ? "wait" : "restart-iterator";
    const iterator = videoFrameIterator;
    const pending = {
      handle: null
    };
    try {
      const result = await iterator.tryToSatisfySeek(newTime, {
        pendingFrameBehavior,
        onWait: () => {
          pending.handle ??= delayPlaybackHandleIfNotPremounting();
          currentDelayHandle = pending.handle;
        },
        shouldContinue: () => !nonce.isStale() && !iterator.isDestroyed()
      }).catch((error) => {
        if (isCodecReclaimedError(error)) {
          return { type: "decoder-reclaimed" };
        }
        throw error;
      });
      if (result.type === "decoder-reclaimed") {
        Internals6.Log.verbose({ logLevel, tag: "@remotion/media" }, `[MediaPlayer] Video decoder was reclaimed, recreating it at ${newTime.toFixed(3)}s`);
        prewarmedVideoIteratorCache.destroy();
        iterator.destroy();
        clearLastDrawnFrame();
        videoFrameIterator = null;
        currentSeek = null;
        if (destroyed || nonce.isStale()) {
          return;
        }
        pending.handle?.unblock();
        pending.handle = null;
        currentDelayHandle = null;
        await startVideoIterator(newTime, nonce, false);
        return;
      }
      if (nonce.isStale() || iterator.isDestroyed()) {
        return;
      }
      if (result.type === "satisfied") {
        await drawFrame(result.frame, newTime);
        currentSeek = newTime;
        return;
      }
      pending.handle?.unblock();
      pending.handle = null;
      currentDelayHandle = null;
      await startVideoIterator(newTime, nonce, true);
    } finally {
      pending.handle?.unblock();
      if (currentDelayHandle === pending.handle) {
        currentDelayHandle = null;
      }
    }
  };
  return {
    startVideoIterator: (timeToSeek, nonce) => startVideoIterator(timeToSeek, nonce, true),
    getVideoIteratorsCreated: () => videoIteratorsCreated,
    seek,
    destroy: () => {
      if (destroyed) {
        return;
      }
      destroyed = true;
      if (pendingEffectRun && pendingEffectRun.state.currentRunId === pendingEffectRun.runId) {
        pendingEffectRun.state.currentRunId++;
      }
      pendingEffectRun = null;
      clearLastDrawnFrame();
      prewarmedVideoIteratorCache.destroy();
      videoFrameIterator?.destroy();
      if (context && canvas && framesRendered > 0) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (currentDelayHandle) {
        currentDelayHandle.unblock();
        currentDelayHandle = null;
      }
      videoFrameIterator = null;
    },
    getVideoFrameIterator: () => videoFrameIterator,
    getPlainFrameTime: (copiedFrameTime) => {
      if (destroyed || pendingEffectRun || lastCompletedEffectPaint) {
        return null;
      }
      return lastPlainFrameTime === undefined ? copiedFrameTime : lastPlainFrameTime;
    },
    drawFrame,
    redrawCurrentFrame,
    getFramesRendered: () => framesRendered
  };
};

// src/media-player.ts
class MediaPlayer {
  tagType;
  canvas;
  context;
  src;
  logLevel;
  playbackRate;
  toneFrequency;
  globalPlaybackRate;
  audioStreamIndex;
  sharedAudioContext;
  audioIteratorManager = null;
  muted = false;
  unsupportedAudioError = null;
  videoIteratorManager = null;
  playing = false;
  lastSeekIntent = null;
  satisfiedSeekRevision = null;
  loop = false;
  fps;
  trimBefore;
  trimAfter;
  sequenceDurationInFrames;
  sequenceOffset;
  totalDuration;
  debugOverlay = false;
  nonceManager;
  onVideoFrameCallback = null;
  getEffects;
  getEffectChainState;
  maxCanvasSinkFrameSize;
  initializationPromise = null;
  premountAwareDelayPlayback;
  seekPromiseChain = Promise.resolve();
  terminalError = null;
  onError;
  constructor({
    canvas,
    src,
    logLevel,
    sharedAudioContext,
    loop,
    trimBefore,
    trimAfter,
    playbackRate,
    toneFrequency,
    globalPlaybackRate,
    audioStreamIndex,
    fps,
    debugOverlay,
    bufferState,
    isPremounting,
    isPostmounting,
    durationInFrames,
    onVideoFrameCallback,
    playing,
    sequenceOffset,
    credentials,
    requestInit,
    tagType,
    getEffects,
    getEffectChainState,
    maxCanvasSinkFrameSize,
    onError
  }) {
    this.canvas = canvas ?? null;
    this.src = src;
    this.logLevel = logLevel;
    this.sharedAudioContext = sharedAudioContext;
    this.playbackRate = playbackRate;
    this.toneFrequency = toneFrequency;
    this.globalPlaybackRate = globalPlaybackRate;
    this.loop = loop;
    this.trimBefore = trimBefore;
    this.trimAfter = trimAfter;
    this.audioStreamIndex = audioStreamIndex;
    this.fps = fps;
    this.debugOverlay = debugOverlay;
    this.premountAwareDelayPlayback = new PremountAwareDelayPlayback({
      bufferState,
      isPremounting,
      isPostmounting
    });
    this.sequenceDurationInFrames = durationInFrames;
    this.nonceManager = makeNonceManager();
    this.onVideoFrameCallback = onVideoFrameCallback;
    this.playing = playing;
    this.sequenceOffset = sequenceOffset;
    const { input, getDuration, release } = acquireSharedInput({
      src: this.src,
      credentials,
      requestInit,
      logLevel
    });
    this.input = input;
    this.getDuration = getDuration;
    this.releaseInput = release;
    this.tagType = tagType;
    this.getEffects = getEffects;
    this.getEffectChainState = getEffectChainState;
    this.maxCanvasSinkFrameSize = maxCanvasSinkFrameSize;
    this.onError = onError;
    if (canvas) {
      const context = canvas.getContext("2d", {
        alpha: true
      });
      if (!context) {
        throw new Error("Could not get 2D context from canvas");
      }
      this.context = context;
    } else {
      this.context = null;
    }
  }
  input;
  getDuration;
  releaseInput;
  disposed = false;
  isDisposalError() {
    return this.disposed || this.input.disposed === true;
  }
  reportTerminalError(error) {
    if (this.terminalError || this.isDisposalError()) {
      return;
    }
    this.terminalError = error;
    this.playing = false;
    this.audioIteratorManager?.destroyIterator();
    this.videoIteratorManager?.destroy();
    this.onError?.(error);
  }
  initialize(startTimeUnresolved, initialMuted, initialVolume) {
    this.muted = initialMuted;
    const promise = this._initialize(startTimeUnresolved, initialMuted, initialVolume);
    this.initializationPromise = promise;
    this.seekPromiseChain = promise;
    return promise;
  }
  getStartTime() {
    return (this.trimBefore ?? 0) / this.fps;
  }
  getSequenceEndTimestamp() {
    return this.sequenceDurationInFrames / this.fps * this.playbackRate + this.getStartTime();
  }
  getSequenceDurationInSeconds() {
    return this.sequenceDurationInFrames / this.fps;
  }
  getMediaEndTimestamp() {
    return calculateEndTime({
      mediaDurationInSeconds: this.totalDuration,
      ifNoMediaDuration: "fail",
      src: this.src,
      trimAfter: this.trimAfter,
      trimBefore: this.trimBefore,
      fps: this.fps
    });
  }
  getLoopSegmentMediaEndTimestamp() {
    return Math.min(this.getMediaEndTimestamp(), this.getSequenceEndTimestamp());
  }
  async _initialize(startTimeUnresolved, initialMuted, initialVolume) {
    let __stack = [];
    try {
      const _ = __using(__stack, this.delayPlaybackHandleIfNotPremounting(), 0);
      try {
        if (this.isDisposalError()) {
          return { type: "disposed" };
        }
        try {
          await this.input.getFormat();
        } catch (error) {
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          const err = error;
          if (isNetworkError(err)) {
            throw error;
          }
          Internals7.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, `[MediaPlayer] Failed to recognize format for ${this.src}`, error);
          return { type: "unknown-container-format", error: err };
        }
        const [durationInSeconds, videoTrack, audioTracks] = await Promise.all([
          this.getDuration(),
          this.input.getPrimaryVideoTrack(),
          this.input.getAudioTracks()
        ]);
        if (this.isDisposalError()) {
          return { type: "disposed" };
        }
        this.totalDuration = durationInSeconds;
        const audioTrack = await resolveAudioTrack({
          videoTrack,
          audioTracks,
          audioStreamIndex: this.audioStreamIndex
        });
        if (!videoTrack && !audioTrack) {
          return { type: "no-tracks" };
        }
        if (videoTrack && this.tagType === "video") {
          if (await videoTrack.isLive()) {
            throw new Error("Live streams are not currently supported by Remotion. Sorry! Source: " + this.src);
          }
          if (await videoTrack.isRelativeToUnixEpoch()) {
            throw new Error("Streams with UNIX timestamps are not currently supported by Remotion. Sorry! Source: " + this.src);
          }
          const canDecode = await videoTrack.canDecode();
          if (!canDecode) {
            if (videoTrack.codec === "prores") {
              return { type: "cannot-decode-prores" };
            }
            return {
              type: "cannot-decode",
              error: new Error(`Cannot decode video track ${videoTrack.id} (${videoTrack.codec ?? "unknown codec"}) in ${this.src}.`)
            };
          }
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          this.videoIteratorManager = await videoIteratorManager({
            getIsDisposed: () => this.isDisposalError(),
            videoTrack,
            delayPlaybackHandleIfNotPremounting: this.delayPlaybackHandleIfNotPremounting,
            context: this.context,
            canvas: this.canvas,
            getOnVideoFrameCallback: () => this.onVideoFrameCallback,
            logLevel: this.logLevel,
            drawDebugOverlay: this.drawDebugOverlay,
            getLoopSegmentMediaEndTimestamp: () => this.getLoopSegmentMediaEndTimestamp(),
            getStartTime: () => this.getStartTime(),
            getIsLooping: () => this.loop,
            getEffects: this.getEffects,
            getEffectChainState: this.getEffectChainState,
            maxCanvasSinkFrameSize: this.maxCanvasSinkFrameSize
          });
        }
        if (this.isDisposalError()) {
          return { type: "disposed" };
        }
        const startTime = this.getTrimmedTime(startTimeUnresolved);
        if (startTime === null) {
          throw new Error(`should have asserted that the time is not null`);
        }
        if (audioTrack && this.sharedAudioContext) {
          if (await audioTrack.isLive()) {
            throw new Error("Live streams are not currently supported by Remotion. Sorry! Source: " + this.src);
          }
          if (await audioTrack.isRelativeToUnixEpoch()) {
            throw new Error("Streams with UNIX timestamps are not currently supported by Remotion. Sorry! Source: " + this.src);
          }
          const canDecode = await audioTrack.canDecode();
          if (!canDecode && !this.muted) {
            return {
              type: "cannot-decode",
              error: new Error(`Cannot decode audio track ${audioTrack.id} (${audioTrack.codec ?? "unknown codec"}) in ${this.src}.`)
            };
          }
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          if (canDecode) {
            this.audioIteratorManager = audioIteratorManager({
              audioTrack,
              delayPlaybackHandleIfNotPremounting: this.delayPlaybackHandleIfNotPremounting,
              sharedAudioContext: this.sharedAudioContext,
              getMediaEndTimestamp: () => this.getMediaEndTimestamp(),
              getSequenceEndTimestamp: () => this.getSequenceEndTimestamp(),
              getStartTime: () => this.getStartTime(),
              getIsPlaying: () => this.playing,
              initialMuted,
              initialVolume,
              toneFrequency: this.toneFrequency,
              drawDebugOverlay: this.drawDebugOverlay,
              onError: (error) => this.reportTerminalError(error),
              getSequenceDurationInSeconds: () => this.getSequenceDurationInSeconds()
            });
          } else {
            this.unsupportedAudioError = new Error(`Cannot decode audio for ${this.src}.`);
          }
        }
        const nonce = this.nonceManager.createAsyncOperation();
        try {
          await Promise.all([
            this.audioIteratorManager ? this.audioIteratorManager.startAudioIterator({
              nonce,
              playbackRate: this.playbackRate * this.globalPlaybackRate,
              startFromSecond: startTime,
              unloopedStartFromSecond: startTimeUnresolved,
              scheduleAudioNode: this.scheduleAudioNode,
              getTargetTime: this.getTargetTime,
              logLevel: this.logLevel,
              loop: this.loop,
              unscheduleAudioNode: this.sharedAudioContext.unscheduleAudioNode,
              getAudioContextCurrentTimeMockedInTest: () => this.sharedAudioContext.audioContext.currentTime
            }) : Promise.resolve(),
            this.videoIteratorManager ? this.videoIteratorManager.startVideoIterator(startTime, nonce) : Promise.resolve()
          ]);
        } catch (error) {
          if (this.isDisposalError()) {
            return { type: "disposed" };
          }
          Internals7.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, "[MediaPlayer] Failed to start audio and video iterators", error);
        }
        if (this.isDisposalError()) {
          return { type: "disposed" };
        }
        return { type: "success", durationInSeconds };
      } catch (error) {
        if (this.isDisposalError()) {
          return { type: "disposed" };
        }
        const err = error;
        if (isNetworkError(err)) {
          Internals7.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, `[MediaPlayer] Network/CORS error for ${this.src}`, err);
          return { type: "network-error", error: err };
        }
        Internals7.Log.error({ logLevel: this.logLevel, tag: "@remotion/media" }, "[MediaPlayer] Failed to initialize", error);
        throw error;
      }
    } catch (_catch) {
      var _err = _catch, _hasErr = 1;
    } finally {
      __callDispose(__stack, _err, _hasErr);
    }
  }
  seekToWithQueue = async (newTime, unloopedNewTime, intent) => {
    const nonce = this.nonceManager.createAsyncOperation();
    await this.seekPromiseChain;
    this.seekPromiseChain = this.seekToDoNotCallDirectly(newTime, unloopedNewTime, nonce, intent);
    await this.seekPromiseChain;
  };
  async seekTo(time, intent) {
    if (this.terminalError || this.isDisposalError()) {
      return;
    }
    const newTime = this.getTrimmedTime(time);
    if (newTime === null) {
      throw new Error(`should have asserted that the time is not null`);
    }
    this.lastSeekIntent = intent;
    if (intent === "discontinuity") {
      this.satisfiedSeekRevision = null;
    }
    await this.seekToWithQueue(newTime, time, intent);
  }
  async seekToDoNotCallDirectly(newTime, unloopedNewTime, nonce, intent) {
    if (nonce.isStale() || this.terminalError || this.isDisposalError()) {
      return;
    }
    try {
      await Promise.all([
        this.videoIteratorManager?.seek({
          newTime,
          nonce,
          fps: this.fps,
          playbackRate: this.playbackRate,
          isPlaying: this.playing,
          continuousPlayback: intent === null ? null : intent !== "discontinuity" && intent.playing && intent.revision === this.satisfiedSeekRevision
        }),
        this.audioIteratorManager?.seek({
          newTime,
          unloopedNewTime,
          nonce,
          playbackRate: this.playbackRate * this.globalPlaybackRate,
          localPlaybackRate: this.playbackRate,
          getTargetTime: this.getTargetTime,
          logLevel: this.logLevel,
          loop: this.loop,
          trimBefore: this.trimBefore,
          trimAfter: this.trimAfter,
          sequenceOffset: this.sequenceOffset,
          sequenceDurationInFrames: this.sequenceDurationInFrames,
          fps: this.fps,
          scheduleAudioNode: this.scheduleAudioNode,
          getAudioContextCurrentTimeMockedInTest: () => this.sharedAudioContext.audioContext.currentTime
        })
      ]);
      if (!nonce.isStale() && intent !== null && intent !== "discontinuity") {
        this.satisfiedSeekRevision = intent.revision;
      }
    } catch (error) {
      if (this.isDisposalError()) {
        return;
      }
      this.reportTerminalError(error);
    }
  }
  play() {
    if (this.playing || this.terminalError) {
      return;
    }
    this.playing = true;
    this.drawDebugOverlay();
  }
  delayPlaybackHandleIfNotPremounting = () => {
    return this.premountAwareDelayPlayback.createHandle();
  };
  pause() {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    this.drawDebugOverlay();
  }
  setMuted(muted) {
    this.muted = muted;
    if (!muted && this.unsupportedAudioError) {
      this.reportTerminalError(this.unsupportedAudioError);
      return;
    }
    this.audioIteratorManager?.setMuted(muted);
  }
  setVolume(volume) {
    if (!this.audioIteratorManager) {
      return;
    }
    this.audioIteratorManager.setVolume(volume);
  }
  getTrimmedTime(unloopedTimeInSeconds) {
    return getTimeInSeconds({
      unloopedTimeInSeconds,
      playbackRate: this.playbackRate,
      loop: this.loop,
      trimBefore: this.trimBefore,
      trimAfter: this.trimAfter,
      mediaDurationInSeconds: this.totalDuration ?? null,
      fps: this.fps,
      ifNoMediaDuration: "infinity",
      src: this.src
    });
  }
  async setTrimRange(trimBefore, trimAfter, unloopedTimeInSeconds) {
    if (this.trimBefore !== trimBefore || this.trimAfter !== trimAfter) {
      this.trimBefore = trimBefore;
      this.trimAfter = trimAfter;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  setDebugOverlay(debugOverlay) {
    this.debugOverlay = debugOverlay;
  }
  async setPlaybackRate(rate, unloopedTimeInSeconds) {
    const previousRate = this.playbackRate;
    if (previousRate !== rate) {
      this.playbackRate = rate;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async setToneFrequency(toneFrequency, unloopedTimeInSeconds) {
    if (this.toneFrequency !== toneFrequency) {
      this.toneFrequency = toneFrequency;
      if (!this.audioIteratorManager) {
        return;
      }
      this.audioIteratorManager.setToneFrequency(toneFrequency);
      this.audioIteratorManager.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async setGlobalPlaybackRate(rate, unloopedTimeInSeconds) {
    const previousRate = this.globalPlaybackRate;
    if (previousRate !== rate) {
      this.globalPlaybackRate = rate;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async setFps(fps, unloopedTimeInSeconds) {
    const previousFps = this.fps;
    if (previousFps !== fps) {
      this.fps = fps;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  setIsPremounting(isPremounting) {
    this.premountAwareDelayPlayback.setIsPremounting(isPremounting);
    processNext();
  }
  setIsPostmounting(isPostmounting) {
    this.premountAwareDelayPlayback.setIsPostmounting(isPostmounting);
    processNext();
  }
  async setLoop(loop, unloopedTimeInSeconds) {
    const previousLoop = this.loop;
    if (previousLoop !== loop) {
      this.loop = loop;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async setSequenceOffset(offset, unloopedTimeInSeconds) {
    const previousOffset = this.sequenceOffset;
    if (previousOffset !== offset) {
      this.sequenceOffset = offset;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async setSequenceDurationInFrames(sequenceDurationInFrames, unloopedTimeInSeconds) {
    const previousDuration = this.sequenceDurationInFrames;
    if (previousDuration !== sequenceDurationInFrames) {
      this.sequenceDurationInFrames = sequenceDurationInFrames;
      this.audioIteratorManager?.destroyIterator();
      await this.seekTo(unloopedTimeInSeconds, "discontinuity");
    }
  }
  async dispose() {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.nonceManager.createAsyncOperation();
    this.videoIteratorManager?.destroy();
    this.audioIteratorManager?.destroyIterator();
    this.premountAwareDelayPlayback.dispose();
    this.releaseInput();
    if (this.initializationPromise) {
      try {
        await this.initializationPromise;
      } catch {}
    }
    this.videoIteratorManager?.destroy();
    this.audioIteratorManager?.destroyIterator();
  }
  getTargetTime = (mediaTimestamp, currentTime) => {
    if (!this.sharedAudioContext) {
      throw new Error("Shared audio context not found");
    }
    const globalTime = (currentTime - this.sharedAudioContext.audioSyncAnchor.value) * this.globalPlaybackRate;
    const timeInSeconds = globalTime - this.sequenceOffset;
    const anchor = this.loop ? this.audioIteratorManager?.getCurrentAnchor() ?? null : null;
    const localTime = anchor ? anchorToContinuousTime({
      anchor,
      unloopedTimeInSeconds: timeInSeconds,
      playbackRate: this.playbackRate
    }) : this.getTrimmedTime(timeInSeconds);
    if (localTime === null) {
      return null;
    }
    const targetTime = (mediaTimestamp - localTime) / (this.playbackRate * this.globalPlaybackRate);
    return targetTime;
  };
  scheduleAudioNode = (node, mediaTimestamp, originalUnloopedMediaTimestamp, sourceOffsetInSeconds, sourceDurationInSeconds) => {
    if (!this.sharedAudioContext) {
      throw new Error("Shared audio context not found");
    }
    const targetTime = this.getTargetTime(mediaTimestamp, this.sharedAudioContext.audioContext.currentTime);
    const combinedPlaybackRate = this.playbackRate * this.globalPlaybackRate;
    if (targetTime === null) {
      return {
        type: "not-started",
        reason: "no target for" + mediaTimestamp.toFixed(3) + "," + this.sharedAudioContext.audioContext.currentTime.toFixed(3)
      };
    }
    const sequenceStartTime = this.getStartTime();
    const offset = getTrimStartForAudioNode({
      mediaTimestamp,
      targetTime,
      sequenceStartTime,
      combinedPlaybackRate,
      sourceStartOffsetInSeconds: sourceOffsetInSeconds
    });
    const duration = getDurationOfNode({
      sourceDurationInSeconds,
      sourceOffsetInSeconds,
      offset
    });
    const scheduledTime = getScheduledTime({
      mediaTimestamp,
      targetTime,
      sequenceStartTime,
      currentTime: this.sharedAudioContext.audioContext.currentTime
    });
    return this.sharedAudioContext.scheduleAudioNode({
      node,
      mediaTimestamp,
      sourceOffset: sourceOffsetInSeconds,
      scheduledTime,
      duration,
      offset,
      originalUnloopedMediaTimestamp
    });
  };
  setVideoFrameCallback(callback) {
    this.onVideoFrameCallback = callback;
  }
  async redrawVideoEffects() {
    await this.videoIteratorManager?.redrawCurrentFrame();
  }
  drawDebugOverlay = () => {
    if (!this.debugOverlay)
      return;
    if (this.context && this.canvas) {
      drawPreviewOverlay({
        context: this.context,
        audioTime: this.sharedAudioContext?.audioContext.currentTime ?? null,
        audioContextState: this.sharedAudioContext?.audioContext.state ?? null,
        audioSyncAnchor: this.sharedAudioContext?.audioSyncAnchor ?? null,
        audioIteratorManager: this.audioIteratorManager,
        playing: this.playing,
        videoIteratorManager: this.videoIteratorManager,
        playbackRate: this.playbackRate * this.globalPlaybackRate
      });
    }
  };
  audioSyncAnchorChanged = async (unloopedTimeInSeconds) => {
    if (!this.audioIteratorManager) {
      return;
    }
    if (this.audioIteratorManager.rescheduleAudio(this.getTrimmedTime(unloopedTimeInSeconds), unloopedTimeInSeconds, this.playbackRate, this.loop)) {
      return;
    }
    const intent = this.lastSeekIntent;
    await this.seekTo(unloopedTimeInSeconds, intent !== null && intent !== "discontinuity" ? { ...intent, playing: true } : intent);
  };
}

// src/on-error.ts
var callOnErrorAndResolve = ({
  onError,
  error,
  disallowFallback,
  isClientSideRendering,
  clientSideError
}) => {
  const result = onError?.(error);
  if (isClientSideRendering) {
    return ["fail", clientSideError];
  }
  if (result === "fail" || result === "fallback") {
    return [result, error];
  }
  if (disallowFallback) {
    return ["fail", error];
  }
  return ["fallback", error];
};

// src/use-common-effects.ts
import { useContext, useLayoutEffect, useRef } from "react";
import { Internals as Internals8 } from "remotion";
var useCommonEffects = ({
  mediaPlayerRef,
  mediaPlayerReady,
  currentTimeRef,
  playing,
  isPlayerBuffering,
  frame,
  trimBefore,
  trimAfter,
  effectiveMuted,
  userPreferredVolume,
  playbackRate,
  toneFrequency,
  globalPlaybackRate,
  fps,
  sequenceOffset,
  loop,
  durationInFrames,
  isPremounting,
  isPostmounting,
  currentTime,
  logLevel,
  label
}) => {
  const sharedAudioContext = useContext(Internals8.SharedAudioContext);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer)
      return;
    if (playing && !isPlayerBuffering) {
      mediaPlayer.play();
    } else {
      mediaPlayer.pause();
    }
  }, [
    isPlayerBuffering,
    playing,
    logLevel,
    mediaPlayerReady,
    frame,
    mediaPlayerRef
  ]);
  useLayoutEffect(() => {
    if (!sharedAudioContext)
      return;
    const { remove } = sharedAudioContext.audioSyncAnchorEmitter.subscribe((event) => {
      if (event === "changed") {
        mediaPlayerRef.current?.audioSyncAnchorChanged(currentTimeRef.current).catch(() => {});
      }
    });
    return () => {
      remove();
    };
  }, [sharedAudioContext, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setTrimRange(trimBefore, trimAfter, currentTimeRef.current);
  }, [trimAfter, trimBefore, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady)
      return;
    mediaPlayer.setMuted(effectiveMuted);
  }, [effectiveMuted, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setVolume(userPreferredVolume);
  }, [userPreferredVolume, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setPlaybackRate(playbackRate, currentTimeRef.current);
  }, [playbackRate, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setToneFrequency(toneFrequency, currentTimeRef.current);
  }, [toneFrequency, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setGlobalPlaybackRate(globalPlaybackRate, currentTimeRef.current);
  }, [globalPlaybackRate, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setLoop(loop, currentTimeRef.current);
  }, [loop, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setSequenceDurationInFrames(durationInFrames, currentTimeRef.current);
  }, [durationInFrames, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer) {
      return;
    }
    mediaPlayer.setIsPremounting(isPremounting);
  }, [isPremounting, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer) {
      return;
    }
    mediaPlayer.setIsPostmounting(isPostmounting);
  }, [isPostmounting, mediaPlayerReady, mediaPlayerRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setFps(fps, currentTimeRef.current);
  }, [fps, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setSequenceOffset(sequenceOffset, currentTimeRef.current);
  }, [sequenceOffset, mediaPlayerReady, mediaPlayerRef, currentTimeRef]);
  const { seek } = useContext(Internals8.SetTimelineContext);
  const revision = seek?.revision.current ?? null;
  const lastRequest = useRef(null);
  useLayoutEffect(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady)
      return;
    const previous = lastRequest.current;
    if (previous?.player === mediaPlayer && previous.time === currentTime && previous.revision === revision && previous.isPremounting === isPremounting && previous.muted === effectiveMuted) {
      return;
    }
    lastRequest.current = {
      player: mediaPlayer,
      time: currentTime,
      revision,
      isPremounting,
      muted: effectiveMuted
    };
    mediaPlayer.seekTo(currentTime, revision === null ? null : { revision, playing }).catch(() => {});
    Internals8.Log.trace({ logLevel, tag: "@remotion/media" }, `[${label}] Updating target time to ${currentTime.toFixed(3)}s`);
  }, [
    currentTime,
    effectiveMuted,
    isPremounting,
    logLevel,
    mediaPlayerReady,
    label,
    mediaPlayerRef,
    playing,
    revision
  ]);
};

// src/use-media-player-muted.ts
import { Internals as Internals9 } from "remotion";
var useMediaPlayerMuted = ({
  muted,
  volume
}) => {
  const [playerMuted] = Internals9.usePlayerMutedState();
  const isInsideNonPremountFreeze = Internals9.useIsInsideNonPremountFreeze();
  return muted || playerMuted || volume <= 0 || isInsideNonPremountFreeze;
};

// src/audio/audio-for-preview.tsx
import { jsx } from "react/jsx-runtime";
var {
  useUnsafeVideoConfig,
  SharedAudioContext,
  useMediaVolumeState,
  useFrameForVolumeProp,
  evaluateVolume,
  warnAboutTooHighVolume,
  usePreload,
  SequenceContext,
  usePlaying,
  useBuffering,
  useMediaAudioState
} = Internals10;
var AudioForPreviewAssertedShowing = ({
  src,
  playbackRate,
  logLevel,
  muted,
  volume,
  loopVolumeCurveBehavior,
  loop,
  trimAfter,
  trimBefore,
  name,
  showInTimeline,
  _remotionInternalStack,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  preservePitch,
  audioStreamIndex,
  fallbackHtml5AudioProps,
  onError,
  credentials,
  requestInit,
  setMediaDurationInSeconds,
  style
}) => {
  const videoConfig = useUnsafeVideoConfig();
  const frame = useCurrentFrame();
  const mediaPlayerRef = useRef2(null);
  const initialTrimBeforeRef = useRef2(trimBefore);
  const initialTrimAfterRef = useRef2(trimAfter);
  const [initialRequestInit] = useState(requestInit);
  const [mediaPlayerReady, setMediaPlayerReady] = useState(false);
  const [knownDuration, setKnownDuration] = useState(null);
  const [terminalError, setTerminalError] = useState(null);
  const [shouldFallbackToNativeAudio, setShouldFallbackToNativeAudio] = useState(false);
  const playing = usePlaying();
  const { playbackRate: globalPlaybackRate } = Internals10.usePlaybackRate();
  const preservePitchOnPlayback = useContext2(Internals10.PlaybackPitchContext);
  const sharedAudioContext = useContext2(SharedAudioContext);
  const buffer = useBufferState();
  const [mediaVolume] = useMediaVolumeState();
  const unloopedVolumePropFrame = useFrameForVolumeProp(loopVolumeCurveBehavior ?? "repeat");
  const volumePropFrame = loop && videoConfig ? frameForVolumeProp({
    behavior: loopVolumeCurveBehavior,
    loop,
    assetDurationInSeconds: knownDuration,
    fps: videoConfig.fps,
    frame,
    startsAt: unloopedVolumePropFrame - frame,
    playbackRate,
    trimBefore,
    trimAfter
  }) : unloopedVolumePropFrame;
  const userPreferredVolume = evaluateVolume({
    frame: volumePropFrame,
    volume,
    mediaVolume
  });
  warnAboutTooHighVolume(userPreferredVolume);
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  if (!src) {
    throw new TypeError("No `src` was passed to <NewAudioForPreview>.");
  }
  const parentSequence = useContext2(SequenceContext);
  const sequencePlaybackRate = parentSequence?.playbackRate ?? 1;
  const effectivePlaybackRate = playbackRate * sequencePlaybackRate;
  const effectiveToneFrequency = (toneFrequency ?? 1) / (preservePitch ? effectivePlaybackRate : 1) / (preservePitchOnPlayback && globalPlaybackRate > 0 ? globalPlaybackRate : 1);
  const initialToneFrequencyRef = useRef2(effectiveToneFrequency);
  const sequenceDurationInFrames = videoConfig.durationInFrames / sequencePlaybackRate;
  const currentTime = frame / sequencePlaybackRate / videoConfig.fps;
  const currentTimeRef = useRef2(currentTime);
  currentTimeRef.current = currentTime;
  const preloadedSrc = usePreload(src);
  const isPremounting = Boolean(parentSequence?.premounting);
  const isPostmounting = Boolean(parentSequence?.postmounting);
  const sequenceOffset = (parentSequence?.absoluteFrom ?? 0) / videoConfig.fps;
  const { isMutedForPlayback: effectiveMuted } = useMediaAudioState({
    muted,
    volume: userPreferredVolume,
    audioEnabled: true
  });
  const mediaPlayerMuted = useMediaPlayerMuted({
    muted,
    volume: userPreferredVolume
  });
  const isPlayerBuffering = useBuffering();
  const initialPlaying = useRef2(playing && !isPlayerBuffering);
  const initialIsPremounting = useRef2(isPremounting);
  const initialIsPostmounting = useRef2(isPostmounting);
  const initialGlobalPlaybackRate = useRef2(globalPlaybackRate);
  const initialPlaybackRate = useRef2(effectivePlaybackRate);
  const initialMuted = useRef2(mediaPlayerMuted);
  const initialVolume = useRef2(userPreferredVolume);
  const initialDurationInFrames = useRef2(sequenceDurationInFrames);
  const initialSequenceOffset = useRef2(sequenceOffset);
  const onErrorRef = useRef2(onError);
  onErrorRef.current = onError;
  const disallowFallbackRef = useRef2(disallowFallbackToHtml5Audio);
  disallowFallbackRef.current = disallowFallbackToHtml5Audio;
  useCommonEffects({
    mediaPlayerRef,
    mediaPlayerReady,
    currentTimeRef,
    playing,
    isPlayerBuffering,
    frame,
    trimBefore,
    trimAfter,
    effectiveMuted: mediaPlayerMuted,
    userPreferredVolume,
    playbackRate: effectivePlaybackRate,
    toneFrequency: effectiveToneFrequency,
    globalPlaybackRate,
    fps: videoConfig.fps,
    sequenceOffset,
    loop,
    durationInFrames: sequenceDurationInFrames,
    isPremounting,
    isPostmounting,
    currentTime,
    logLevel,
    label: "AudioForPreview"
  });
  useEffect(() => {
    if (!sharedAudioContext)
      return;
    if (!sharedAudioContext.audioContext)
      return;
    const {
      audioContext,
      gainNode,
      audioSyncAnchor,
      scheduleAudioNode,
      unscheduleAudioNode
    } = sharedAudioContext;
    if (!gainNode) {
      return;
    }
    try {
      const player = new MediaPlayer({
        src: preloadedSrc,
        logLevel,
        sharedAudioContext: {
          audioContext,
          gainNode,
          audioSyncAnchor,
          scheduleAudioNode,
          unscheduleAudioNode
        },
        loop,
        trimAfter: initialTrimAfterRef.current,
        trimBefore: initialTrimBeforeRef.current,
        fps: videoConfig.fps,
        canvas: null,
        playbackRate: initialPlaybackRate.current,
        toneFrequency: initialToneFrequencyRef.current,
        audioStreamIndex: audioStreamIndex ?? null,
        debugOverlay: false,
        bufferState: buffer,
        isPostmounting: initialIsPostmounting.current,
        isPremounting: initialIsPremounting.current,
        globalPlaybackRate: initialGlobalPlaybackRate.current,
        durationInFrames: initialDurationInFrames.current,
        onVideoFrameCallback: null,
        playing: initialPlaying.current,
        sequenceOffset: initialSequenceOffset.current,
        credentials,
        requestInit: initialRequestInit,
        tagType: "audio",
        getEffects: () => [],
        getEffectChainState: () => null,
        maxCanvasSinkFrameSize: null,
        onError: (error) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError: onErrorRef.current,
            error,
            disallowFallback: disallowFallbackRef.current,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fallback") {
            setShouldFallbackToNativeAudio(true);
          } else {
            setTerminalError(errorToUse);
          }
        }
      });
      mediaPlayerRef.current = player;
      player.initialize(currentTimeRef.current, initialMuted.current, initialVolume.current).then((result) => {
        if (result.type === "disposed") {
          return;
        }
        const handleError = (error, fallbackMessage) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError,
            error,
            disallowFallback: disallowFallbackToHtml5Audio,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fail") {
            throw errorToUse;
          } else {
            Internals10.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
            setShouldFallbackToNativeAudio(true);
          }
        };
        if (result.type === "unknown-container-format" || result.type === "network-error") {
          throw result.error;
        }
        if (result.type === "cannot-decode") {
          handleError(result.error, `Cannot decode ${preloadedSrc}, falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "no-tracks") {
          handleError(new Error(`No video or audio tracks found for ${preloadedSrc}.`), `No video or audio tracks found for ${preloadedSrc}, falling back to <Html5Audio>`);
          return;
        }
        if (result.type === "cannot-decode-prores") {
          throw new Error(`Encountered ProRes media for ${preloadedSrc}. But this should never happen, since you used the <Audio> tag. Please report this as a bug.`);
        }
        if (result.type === "success") {
          setMediaPlayerReady(true);
          setMediaDurationInSeconds(result.durationInSeconds);
          setKnownDuration(result.durationInSeconds);
          Internals10.Log.trace({ logLevel, tag: "@remotion/media" }, `[AudioForPreview] MediaPlayer initialized successfully`);
        }
      }).catch((error) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error,
          disallowFallback: disallowFallbackToHtml5Audio,
          isClientSideRendering: false,
          clientSideError: error
        });
        if (action === "fail") {
          throw errorToUse;
        } else {
          Internals10.Log.error({ logLevel, tag: "@remotion/media" }, "[AudioForPreview] Failed to initialize MediaPlayer", error);
          setShouldFallbackToNativeAudio(true);
        }
      });
    } catch (error) {
      const [action, errorToUse] = callOnErrorAndResolve({
        error,
        onError,
        disallowFallback: disallowFallbackToHtml5Audio,
        isClientSideRendering: false,
        clientSideError: error
      });
      if (action === "fail") {
        throw errorToUse;
      }
      Internals10.Log.error({ logLevel, tag: "@remotion/media" }, "[AudioForPreview] MediaPlayer initialization failed", errorToUse);
      setShouldFallbackToNativeAudio(true);
    }
    return () => {
      if (mediaPlayerRef.current) {
        Internals10.Log.trace({ logLevel, tag: "@remotion/media" }, `[AudioForPreview] Disposing MediaPlayer`);
        mediaPlayerRef.current.dispose();
        mediaPlayerRef.current = null;
      }
      setMediaPlayerReady(false);
      setShouldFallbackToNativeAudio(false);
    };
  }, [
    preloadedSrc,
    logLevel,
    sharedAudioContext,
    loop,
    videoConfig.fps,
    audioStreamIndex,
    disallowFallbackToHtml5Audio,
    buffer,
    onError,
    credentials,
    initialRequestInit,
    setMediaDurationInSeconds
  ]);
  if (terminalError) {
    throw terminalError;
  }
  if (shouldFallbackToNativeAudio && !disallowFallbackToHtml5Audio) {
    return /* @__PURE__ */ jsx(RemotionAudio, {
      src,
      muted: effectiveMuted,
      volume,
      startFrom: trimBefore,
      endAt: trimAfter,
      playbackRate,
      loopVolumeCurveBehavior,
      name,
      loop,
      showInTimeline,
      style: style ?? undefined,
      _remotionInternalStack: _remotionInternalStack ?? undefined,
      toneFrequency,
      audioStreamIndex,
      pauseWhenBuffering: fallbackHtml5AudioProps?.pauseWhenBuffering,
      crossOrigin: fallbackHtml5AudioProps?.crossOrigin,
      ...fallbackHtml5AudioProps,
      preservePitch: fallbackHtml5AudioProps?.preservePitch ?? preservePitch ?? true
    });
  }
  return null;
};
var AudioForPreview = ({
  loop = false,
  src,
  logLevel,
  muted,
  name,
  volume,
  loopVolumeCurveBehavior,
  playbackRate = 1,
  trimAfter,
  trimBefore,
  showInTimeline,
  _remotionInternalStack,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  preservePitch,
  audioStreamIndex,
  fallbackHtml5AudioProps,
  onError,
  credentials,
  requestInit,
  setMediaDurationInSeconds,
  style
}) => {
  const preloadedSrc = usePreload(src);
  const defaultLogLevel = Internals10.useLogLevel();
  const frame = useCurrentFrame();
  const videoConfig = useVideoConfig();
  const currentTime = frame / videoConfig.fps;
  const showShow = useMemo(() => {
    return getTimeInSeconds({
      unloopedTimeInSeconds: currentTime,
      playbackRate,
      loop,
      trimBefore,
      trimAfter,
      mediaDurationInSeconds: Infinity,
      fps: videoConfig.fps,
      ifNoMediaDuration: "infinity",
      src
    }) !== null;
  }, [
    currentTime,
    playbackRate,
    src,
    trimAfter,
    trimBefore,
    videoConfig.fps,
    loop
  ]);
  if (!showShow) {
    return null;
  }
  if (!setMediaDurationInSeconds) {
    throw new Error("setMediaDurationInSeconds is required");
  }
  return /* @__PURE__ */ jsx(AudioForPreviewAssertedShowing, {
    audioStreamIndex,
    src: preloadedSrc,
    playbackRate,
    logLevel: logLevel ?? defaultLogLevel,
    muted: muted ?? false,
    volume: volume ?? 1,
    loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
    loop,
    trimAfter,
    trimBefore,
    name,
    showInTimeline: showInTimeline ?? true,
    _remotionInternalStack,
    disallowFallbackToHtml5Audio: disallowFallbackToHtml5Audio ?? false,
    toneFrequency,
    preservePitch,
    onError,
    credentials,
    requestInit,
    fallbackHtml5AudioProps,
    setMediaDurationInSeconds,
    style
  });
};

// src/audio/audio-for-rendering.tsx
import { useContext as useContext3, useLayoutEffect as useLayoutEffect2, useMemo as useMemo2, useState as useState2 } from "react";
import {
  Html5Audio,
  Internals as Internals19,
  random,
  useCurrentFrame as useCurrentFrame2,
  useDelayRender,
  useRemotionEnvironment
} from "remotion";

// src/caches.ts
import React2 from "react";
import { Internals as Internals17 } from "remotion";

// src/audio-extraction/audio-manager.ts
import { Internals as Internals12 } from "remotion";

// src/serialized-queue.ts
var makeSerializedQueue = () => {
  let tail = Promise.resolve(undefined);
  return (fn) => {
    const result = tail.then(() => fn());
    tail = result.then(() => {
      return;
    }, () => {
      return;
    });
    return result;
  };
};

// src/audio-extraction/audio-iterator.ts
import { Internals as Internals11 } from "remotion";

// src/audio-extraction/audio-cache.ts
var BYTES_PER_SAMPLE = {
  u8: 1,
  s16: 2,
  s32: 4,
  f32: 4,
  "u8-planar": 1,
  "s16-planar": 2,
  "s32-planar": 4,
  "f32-planar": 4
};
var getAudioSampleByteSize = (sample) => {
  return sample.numberOfFrames * sample.numberOfChannels * BYTES_PER_SAMPLE[sample.format];
};
var makeAudioCache = () => {
  const timestamps = [];
  const samples = {};
  const addFrame = (sample) => {
    timestamps.push(sample.timestamp);
    samples[sample.timestamp] = sample;
  };
  const clearBeforeThreshold = (threshold) => {
    for (const timestamp of timestamps.slice()) {
      const endTimestamp = timestamp + samples[timestamp].duration;
      if (endTimestamp < threshold) {
        const isLast = timestamp === timestamps[timestamps.length - 1];
        if (isLast) {
          continue;
        }
        samples[timestamp].close();
        delete samples[timestamp];
        timestamps.splice(timestamps.indexOf(timestamp), 1);
      }
    }
  };
  const deleteAll = () => {
    for (const timestamp of timestamps) {
      samples[timestamp].close();
      delete samples[timestamp];
    }
    timestamps.length = 0;
  };
  const getSamples = (timestamp, durationInSeconds) => {
    const selected = [];
    for (let i = 0;i < timestamps.length; i++) {
      const sampleTimestamp = timestamps[i];
      const sample = samples[sampleTimestamp];
      if (sample.timestamp + sample.duration - 0.0000000001 <= timestamp) {
        continue;
      }
      if (sample.timestamp >= timestamp + durationInSeconds - 0.0000000001) {
        break;
      }
      selected.push(sample);
    }
    return selected;
  };
  const getOpenTimestamps = () => {
    return timestamps;
  };
  const getTotalSize = () => {
    let total = 0;
    for (const timestamp of timestamps) {
      total += getAudioSampleByteSize(samples[timestamp]);
    }
    return total;
  };
  const getOldestTimestamp = () => {
    return timestamps[0];
  };
  const getNewestTimestamp = () => {
    if (timestamps.length === 0) {
      return null;
    }
    const sample = samples[timestamps[timestamps.length - 1]];
    return sample.timestamp + sample.duration;
  };
  return {
    addFrame,
    clearBeforeThreshold,
    deleteAll,
    getSamples,
    getOldestTimestamp,
    getNewestTimestamp,
    getOpenTimestamps,
    getTotalSize
  };
};

// src/audio-extraction/audio-iterator.ts
var EXTRA_THRESHOLD_IN_SECONDS = 1.5;
var safetyOutOfOrderThreshold = 0.2;
var warned = {};
var warnAboutMatroskaOnce = (src, logLevel) => {
  if (warned[src]) {
    return;
  }
  warned[src] = true;
  Internals11.Log.warn({ logLevel, tag: "@remotion/media" }, `Audio from ${src} will need to be read from the beginning. https://www.remotion.dev/docs/media/support#matroska-limitation`);
};
var makeAudioIterator2 = ({
  audioSampleSink,
  isMatroska,
  startTimestamp,
  src,
  actualMatroskaTimestamps,
  logLevel
}) => {
  let sampleIterator = audioSampleSink.samples(isMatroska ? 0 : Math.max(0, startTimestamp - EXTRA_THRESHOLD_IN_SECONDS));
  if (isMatroska) {
    warnAboutMatroskaOnce(src, logLevel);
  }
  let fullDuration = null;
  const cache = makeAudioCache();
  let lastUsed = Date.now();
  let deleted = false;
  let decodeFailure = null;
  const getNextSample = async () => {
    lastUsed = Date.now();
    if (decodeFailure) {
      throw decodeFailure.error;
    }
    let retried = false;
    const timestamps = cache.getOpenTimestamps();
    const lastTimestamp = timestamps[timestamps.length - 1];
    try {
      while (true) {
        const { value: sample, done } = await sampleIterator.next().catch(async (error) => {
          if (retried || deleted || !isCodecReclaimedError(error)) {
            throw error;
          }
          retried = true;
          await sampleIterator.return().catch(() => {
            return;
          });
          if (deleted) {
            throw error;
          }
          sampleIterator = audioSampleSink.samples(isMatroska ? 0 : Math.max(0, (cache.getNewestTimestamp() ?? startTimestamp) - EXTRA_THRESHOLD_IN_SECONDS));
          return sampleIterator.next();
        });
        if (done) {
          fullDuration = cache.getNewestTimestamp();
          return null;
        }
        const realTimestamp = actualMatroskaTimestamps.getRealTimestamp(sample.timestamp);
        if (realTimestamp !== null && realTimestamp !== sample.timestamp) {
          sample.setTimestamp(realTimestamp);
        }
        if (retried && lastTimestamp !== undefined && sample.timestamp <= lastTimestamp) {
          sample.close();
          continue;
        }
        actualMatroskaTimestamps.observeTimestamp(sample.timestamp);
        actualMatroskaTimestamps.observeTimestamp(sample.timestamp + sample.duration);
        cache.addFrame(sample);
        return sample;
      }
    } catch (error) {
      decodeFailure = { error };
      await sampleIterator.return().catch(() => {
        return;
      });
      throw error;
    }
  };
  const getSamples = async (timestamp, durationInSeconds) => {
    lastUsed = Date.now();
    if (fullDuration !== null && timestamp > fullDuration) {
      cache.clearBeforeThreshold(fullDuration - safetyOutOfOrderThreshold);
      return [];
    }
    const samples = cache.getSamples(timestamp, durationInSeconds);
    const newestTimestamp = cache.getNewestTimestamp();
    if (newestTimestamp !== null) {
      if (newestTimestamp >= timestamp + durationInSeconds - 0.0000000001) {
        return samples;
      }
    }
    while (true) {
      const sample = await getNextSample();
      const deleteBefore = fullDuration === null ? timestamp : Math.min(timestamp, fullDuration);
      cache.clearBeforeThreshold(deleteBefore - safetyOutOfOrderThreshold);
      if (sample === null) {
        break;
      }
      if (sample.timestamp + sample.duration - 0.0000000001 <= timestamp) {
        continue;
      }
      if (sample.timestamp >= timestamp + durationInSeconds - 0.0000000001) {
        break;
      }
      samples.push(sample);
    }
    return samples;
  };
  const logOpenFrames = () => {
    const openTimestamps = cache.getOpenTimestamps();
    if (openTimestamps.length > 0) {
      const first = openTimestamps[0];
      const last = openTimestamps[openTimestamps.length - 1];
      Internals11.Log.verbose({ logLevel, tag: "@remotion/media" }, "Open audio samples for src", src, `${first.toFixed(3)}...${last.toFixed(3)}`);
    }
  };
  const getCacheStats = () => {
    return {
      count: cache.getOpenTimestamps().length,
      size: cache.getTotalSize()
    };
  };
  const canSatisfyRequestedTime = (timestamp) => {
    const oldestTimestamp = cache.getOldestTimestamp() ?? startTimestamp;
    if (fullDuration !== null && timestamp > fullDuration) {
      return true;
    }
    return oldestTimestamp < timestamp && Math.abs(oldestTimestamp - timestamp) < 10;
  };
  const prepareForDeletion = () => {
    deleted = true;
    cache.deleteAll();
    sampleIterator.return().then((value) => {
      if (value.value) {
        value.value.close();
      }
    }).catch(() => {
      return;
    });
    fullDuration = null;
  };
  let op = Promise.resolve([]);
  return {
    src,
    getSamples: (ts, dur) => {
      op = op.then(() => getSamples(ts, dur));
      return op;
    },
    waitForCompletion: async () => {
      await op;
      return true;
    },
    canSatisfyRequestedTime,
    logOpenFrames,
    getCacheStats,
    getLastUsed: () => lastUsed,
    prepareForDeletion,
    startTimestamp,
    clearBeforeThreshold: cache.clearBeforeThreshold,
    getOldestTimestamp: cache.getOldestTimestamp,
    getNewestTimestamp: cache.getNewestTimestamp
  };
};

// src/audio-extraction/audio-manager.ts
var makeAudioManager = ({
  getTotalCacheStats
}) => {
  const iterators = [];
  let disposed = false;
  const makeIterator = ({
    timeInSeconds,
    src,
    audioSampleSink,
    isMatroska,
    actualMatroskaTimestamps,
    logLevel
  }) => {
    const iterator = makeAudioIterator2({
      audioSampleSink,
      isMatroska,
      startTimestamp: timeInSeconds,
      src,
      actualMatroskaTimestamps,
      logLevel
    });
    iterators.push(iterator);
    return iterator;
  };
  const getIteratorMostInThePast = () => {
    let mostInThePast = null;
    let mostInThePastIterator = null;
    for (const iterator of iterators) {
      const lastUsed = iterator.getLastUsed();
      if (mostInThePast === null || lastUsed < mostInThePast) {
        mostInThePast = lastUsed;
        mostInThePastIterator = iterator;
      }
    }
    return mostInThePastIterator;
  };
  const deleteOldestIterator = () => {
    const iterator = getIteratorMostInThePast();
    if (iterator) {
      iterator.prepareForDeletion();
      iterators.splice(iterators.indexOf(iterator), 1);
    }
  };
  const deleteDuplicateIterators = (logLevel) => {
    const seenKeys = new Set;
    for (let i = 0;i < iterators.length; i++) {
      const iterator = iterators[i];
      const key = `${iterator.src}-${iterator.getOldestTimestamp()}-${iterator.getNewestTimestamp()}`;
      if (seenKeys.has(key)) {
        iterator.prepareForDeletion();
        iterators.splice(iterators.indexOf(iterator), 1);
        Internals12.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted duplicate iterator for ${iterator.src}`);
      }
      seenKeys.add(key);
    }
  };
  const getIterator = async ({
    src,
    timeInSeconds,
    audioSampleSink,
    isMatroska,
    actualMatroskaTimestamps,
    logLevel,
    maxCacheSize
  }) => {
    if (disposed) {
      throw new Error("Media cache has already been disposed");
    }
    let attempts = 0;
    const maxAttempts = 3;
    while ((await getTotalCacheStats()).totalSize > maxCacheSize && attempts < maxAttempts) {
      deleteOldestIterator();
      attempts++;
    }
    if ((await getTotalCacheStats()).totalSize > maxCacheSize && attempts >= maxAttempts) {
      Internals12.Log.warn({ logLevel, tag: "@remotion/media" }, `Audio cache: Exceeded max cache size after ${maxAttempts} attempts. Still ${(await getTotalCacheStats()).totalSize} bytes used, target was ${maxCacheSize} bytes.`);
    }
    for (const iterator of iterators) {
      if (iterator.src === src && await iterator.waitForCompletion() && iterator.canSatisfyRequestedTime(timeInSeconds)) {
        return iterator;
      }
    }
    for (let i = 0;i < iterators.length; i++) {
      const iterator = iterators[i];
      if (iterator.src === src && iterator.startTimestamp === timeInSeconds) {
        iterator.prepareForDeletion();
        iterators.splice(iterators.indexOf(iterator), 1);
      }
    }
    deleteDuplicateIterators(logLevel);
    if (disposed) {
      throw new Error("Media cache has already been disposed");
    }
    return makeIterator({
      src,
      timeInSeconds,
      audioSampleSink,
      isMatroska,
      actualMatroskaTimestamps,
      logLevel
    });
  };
  const getCacheStats = () => {
    let totalCount = 0;
    let totalSize = 0;
    for (const iterator of iterators) {
      const { count, size } = iterator.getCacheStats();
      totalCount += count;
      totalSize += size;
    }
    return { count: totalCount, totalSize };
  };
  const logOpenFrames = () => {
    for (const iterator of iterators) {
      iterator.logOpenFrames();
    }
  };
  const clearAll = () => {
    for (const iterator of iterators) {
      iterator.prepareForDeletion();
    }
    iterators.length = 0;
  };
  const dispose = () => {
    if (disposed) {
      return;
    }
    disposed = true;
    clearAll();
  };
  const enqueue = makeSerializedQueue();
  return {
    getIterator: ({
      src,
      timeInSeconds,
      audioSampleSink,
      isMatroska,
      actualMatroskaTimestamps,
      logLevel,
      maxCacheSize
    }) => {
      return enqueue(() => getIterator({
        src,
        timeInSeconds,
        audioSampleSink,
        isMatroska,
        actualMatroskaTimestamps,
        logLevel,
        maxCacheSize
      }));
    },
    getCacheStats,
    getIteratorMostInThePast,
    logOpenFrames,
    deleteDuplicateIterators,
    clearAll,
    dispose
  };
};

// src/get-sink.ts
import { Internals as Internals14 } from "remotion";

// src/video-extraction/get-frames-since-keyframe.ts
import {
  ALL_FORMATS as ALL_FORMATS2,
  AudioSampleSink,
  EncodedPacketSink,
  Input as Input2,
  MATROSKA,
  UrlSource as UrlSource2,
  VideoSampleSink as VideoSampleSink2,
  WEBM
} from "mediabunny";
import { Internals as Internals13 } from "remotion";

// src/browser-can-use-webgl2.ts
var browserCanUseWebGl2 = null;
var browserCanUseWebGl2Uncached = () => {
  const canvas = new OffscreenCanvas(1, 1);
  const context = canvas.getContext("webgl2");
  return context !== null;
};
var canBrowserUseWebGl2 = () => {
  if (browserCanUseWebGl2 !== null) {
    return browserCanUseWebGl2;
  }
  browserCanUseWebGl2 = browserCanUseWebGl2Uncached();
  return browserCanUseWebGl2;
};

// src/video-extraction/remember-actual-matroska-timestamps.ts
var rememberActualMatroskaTimestamps = (isMatroska) => {
  const observations = [];
  const observeTimestamp = (startTime) => {
    if (!isMatroska) {
      return;
    }
    observations.push(startTime);
  };
  const getRealTimestamp = (observedTimestamp) => {
    if (!isMatroska) {
      return observedTimestamp;
    }
    return observations.find((observation) => Math.abs(observedTimestamp - observation) < 0.001) ?? null;
  };
  return {
    observeTimestamp,
    getRealTimestamp
  };
};

// src/video-extraction/get-frames-since-keyframe.ts
var makeSinks = (src, logLevel, credentials, requestInit) => {
  const resolvedRequestInit = resolveRequestInit({ credentials, requestInit });
  const input = new Input2({
    formats: ALL_FORMATS2,
    source: new UrlSource2(src, {
      handleUnhandledError: (error) => {
        Internals13.Log.warn({ logLevel, tag: "@remotion/media" }, `A speculative fetch for "${src}" failed:`, error);
      },
      maxCacheSize: getMaxSourceCacheSize(logLevel),
      ...resolvedRequestInit ? { requestInit: resolvedRequestInit } : undefined
    })
  });
  const getSinks = async () => {
    let format = null;
    let formatDetectionError = null;
    try {
      format = await input.getFormat();
    } catch (error) {
      formatDetectionError = error;
    }
    const isNetworkFailure = formatDetectionError !== null && isNetworkError(formatDetectionError);
    const isMatroska = format === MATROSKA || format === WEBM;
    const getVideoSinks = async () => {
      if (isNetworkFailure) {
        return "network-error";
      }
      if (format === null) {
        return "unknown-container-format";
      }
      const videoTrack = await input.getPrimaryVideoTrack();
      if (!videoTrack) {
        return "no-video-track";
      }
      if (await videoTrack.isLive()) {
        throw new Error("Live streams are not currently supported by Remotion. Sorry! Source: " + src);
      }
      if (await videoTrack.isRelativeToUnixEpoch()) {
        throw new Error("Streams with UNIX timestamps are not currently supported by Remotion. Sorry! Source: " + src);
      }
      const canDecode = await videoTrack.canDecode();
      if (!canDecode) {
        if (videoTrack.codec === "prores") {
          return "cannot-decode-prores";
        }
        return "cannot-decode";
      }
      const sampleSink = new VideoSampleSink2(videoTrack);
      const packetSink = new EncodedPacketSink(videoTrack);
      const startPacket = await packetSink.getFirstPacket({
        verifyKeyPackets: true
      });
      const hasAlpha = startPacket?.sideData.alpha;
      if (hasAlpha && !canBrowserUseWebGl2()) {
        Internals13.Log.warn({ logLevel, tag: "@remotion/media" }, `WebGL2 is not available, using the non-fast CPU path to decode alpha for ${src}.`);
      }
      return {
        sampleSink,
        timeResolution: await videoTrack.getTimeResolution()
      };
    };
    let videoSinksPromise = null;
    const getVideoSinksPromise = () => {
      if (videoSinksPromise) {
        return videoSinksPromise;
      }
      videoSinksPromise = getVideoSinks();
      return videoSinksPromise;
    };
    const audioSinksPromise = {};
    const getAudioSinks = async (index) => {
      if (isNetworkFailure) {
        return "network-error";
      }
      if (format === null) {
        return "unknown-container-format";
      }
      const [videoTrack, audioTracks] = await Promise.all([
        input.getPrimaryVideoTrack(),
        input.getAudioTracks()
      ]);
      const audioTrack = await resolveAudioTrack({
        videoTrack,
        audioTracks,
        audioStreamIndex: index
      });
      if (!audioTrack) {
        return "no-audio-track";
      }
      const canDecode = await audioTrack.canDecode();
      if (!canDecode) {
        return "cannot-decode-audio";
      }
      return {
        sampleSink: new AudioSampleSink(audioTrack),
        sampleRate: await audioTrack.getSampleRate(),
        numberOfChannels: await audioTrack.getNumberOfChannels()
      };
    };
    const getAudioSinksPromise = (index) => {
      const keyIndex = index === null ? -1 : index;
      if (audioSinksPromise[keyIndex]) {
        return audioSinksPromise[keyIndex];
      }
      audioSinksPromise[keyIndex] = getAudioSinks(index);
      return audioSinksPromise[keyIndex];
    };
    return {
      formatDetectionError,
      getVideo: () => getVideoSinksPromise(),
      getAudio: (index) => getAudioSinksPromise(index),
      actualMatroskaTimestamps: rememberActualMatroskaTimestamps(isMatroska),
      isMatroska,
      getDuration: () => {
        return getDurationOrCompute(input);
      }
    };
  };
  return {
    promise: getSinks(),
    dispose: () => input.dispose()
  };
};

// src/get-sink.ts
var getSinkCacheKey = ({
  src,
  credentials,
  requestInit
}) => JSON.stringify([
  src,
  credentials,
  getMediaRequestInitFingerprint(requestInit)
]);
var makeSinkManager = () => {
  const sinkPromises = {};
  const inputDisposers = {};
  let disposed = false;
  return {
    getSink: (src, logLevel, credentials, requestInit) => {
      if (disposed) {
        return Promise.reject(new Error("Media cache has already been disposed"));
      }
      const normalizedRequestInit = normalizeMediaRequestInit(requestInit);
      const cacheKey = getSinkCacheKey({
        src,
        credentials,
        requestInit: normalizedRequestInit
      });
      let promise = sinkPromises[cacheKey];
      if (!promise) {
        Internals14.Log.verbose({
          logLevel,
          tag: "@remotion/media"
        }, `Sink for ${src} was not found, creating new sink`);
        const sinks = makeSinks(src, logLevel, credentials, normalizedRequestInit);
        promise = sinks.promise;
        sinkPromises[cacheKey] = promise;
        inputDisposers[cacheKey] = sinks.dispose;
      }
      return promise;
    },
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      let firstError = null;
      for (const cacheKey of Object.keys(inputDisposers)) {
        try {
          inputDisposers[cacheKey]();
        } catch (error) {
          firstError ??= error;
        } finally {
          delete inputDisposers[cacheKey];
          delete sinkPromises[cacheKey];
        }
      }
      if (firstError !== null) {
        throw firstError;
      }
    }
  };
};

// src/video-extraction/keyframe-manager.ts
import { Internals as Internals16 } from "remotion";

// src/render-timestamp-range.ts
var renderTimestampRange = (timestamps) => {
  if (timestamps.length === 0) {
    return "(none)";
  }
  if (timestamps.length === 1) {
    return timestamps[0].toFixed(3);
  }
  return `${timestamps[0].toFixed(3)}...${timestamps[timestamps.length - 1].toFixed(3)}`;
};

// src/video-extraction/keyframe-bank.ts
import { Internals as Internals15 } from "remotion";

// src/video-extraction/get-allocation-size.ts
var BYTES_PER_PIXEL_FOR_OPAQUE_FRAME = 3;
var getAllocationSize = (sample) => {
  if (sample.format === null) {
    return sample.codedHeight * sample.codedWidth * BYTES_PER_PIXEL_FOR_OPAQUE_FRAME;
  }
  return sample.allocationSize();
};

// src/video-extraction/keyframe-bank.ts
var BIGGEST_ALLOWED_JUMP_FORWARD_SECONDS = 3;
var makeKeyframeBank = async ({
  logLevel: parentLogLevel,
  src,
  videoSampleSink,
  timeResolution,
  initialTimestampRequest
}) => {
  let sampleIterator = videoSampleSink.samples(Math.round(initialTimestampRequest * timeResolution) / timeResolution);
  const frames = {};
  const frameTimestamps = [];
  let hasReachedEndOfVideo = false;
  let lastUsed = Date.now();
  let allocationSize = 0;
  let pendingOperations = 0;
  let deleted = false;
  let decodeFailure = null;
  const readNextSample = async () => {
    if (decodeFailure) {
      throw decodeFailure.error;
    }
    try {
      return await sampleIterator.next();
    } catch (error) {
      if (!isCodecReclaimedError(error) || deleted) {
        decodeFailure = { error };
        await sampleIterator.return().catch(() => {
          return;
        });
        throw error;
      }
      await sampleIterator.return().catch(() => {
        return;
      });
      const lastTimestamp = frameTimestamps[frameTimestamps.length - 1];
      try {
        if (deleted) {
          throw error;
        }
        sampleIterator = videoSampleSink.samples(lastTimestamp ?? Math.round(initialTimestampRequest * timeResolution) / timeResolution);
        let sample = await sampleIterator.next();
        if (lastTimestamp !== undefined) {
          while (sample.value && sample.value.timestamp <= lastTimestamp) {
            sample.value.close();
            sample = await sampleIterator.next();
          }
        }
        return sample;
      } catch (retryError) {
        decodeFailure = { error: retryError };
        await sampleIterator.return().catch(() => {
          return;
        });
        throw retryError;
      }
    }
  };
  const getMeasuredDurationOfFrame = (timestamp) => {
    const index = frameTimestamps.indexOf(timestamp);
    if (index === -1) {
      throw new Error(`Frame ${timestamp} not found`);
    }
    const nextTimestamp = frameTimestamps[index + 1];
    if (nextTimestamp === undefined) {
      return null;
    }
    return nextTimestamp - timestamp;
  };
  const getKnownDurationOfFrame = (timestamp) => {
    const measuredDuration = getMeasuredDurationOfFrame(timestamp);
    if (measuredDuration !== null) {
      return measuredDuration;
    }
    if (!hasReachedEndOfVideo) {
      return null;
    }
    return frames[timestamp].duration ?? 0;
  };
  const getEstimatedDurationOfFrame = (timestamp) => {
    return getMeasuredDurationOfFrame(timestamp) ?? frames[timestamp].duration ?? 0;
  };
  const deleteFrameAtTimestamp = (timestamp) => {
    allocationSize -= getAllocationSize(frames[timestamp]);
    frameTimestamps.splice(frameTimestamps.indexOf(timestamp), 1);
    frames[timestamp].close();
    delete frames[timestamp];
  };
  const deleteFramesBeforeTimestamp = ({
    logLevel,
    timestampInSeconds
  }) => {
    const deletedTimestamps = [];
    for (const frameTimestamp of frameTimestamps.slice()) {
      if (hasReachedEndOfVideo) {
        const isLast = frameTimestamp === frameTimestamps[frameTimestamps.length - 1];
        if (isLast) {
          continue;
        }
      }
      if (!frames[frameTimestamp]) {
        continue;
      }
      const duration = getKnownDurationOfFrame(frameTimestamp);
      if (duration === null) {
        continue;
      }
      if (frameTimestamp + duration < timestampInSeconds) {
        deleteFrameAtTimestamp(frameTimestamp);
        deletedTimestamps.push(frameTimestamp);
      }
    }
    if (deletedTimestamps.length > 0) {
      Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted ${deletedTimestamps.length} frame${deletedTimestamps.length === 1 ? "" : "s"} ${renderTimestampRange(deletedTimestamps)} for src ${src} because it is lower than ${timestampInSeconds}. Remaining: ${renderTimestampRange(frameTimestamps)}`);
    }
  };
  const hasDecodedEnoughForTimestamp = (timestamp) => {
    const lastFrameTimestamp = frameTimestamps[frameTimestamps.length - 1];
    if (lastFrameTimestamp === undefined) {
      return false;
    }
    const lastFrame = frames[lastFrameTimestamp];
    if (!lastFrame) {
      return true;
    }
    if (roundTo4Digits(lastFrameTimestamp) >= roundTo4Digits(timestamp)) {
      return true;
    }
    const duration = getKnownDurationOfFrame(lastFrameTimestamp);
    if (duration === null) {
      return false;
    }
    return roundTo4Digits(lastFrameTimestamp + duration) > roundTo4Digits(timestamp);
  };
  const addFrame = (frame, logLevel) => {
    if (frames[frame.timestamp]) {
      deleteFrameAtTimestamp(frame.timestamp);
    }
    frames[frame.timestamp] = frame;
    frameTimestamps.push(frame.timestamp);
    allocationSize += getAllocationSize(frame);
    while (frameTimestamps.length > 2) {
      deleteFrameAtTimestamp(frameTimestamps[0]);
    }
    lastUsed = Date.now();
    Internals15.Log.trace({ logLevel, tag: "@remotion/media" }, `Added frame at ${frame.timestamp}sec to bank`);
  };
  const ensureEnoughFramesForTimestamp = async (timestampInSeconds, logLevel, fps) => {
    while (!hasDecodedEnoughForTimestamp(timestampInSeconds)) {
      deleteFramesBeforeTimestamp({
        logLevel: parentLogLevel,
        timestampInSeconds: timestampInSeconds - getSafeWindowOfMonotonicity(fps)
      });
      const sample = await readNextSample();
      if (sample.value) {
        addFrame(sample.value, logLevel);
      }
      if (sample.done) {
        hasReachedEndOfVideo = true;
        break;
      }
      deleteFramesBeforeTimestamp({
        logLevel: parentLogLevel,
        timestampInSeconds: timestampInSeconds - getSafeWindowOfMonotonicity(fps)
      });
    }
    lastUsed = Date.now();
  };
  const getFrameFromTimestamp = async (timestampInSeconds, fps) => {
    lastUsed = Date.now();
    let adjustedTimestamp = timestampInSeconds;
    if (hasReachedEndOfVideo && roundTo4Digits(adjustedTimestamp) > roundTo4Digits(frameTimestamps[frameTimestamps.length - 1])) {
      adjustedTimestamp = frameTimestamps[frameTimestamps.length - 1];
    }
    await ensureEnoughFramesForTimestamp(adjustedTimestamp, parentLogLevel, fps);
    for (let i = frameTimestamps.length - 1;i >= 0; i--) {
      const sample = frames[frameTimestamps[i]];
      if (!sample) {
        return null;
      }
      if (hasFrameStarted(sample.timestamp, adjustedTimestamp)) {
        return sample;
      }
    }
    return frames[frameTimestamps[0]] ?? null;
  };
  const hasTimestampInSecond = async (timestamp, fps) => {
    return await getFrameFromTimestamp(timestamp, fps) !== null;
  };
  const getOpenFrameCount = () => {
    return {
      size: allocationSize,
      timestamps: frameTimestamps
    };
  };
  const getLastUsed = () => {
    return lastUsed;
  };
  const enqueue = makeSerializedQueue();
  const firstFrame = await readNextSample();
  if (!firstFrame.value) {
    throw new Error("No first frame found");
  }
  const startTimestampInSeconds = firstFrame.value.timestamp;
  Internals15.Log.verbose({ logLevel: parentLogLevel, tag: "@remotion/media" }, `Creating keyframe bank from ${startTimestampInSeconds}sec`);
  addFrame(firstFrame.value, parentLogLevel);
  const getRangeOfTimestamps = () => {
    if (frameTimestamps.length === 0) {
      return null;
    }
    const firstTimestamp = frameTimestamps[0];
    const lastTimestamp = frameTimestamps[frameTimestamps.length - 1];
    const lastFrameDuration = getEstimatedDurationOfFrame(lastTimestamp);
    return {
      firstTimestamp,
      lastTimestamp: lastTimestamp + lastFrameDuration
    };
  };
  const prepareForDeletion = (logLevel, reason) => {
    deleted = true;
    const range = getRangeOfTimestamps();
    if (range) {
      Internals15.Log.verbose({ logLevel, tag: "@remotion/media" }, `Preparing for deletion (${reason}) of keyframe bank from ${range?.firstTimestamp}sec to ${range?.lastTimestamp}sec`);
    }
    let framesDeleted = 0;
    for (const frameTimestamp of frameTimestamps.slice()) {
      if (!frames[frameTimestamp]) {
        continue;
      }
      deleteFrameAtTimestamp(frameTimestamp);
      framesDeleted++;
    }
    sampleIterator.return().catch(() => {
      return;
    });
    frameTimestamps.length = 0;
    return { framesDeleted };
  };
  const canSatisfyTimestamp = (timestamp) => {
    if (frameTimestamps.length === 0) {
      return false;
    }
    const roundedTimestamp = roundTo4Digits(timestamp);
    const firstFrameTimestamp = roundTo4Digits(frameTimestamps[0]);
    const range = getRangeOfTimestamps();
    if (!range) {
      return false;
    }
    const lastFrameTimestamp = roundTo4Digits(frameTimestamps[frameTimestamps.length - 1]);
    const lastFrameEndTimestamp = roundTo4Digits(range.lastTimestamp);
    if (hasReachedEndOfVideo && roundedTimestamp > lastFrameTimestamp) {
      return true;
    }
    if (roundedTimestamp < firstFrameTimestamp) {
      const firstFrameIsInitialFrame = firstFrameTimestamp === startTimestampInSeconds;
      const firstFrameDoesSatisfy = firstFrameIsInitialFrame && roundedTimestamp >= initialTimestampRequest;
      return firstFrameDoesSatisfy;
    }
    if (roundedTimestamp - BIGGEST_ALLOWED_JUMP_FORWARD_SECONDS > lastFrameEndTimestamp) {
      return false;
    }
    return true;
  };
  const keyframeBank = {
    getFrameFromTimestamp: (timestamp, fps) => {
      pendingOperations++;
      return enqueue(() => getFrameFromTimestamp(timestamp, fps)).finally(() => {
        pendingOperations--;
      });
    },
    prepareForDeletion,
    hasTimestampInSecond: (timestamp, fps) => {
      pendingOperations++;
      return enqueue(() => hasTimestampInSecond(timestamp, fps)).finally(() => {
        pendingOperations--;
      });
    },
    addFrame,
    deleteFramesBeforeTimestamp,
    src,
    getOpenFrameCount,
    getLastUsed,
    isBusy: () => pendingOperations > 0,
    canSatisfyTimestamp,
    getRangeOfTimestamps
  };
  return keyframeBank;
};

// src/video-extraction/keyframe-manager.ts
var RECENTLY_USED_REQUEST_COUNT = 50;
var makeKeyframeManager = ({
  getTotalCacheStats
}) => {
  let sources = {};
  let disposed = false;
  let requestCountForSrc = {};
  const lastRequestForBank = new WeakMap;
  const addKeyframeBank = ({
    src,
    bank,
    logLevel
  }) => {
    if (disposed) {
      bank.prepareForDeletion(logLevel, "media cache was disposed");
      return false;
    }
    sources[src] = sources[src] ?? [];
    sources[src].push(bank);
    return true;
  };
  const logCacheStats = (logLevel) => {
    let count = 0;
    let totalSize = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        const { size, timestamps } = bank.getOpenFrameCount();
        count += timestamps.length;
        totalSize += size;
        if (size === 0) {
          continue;
        }
        Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, `Open frames for src ${src}: ${renderTimestampRange(timestamps)}`);
      }
    }
    Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, `Video cache stats: ${count} open frames, ${totalSize} bytes`);
  };
  const getCacheStats = () => {
    let count = 0;
    let totalSize = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        const { timestamps, size } = bank.getOpenFrameCount();
        count += timestamps.length;
        totalSize += size;
        if (size === 0) {
          continue;
        }
      }
    }
    return { count, totalSize };
  };
  const getTheKeyframeBankMostInThePast = () => {
    let mostInThePast = null;
    let mostInThePastBank = null;
    let numberOfBanks = 0;
    for (const src in sources) {
      for (const bank of sources[src]) {
        numberOfBanks++;
        if (bank.isBusy()) {
          continue;
        }
        const index = sources[src].indexOf(bank);
        const lastUsed = bank.getLastUsed();
        if (mostInThePast === null || lastUsed < mostInThePast) {
          mostInThePast = lastUsed;
          mostInThePastBank = { src, bank, index };
        }
      }
    }
    if (!mostInThePastBank) {
      return { mostInThePastBank: null, numberOfBanks };
    }
    return { mostInThePastBank, numberOfBanks };
  };
  const deleteOldestKeyframeBank = (logLevel) => {
    const { mostInThePastBank, numberOfBanks } = getTheKeyframeBankMostInThePast();
    if (numberOfBanks < 2 || mostInThePastBank === null) {
      return { finish: true };
    }
    const { bank, src, index } = mostInThePastBank;
    const range = bank.getRangeOfTimestamps();
    const { framesDeleted } = bank.prepareForDeletion(logLevel, "deleted oldest keyframe bank to stay under max cache size");
    sources[src].splice(index, 1);
    if (range) {
      Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, `Deleted ${framesDeleted} frames for src ${src} from ${range?.firstTimestamp}sec to ${range?.lastTimestamp}sec to free up memory.`);
    }
    return { finish: false };
  };
  const ensureToStayUnderMaxCacheSize = (logLevel, maxCacheSize) => {
    let cacheStats = getTotalCacheStats();
    let attempts = 0;
    const maxAttempts = 3;
    while (cacheStats.totalSize > maxCacheSize && attempts < maxAttempts) {
      const { finish } = deleteOldestKeyframeBank(logLevel);
      if (finish) {
        break;
      }
      Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, "Deleted oldest keyframe bank to stay under max cache size", (cacheStats.totalSize / 1024 / 1024).toFixed(1), "out of", (maxCacheSize / 1024 / 1024).toFixed(1));
      cacheStats = getTotalCacheStats();
      attempts++;
    }
    if (cacheStats.totalSize > maxCacheSize && attempts >= maxAttempts) {
      Internals16.Log.warn({ logLevel, tag: "@remotion/media" }, `Exceeded max cache size after ${maxAttempts} attempts. Remaining cache size: ${(cacheStats.totalSize / 1024 / 1024).toFixed(1)} MB, target was ${(maxCacheSize / 1024 / 1024).toFixed(1)} MB.`);
    }
  };
  const clearKeyframeBanksBeforeTime = ({
    timestampInSeconds,
    src,
    logLevel,
    fps
  }) => {
    const threshold = timestampInSeconds - getSafeWindowOfMonotonicity(fps);
    if (!sources[src]) {
      return;
    }
    const banks = sources[src];
    const currentRequest = requestCountForSrc[src] ?? 0;
    for (const bank of banks) {
      if (bank.isBusy()) {
        continue;
      }
      const range = bank.getRangeOfTimestamps();
      if (!range) {
        continue;
      }
      const lastRequest = lastRequestForBank.get(bank);
      if (lastRequest !== undefined && currentRequest - lastRequest < RECENTLY_USED_REQUEST_COUNT) {
        continue;
      }
      if (range.lastTimestamp < threshold) {
        bank.prepareForDeletion(logLevel, "cleared before threshold " + threshold);
        Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, `[Video] Cleared frames for src ${src} from ${range.firstTimestamp}sec to ${range.lastTimestamp}sec`);
        const bankIndex = banks.indexOf(bank);
        delete sources[src][bankIndex];
      } else {
        bank.deleteFramesBeforeTimestamp({
          timestampInSeconds: threshold,
          logLevel
        });
      }
    }
    sources[src] = sources[src].filter((bank) => bank !== undefined);
    logCacheStats(logLevel);
  };
  const getKeyframeBankOrRefetch = async ({
    timestamp,
    videoSampleSink,
    timeResolution,
    src,
    logLevel
  }) => {
    const existingBanks = sources[src] ?? [];
    const existingBank = existingBanks?.find((bank) => bank.canSatisfyTimestamp(timestamp));
    if (!existingBank) {
      Internals16.Log.trace({ logLevel, tag: "@remotion/media" }, `Creating new keyframe bank for src ${src} at timestamp ${timestamp}`);
      const newKeyframeBank = await makeKeyframeBank({
        videoSampleSink,
        timeResolution,
        logLevel,
        src,
        initialTimestampRequest: timestamp
      });
      if (!addKeyframeBank({ src, bank: newKeyframeBank, logLevel })) {
        return null;
      }
      return newKeyframeBank;
    }
    if (existingBank.canSatisfyTimestamp(timestamp)) {
      Internals16.Log.trace({ logLevel, tag: "@remotion/media" }, `Keyframe bank exists and satisfies timestamp ${timestamp}`);
      return existingBank;
    }
    Internals16.Log.verbose({ logLevel, tag: "@remotion/media" }, `Keyframe bank exists but frame at time ${timestamp} does not exist anymore.`);
    existingBank.prepareForDeletion(logLevel, "already existed but evicted");
    sources[src] = sources[src].filter((bank) => bank !== existingBank);
    const replacementKeybank = await makeKeyframeBank({
      videoSampleSink,
      timeResolution,
      initialTimestampRequest: timestamp,
      logLevel,
      src
    });
    if (!addKeyframeBank({ src, bank: replacementKeybank, logLevel })) {
      return null;
    }
    return replacementKeybank;
  };
  const requestKeyframeBank = async ({
    timestamp,
    videoSampleSink,
    timeResolution,
    src,
    logLevel,
    maxCacheSize,
    fps
  }) => {
    if (disposed) {
      return null;
    }
    requestCountForSrc[src] = (requestCountForSrc[src] ?? 0) + 1;
    ensureToStayUnderMaxCacheSize(logLevel, maxCacheSize);
    clearKeyframeBanksBeforeTime({
      timestampInSeconds: timestamp,
      src,
      logLevel,
      fps
    });
    const keyframeBank = await getKeyframeBankOrRefetch({
      timestamp,
      videoSampleSink,
      timeResolution,
      src,
      logLevel
    });
    if (keyframeBank) {
      lastRequestForBank.set(keyframeBank, requestCountForSrc[src]);
    }
    return keyframeBank;
  };
  const clearAll = (logLevel) => {
    const srcs = Object.keys(sources);
    for (const src of srcs) {
      const banks = sources[src];
      for (const bank of banks) {
        bank.prepareForDeletion(logLevel, "clearAll");
      }
      sources[src] = [];
    }
    sources = {};
    requestCountForSrc = {};
  };
  const dispose = (logLevel) => {
    if (disposed) {
      return;
    }
    disposed = true;
    clearAll(logLevel);
  };
  const enqueue = makeSerializedQueue();
  return {
    requestKeyframeBank: ({
      timestamp,
      videoSampleSink,
      timeResolution,
      src,
      logLevel,
      maxCacheSize,
      fps
    }) => {
      return enqueue(() => requestKeyframeBank({
        timestamp,
        videoSampleSink,
        timeResolution,
        src,
        logLevel,
        maxCacheSize,
        fps
      }));
    },
    getCacheStats,
    clearAll,
    dispose
  };
};

// src/caches.ts
var getSafeWindowOfMonotonicity = (fps) => 0.2 * 30 / fps;
var makeMediaCache = () => {
  const sinkManager = makeSinkManager();
  const managerInstances = {
    keyframe: null,
    audio: null
  };
  const getCacheStats = () => {
    const { keyframe: currentKeyframeManager, audio: currentAudioManager } = managerInstances;
    if (currentKeyframeManager === null || currentAudioManager === null) {
      throw new Error("Media cache managers have not been initialized");
    }
    const keyframeManagerCacheStats = currentKeyframeManager.getCacheStats();
    const audioManagerCacheStats = currentAudioManager.getCacheStats();
    return {
      count: keyframeManagerCacheStats.count + audioManagerCacheStats.count,
      totalSize: keyframeManagerCacheStats.totalSize + audioManagerCacheStats.totalSize
    };
  };
  const keyframeManagerInstance = makeKeyframeManager({
    getTotalCacheStats: getCacheStats
  });
  const audioManagerInstance = makeAudioManager({
    getTotalCacheStats: getCacheStats
  });
  managerInstances.keyframe = keyframeManagerInstance;
  managerInstances.audio = audioManagerInstance;
  const queueFrameExtraction = makeSerializedQueue();
  const queueAudioExtraction = makeSerializedQueue();
  let disposed = false;
  return {
    sinkManager,
    keyframeManager: keyframeManagerInstance,
    audioManager: audioManagerInstance,
    getTotalCacheStats: getCacheStats,
    isDisposed: () => disposed,
    queueFrameExtraction,
    queueAudioExtraction,
    dispose: (logLevel) => {
      if (disposed) {
        return;
      }
      disposed = true;
      try {
        keyframeManagerInstance.dispose(logLevel);
      } finally {
        try {
          audioManagerInstance.dispose();
        } finally {
          sinkManager.dispose();
        }
      }
    }
  };
};
var globalMediaCache = makeMediaCache();
var { keyframeManager, audioManager, getTotalCacheStats } = globalMediaCache;
var useRenderMediaCache = (logLevel) => {
  const renderResourceManager = React2.useContext(Internals17.RenderResourceManagerContext);
  if (renderResourceManager === null) {
    return globalMediaCache;
  }
  return renderResourceManager.getOrCreateResource({
    key: "@remotion/media/cache",
    create: () => {
      const resource = makeMediaCache();
      return {
        resource,
        dispose: () => resource.dispose(logLevel)
      };
    }
  });
};
var useMaxMediaCacheSize = (logLevel) => {
  const context = React2.useContext(Internals17.MaxMediaCacheSizeContext);
  if (context === null) {
    return getMaxVideoCacheSize(logLevel);
  }
  return context;
};

// src/convert-audiodata/apply-volume.ts
var applyVolume = (array, volume) => {
  if (volume === 1) {
    return;
  }
  for (let i = 0;i < array.length; i++) {
    const newValue = array[i] * volume;
    if (newValue < -32768) {
      array[i] = -32768;
    } else if (newValue > 32767) {
      array[i] = 32767;
    } else {
      array[i] = newValue;
    }
  }
};

// src/convert-audiodata/resample-audiodata.ts
var TARGET_NUMBER_OF_CHANNELS = 2;
var getTargetSampleRate = () => {
  if (typeof window !== "undefined" && window.remotion_sampleRate) {
    return window.remotion_sampleRate;
  }
  return 48000;
};
var fixFloatingPoint = (value) => {
  if (value % 1 < 0.0000001) {
    return Math.floor(value);
  }
  if (value % 1 > 0.9999999) {
    return Math.ceil(value);
  }
  return value;
};
var clampToS16 = (value) => {
  return Math.max(-32768, Math.min(32767, value));
};
var resampleAudioData = ({
  srcNumberOfChannels,
  sourceChannels,
  destination,
  targetFrames,
  chunkSize
}) => {
  const getSourceValues = (startUnfixed, endUnfixed, channelIndex) => {
    const start = fixFloatingPoint(startUnfixed);
    const end = fixFloatingPoint(endUnfixed);
    const startFloor = Math.floor(start);
    const startCeil = Math.ceil(start);
    const startFraction = start - startFloor;
    const endFraction = end - Math.floor(end);
    const endFloor = Math.floor(end);
    let weightedSum = 0;
    let totalWeight = 0;
    if (startFraction > 0) {
      const firstSample = sourceChannels[startFloor * srcNumberOfChannels + channelIndex];
      weightedSum += firstSample * (1 - startFraction);
      totalWeight += 1 - startFraction;
    }
    for (let k = startCeil;k < endFloor; k++) {
      const num = sourceChannels[k * srcNumberOfChannels + channelIndex];
      weightedSum += num;
      totalWeight += 1;
    }
    if (endFraction > 0) {
      const lastSample = sourceChannels[endFloor * srcNumberOfChannels + channelIndex];
      weightedSum += lastSample * endFraction;
      totalWeight += endFraction;
    }
    const average = weightedSum / totalWeight;
    return average;
  };
  for (let newFrameIndex = 0;newFrameIndex < targetFrames; newFrameIndex++) {
    const start = newFrameIndex * chunkSize;
    const end = start + chunkSize;
    if (TARGET_NUMBER_OF_CHANNELS === srcNumberOfChannels) {
      for (let i = 0;i < srcNumberOfChannels; i++) {
        destination[newFrameIndex * srcNumberOfChannels + i] = getSourceValues(start, end, i);
      }
    }
    if (srcNumberOfChannels === 1) {
      const m = getSourceValues(start, end, 0);
      destination[newFrameIndex * 2 + 0] = m;
      destination[newFrameIndex * 2 + 1] = m;
    } else if (srcNumberOfChannels === 4) {
      const l = getSourceValues(start, end, 0);
      const r = getSourceValues(start, end, 1);
      const sl = getSourceValues(start, end, 2);
      const sr = getSourceValues(start, end, 3);
      const l2 = 0.5 * (l + sl);
      const r2 = 0.5 * (r + sr);
      destination[newFrameIndex * 2 + 0] = l2;
      destination[newFrameIndex * 2 + 1] = r2;
    } else if (srcNumberOfChannels === 6) {
      const l = getSourceValues(start, end, 0);
      const r = getSourceValues(start, end, 1);
      const c = getSourceValues(start, end, 2);
      const sl = getSourceValues(start, end, 4);
      const sr = getSourceValues(start, end, 5);
      const sq = Math.sqrt(1 / 2);
      const norm = 1 / (1 + sq + sq);
      const l2 = (l + sq * (c + sl)) * norm;
      const r2 = (r + sq * (c + sr)) * norm;
      destination[newFrameIndex * 2 + 0] = clampToS16(l2);
      destination[newFrameIndex * 2 + 1] = clampToS16(r2);
    } else {
      for (let i = 0;i < srcNumberOfChannels; i++) {
        destination[newFrameIndex * TARGET_NUMBER_OF_CHANNELS + i] = getSourceValues(start, end, i);
      }
    }
  }
};

// src/convert-audiodata/convert-audiodata.ts
var FORMAT = "s16";
var fixFloatingPoint2 = (value) => {
  const decimal = Math.abs(value % 1);
  if (decimal < 0.0000001) {
    return value < 0 ? Math.ceil(value) : Math.floor(value);
  }
  if (decimal > 0.9999999) {
    return value < 0 ? Math.floor(value) : Math.ceil(value);
  }
  return value;
};
var ceilButNotIfFloatingPointIssue = (value) => {
  const fixed = fixFloatingPoint2(value);
  return Math.ceil(fixed);
};
var convertAudioDataToS16 = ({
  audioData,
  trimStartInSeconds,
  trimEndInSeconds,
  audioDataTimestamp,
  isLast
}) => {
  const {
    numberOfChannels: srcNumberOfChannels,
    sampleRate: currentSampleRate,
    numberOfFrames
  } = audioData;
  const frameOffset = Math.floor(fixFloatingPoint2(trimStartInSeconds * audioData.sampleRate));
  const unroundedFrameCount = numberOfFrames - trimEndInSeconds * audioData.sampleRate - frameOffset;
  const frameCount = isLast ? ceilButNotIfFloatingPointIssue(unroundedFrameCount) : Math.round(unroundedFrameCount);
  const srcChannels = new Int16Array(srcNumberOfChannels * numberOfFrames);
  const isF32 = audioData.format === "f32" || audioData.format === "f32-planar";
  if (isF32) {
    const bytesPerPlane = numberOfFrames * 4;
    const f32Buffer = new ArrayBuffer(srcNumberOfChannels * bytesPerPlane);
    for (let ch = 0;ch < srcNumberOfChannels; ch++) {
      audioData.copyTo(new Float32Array(f32Buffer, ch * bytesPerPlane, numberOfFrames), {
        planeIndex: ch,
        frameOffset: 0,
        frameCount: numberOfFrames,
        format: "f32-planar"
      });
    }
    const f32AudioData = new AudioData({
      format: "f32-planar",
      sampleRate: currentSampleRate,
      numberOfFrames,
      numberOfChannels: srcNumberOfChannels,
      timestamp: audioData.timestamp,
      data: f32Buffer
    });
    f32AudioData.copyTo(srcChannels, {
      planeIndex: 0,
      format: FORMAT,
      frameOffset: 0,
      frameCount: numberOfFrames
    });
    f32AudioData.close();
  } else {
    audioData.copyTo(srcChannels, {
      planeIndex: 0,
      format: FORMAT,
      frameOffset: 0,
      frameCount: numberOfFrames
    });
  }
  const timestampOffsetMicroseconds = frameOffset / audioData.sampleRate * 1e6;
  return {
    data: srcChannels.subarray(frameOffset * srcNumberOfChannels, (frameOffset + frameCount) * srcNumberOfChannels),
    numberOfChannels: srcNumberOfChannels,
    numberOfFrames: frameCount,
    sampleRate: currentSampleRate,
    timestamp: audioDataTimestamp * 1e6 + fixFloatingPoint2(timestampOffsetMicroseconds),
    durationInMicroSeconds: fixFloatingPoint2(frameCount / currentSampleRate * 1e6)
  };
};
var resamplePcmS16AudioData = ({
  targetSampleRate,
  audioData,
  playbackRate,
  isLast
}) => {
  const ratio = audioData.sampleRate / targetSampleRate;
  const newNumberOfFrames = isLast ? ceilButNotIfFloatingPointIssue(audioData.numberOfFrames / ratio / playbackRate) : Math.round(audioData.numberOfFrames / ratio / playbackRate);
  if (newNumberOfFrames === 0) {
    throw new Error("Cannot resample - the given sample rate would result in less than 1 sample");
  }
  if (newNumberOfFrames === audioData.numberOfFrames && TARGET_NUMBER_OF_CHANNELS === audioData.numberOfChannels && playbackRate === 1) {
    return {
      data: audioData.data,
      numberOfFrames: newNumberOfFrames,
      timestamp: audioData.timestamp,
      durationInMicroSeconds: fixFloatingPoint2(newNumberOfFrames / targetSampleRate * 1e6)
    };
  }
  const data = new Int16Array(newNumberOfFrames * TARGET_NUMBER_OF_CHANNELS);
  const chunkSize = audioData.numberOfFrames / newNumberOfFrames;
  resampleAudioData({
    srcNumberOfChannels: audioData.numberOfChannels,
    sourceChannels: audioData.data,
    destination: data,
    targetFrames: newNumberOfFrames,
    chunkSize
  });
  const newAudioData = {
    data,
    numberOfFrames: newNumberOfFrames,
    timestamp: audioData.timestamp,
    durationInMicroSeconds: fixFloatingPoint2(newNumberOfFrames / targetSampleRate * 1e6)
  };
  return newAudioData;
};

// src/convert-audiodata/combine-audiodata.ts
var combineAudioDataAndClosePrevious = (audioDataArray) => {
  let numberOfFrames = 0;
  let durationInMicroSeconds = 0;
  const { numberOfChannels, sampleRate, timestamp } = audioDataArray[0];
  for (const audioData of audioDataArray) {
    if (audioData.numberOfChannels !== numberOfChannels || audioData.sampleRate !== sampleRate) {
      throw new Error("Cannot combine audio data with different formats");
    }
    numberOfFrames += audioData.numberOfFrames;
    durationInMicroSeconds += audioData.durationInMicroSeconds;
  }
  const arr = new Int16Array(numberOfFrames * numberOfChannels);
  let offset = 0;
  for (const audioData of audioDataArray) {
    arr.set(audioData.data, offset);
    offset += audioData.data.length;
  }
  return {
    data: arr,
    numberOfChannels,
    numberOfFrames,
    sampleRate,
    timestamp: fixFloatingPoint2(timestamp),
    durationInMicroSeconds: fixFloatingPoint2(durationInMicroSeconds)
  };
};

// src/audio-extraction/extract-audio.ts
var extractAudioInternal = async ({
  sampleRate,
  src,
  timeInSeconds: unloopedTimeInSeconds,
  durationInSeconds: durationNotYetApplyingPlaybackRate,
  logLevel,
  loop,
  playbackRate,
  audioStreamIndex,
  trimBefore,
  trimAfter,
  fps,
  maxCacheSize,
  credentials,
  requestInit,
  mediaCache
}) => {
  const { getAudio, actualMatroskaTimestamps, isMatroska, getDuration } = await mediaCache.sinkManager.getSink(src, logLevel, credentials, requestInit);
  let mediaDurationInSeconds = null;
  if (loop) {
    mediaDurationInSeconds = await getDuration();
  }
  const audio = await getAudio(audioStreamIndex);
  if (audio === "network-error") {
    return "network-error";
  }
  if (audio === "no-audio-track") {
    return { data: null, durationInSeconds: null };
  }
  if (audio === "cannot-decode-audio") {
    return "cannot-decode";
  }
  if (audio === "unknown-container-format") {
    return "unknown-container-format";
  }
  const timeInSeconds = getTimeInSeconds({
    loop,
    mediaDurationInSeconds,
    unloopedTimeInSeconds,
    src,
    trimAfter,
    playbackRate,
    trimBefore,
    fps,
    ifNoMediaDuration: "fail"
  });
  if (timeInSeconds === null) {
    return { data: null, durationInSeconds: mediaDurationInSeconds };
  }
  try {
    const durationInSeconds = durationNotYetApplyingPlaybackRate * playbackRate;
    const loopStartInSeconds = (trimBefore ?? 0) / fps;
    const loopEndInSeconds = trimAfter === undefined ? mediaDurationInSeconds ?? Infinity : trimAfter / fps;
    if (loop && loopEndInSeconds <= loopStartInSeconds) {
      throw new Error(`Cannot loop audio over an empty range (${loopStartInSeconds} to ${loopEndInSeconds})`);
    }
    const audioDataArray = [];
    let remainingDurationInSeconds = durationInSeconds;
    let segmentStartInSeconds = timeInSeconds;
    let pendingSilenceInSeconds = 0;
    const appendSilence = (silenceDurationInSeconds, numberOfChannels, sourceSampleRate) => {
      const silenceFrames = Math.ceil(fixFloatingPoint2(silenceDurationInSeconds * sourceSampleRate));
      if (silenceFrames > 0) {
        audioDataArray.push({
          data: new Int16Array(silenceFrames * numberOfChannels),
          numberOfChannels,
          numberOfFrames: silenceFrames,
          sampleRate: sourceSampleRate,
          timestamp: timeInSeconds * 1e6,
          durationInMicroSeconds: silenceFrames / sourceSampleRate * 1e6
        });
      }
    };
    while (remainingDurationInSeconds > 0.0000000001) {
      const segmentDurationInSeconds = loop ? Math.min(remainingDurationInSeconds, loopEndInSeconds - segmentStartInSeconds) : remainingDurationInSeconds;
      if (segmentDurationInSeconds <= 0) {
        segmentStartInSeconds = loopStartInSeconds;
        continue;
      }
      const sampleIterator = await mediaCache.audioManager.getIterator({
        src,
        timeInSeconds: segmentStartInSeconds,
        audioSampleSink: audio.sampleSink,
        isMatroska,
        actualMatroskaTimestamps,
        logLevel,
        maxCacheSize
      });
      const samples = await sampleIterator.getSamples(segmentStartInSeconds, segmentDurationInSeconds);
      mediaCache.audioManager.logOpenFrames();
      const segmentAudioData = [];
      for (let i = 0;i < samples.length; i++) {
        const sample = samples[i];
        const segmentEndInSeconds = segmentStartInSeconds + segmentDurationInSeconds;
        if (Math.abs(sample.timestamp - segmentEndInSeconds) * sample.sampleRate < 1) {
          continue;
        }
        if (sample.timestamp + sample.duration <= segmentStartInSeconds) {
          continue;
        }
        const isFirstSample = i === 0;
        const isLastSample = i === samples.length - 1;
        const audioDataRaw = sample.toAudioData();
        let trimStartInSeconds = 0;
        let trimEndInSeconds = 0;
        let leadingSilence = null;
        if (isFirstSample) {
          trimStartInSeconds = fixFloatingPoint2(segmentStartInSeconds - sample.timestamp);
          if (trimStartInSeconds < 0) {
            const silenceFrames = Math.ceil(fixFloatingPoint2(-trimStartInSeconds * audioDataRaw.sampleRate));
            leadingSilence = {
              data: new Int16Array(silenceFrames * audioDataRaw.numberOfChannels),
              numberOfChannels: audioDataRaw.numberOfChannels,
              numberOfFrames: silenceFrames,
              sampleRate: audioDataRaw.sampleRate,
              timestamp: segmentStartInSeconds * 1e6,
              durationInMicroSeconds: silenceFrames / audioDataRaw.sampleRate * 1e6
            };
            trimStartInSeconds = 0;
          }
        }
        if (isLastSample) {
          trimEndInSeconds = Math.max(0, sample.timestamp + sample.duration - segmentEndInSeconds);
        }
        const audioData = convertAudioDataToS16({
          audioData: audioDataRaw,
          trimStartInSeconds,
          trimEndInSeconds,
          audioDataTimestamp: sample.timestamp,
          isLast: isLastSample
        });
        audioDataRaw.close();
        if (audioData.numberOfFrames === 0) {
          continue;
        }
        if (leadingSilence) {
          segmentAudioData.push(leadingSilence);
        }
        segmentAudioData.push(audioData);
      }
      if (segmentAudioData.length > 0) {
        const { numberOfChannels, sampleRate: sourceSampleRate } = segmentAudioData[0];
        appendSilence(pendingSilenceInSeconds, numberOfChannels, sourceSampleRate);
        audioDataArray.push(...segmentAudioData);
        const segmentFrames = segmentAudioData.reduce((sum, data) => sum + data.numberOfFrames, 0);
        pendingSilenceInSeconds = Math.max(0, segmentDurationInSeconds - segmentFrames / sourceSampleRate);
      } else {
        pendingSilenceInSeconds += segmentDurationInSeconds;
      }
      remainingDurationInSeconds -= segmentDurationInSeconds;
      segmentStartInSeconds = loopStartInSeconds;
    }
    if (audioDataArray.length === 0) {
      return { data: null, durationInSeconds: mediaDurationInSeconds };
    }
    if (loop && pendingSilenceInSeconds > 0) {
      const { numberOfChannels, sampleRate: sourceSampleRate } = audioDataArray[0];
      appendSilence(pendingSilenceInSeconds, numberOfChannels, sourceSampleRate);
    }
    const combined = combineAudioDataAndClosePrevious(audioDataArray);
    const resampled = resamplePcmS16AudioData({
      targetSampleRate: sampleRate,
      audioData: combined,
      playbackRate,
      isLast: true
    });
    return { data: resampled, durationInSeconds: mediaDurationInSeconds };
  } catch (err) {
    const error = err;
    if (isNetworkError(error)) {
      return "network-error";
    }
    if (isUnsupportedConfigurationError(error)) {
      return "cannot-decode";
    }
    throw err;
  }
};
var extractAudio = (params) => {
  return params.mediaCache.queueAudioExtraction(() => extractAudioInternal(params));
};

// src/video-extraction/extract-frame.ts
import { Internals as Internals18 } from "remotion";
var extractFrameInternal = async ({
  src,
  timeInSeconds: unloopedTimeInSeconds,
  logLevel,
  loop,
  trimAfter,
  trimBefore,
  playbackRate,
  fps,
  maxCacheSize,
  credentials,
  requestInit,
  mediaCache
}) => {
  const sink = await mediaCache.sinkManager.getSink(src, logLevel, credentials, requestInit);
  const [video, mediaDurationInSecondsRaw] = await Promise.all([
    sink.getVideo(),
    loop ? sink.getDuration() : Promise.resolve(null)
  ]);
  const mediaDurationInSeconds = loop ? mediaDurationInSecondsRaw : null;
  if (video === "no-video-track") {
    throw new Error(`No video track found for ${src}`);
  }
  if (video === "cannot-decode") {
    return {
      type: "cannot-decode",
      durationInSeconds: mediaDurationInSeconds,
      error: new Error(`The video track in ${src} has a codec or configuration the browser cannot decode.`)
    };
  }
  if (video === "cannot-decode-prores") {
    return {
      type: "cannot-decode-prores",
      durationInSeconds: mediaDurationInSeconds
    };
  }
  if (video === "unknown-container-format") {
    return { type: "unknown-container-format" };
  }
  if (video === "network-error") {
    return { type: "network-error" };
  }
  if (video === "cannot-decode-alpha") {
    return {
      type: "cannot-decode-alpha",
      durationInSeconds: mediaDurationInSeconds
    };
  }
  const timeInSeconds = getTimeInSeconds({
    loop,
    mediaDurationInSeconds,
    unloopedTimeInSeconds,
    src,
    trimAfter,
    playbackRate,
    trimBefore,
    fps,
    ifNoMediaDuration: "fail"
  });
  if (timeInSeconds === null) {
    return {
      type: "success",
      frame: null,
      rotation: 0,
      durationInSeconds: await sink.getDuration()
    };
  }
  try {
    const keyframeBank = await mediaCache.keyframeManager.requestKeyframeBank({
      videoSampleSink: video.sampleSink,
      timeResolution: video.timeResolution,
      timestamp: timeInSeconds,
      src,
      logLevel,
      maxCacheSize,
      fps
    });
    if (!keyframeBank) {
      return {
        type: "success",
        frame: null,
        rotation: 0,
        durationInSeconds: await sink.getDuration()
      };
    }
    const frame = await keyframeBank.getFrameFromTimestamp(timeInSeconds, fps);
    const rotation = frame?.rotation ?? 0;
    return {
      type: "success",
      frame: frame?.toVideoFrame() ?? null,
      rotation,
      durationInSeconds: await sink.getDuration()
    };
  } catch (err) {
    Internals18.Log.info({ logLevel, tag: "@remotion/media" }, `Error decoding ${src} at time ${timeInSeconds}: ${err}`, err);
    return {
      type: "cannot-decode",
      durationInSeconds: mediaDurationInSeconds,
      error: new Error(`Video decoding failed for ${src} at ${timeInSeconds}s: ${err}`, { cause: err })
    };
  }
};
var extractFrame = (params) => {
  return params.mediaCache.queueFrameExtraction(() => extractFrameInternal(params));
};

// src/video-extraction/rotate-frame.ts
var rotateFrame = async ({
  frame,
  rotation
}) => {
  if (rotation === 0) {
    const directBitmap = await createImageBitmap(frame);
    return directBitmap;
  }
  const width = rotation === 90 || rotation === 270 ? frame.displayHeight : frame.displayWidth;
  const height = rotation === 90 || rotation === 270 ? frame.displayWidth : frame.displayHeight;
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2d context");
  }
  canvas.width = width;
  canvas.height = height;
  if (rotation === 90) {
    ctx.translate(width, 0);
  } else if (rotation === 180) {
    ctx.translate(width, height);
  } else if (rotation === 270) {
    ctx.translate(0, height);
  }
  ctx.rotate(rotation * (Math.PI / 180));
  ctx.drawImage(frame, 0, 0);
  const bitmap = await createImageBitmap(canvas);
  return bitmap;
};

// src/extract-frame-and-audio.ts
var extractFrameAndAudio = async ({
  sampleRate,
  src,
  timeInSeconds,
  logLevel,
  durationInSeconds,
  playbackRate,
  includeAudio,
  includeVideo,
  loop,
  audioStreamIndex,
  trimAfter,
  trimBefore,
  fps,
  maxCacheSize,
  credentials,
  requestInit,
  mediaCache
}) => {
  let extractedFrame = null;
  try {
    if (includeVideo || includeAudio) {
      const sink = await mediaCache.sinkManager.getSink(src, logLevel, credentials, requestInit);
      if (sink.formatDetectionError) {
        return {
          type: isNetworkError(sink.formatDetectionError) ? "network-error" : "unknown-container-format",
          error: sink.formatDetectionError
        };
      }
    }
    const [videoResult, audioResult] = await Promise.allSettled([
      includeVideo ? extractFrame({
        src,
        timeInSeconds,
        logLevel,
        loop,
        trimAfter,
        playbackRate,
        trimBefore,
        fps,
        maxCacheSize,
        credentials,
        requestInit,
        mediaCache
      }) : null,
      includeAudio ? extractAudio({
        sampleRate,
        src,
        timeInSeconds,
        durationInSeconds,
        logLevel,
        loop,
        playbackRate,
        audioStreamIndex,
        trimAfter,
        fps,
        trimBefore,
        maxCacheSize,
        credentials,
        requestInit,
        mediaCache
      }) : null
    ]);
    if (videoResult.status === "rejected") {
      throw videoResult.reason;
    }
    const video = videoResult.value;
    extractedFrame = video?.type === "success" ? video.frame : null;
    if (audioResult.status === "rejected") {
      throw audioResult.reason;
    }
    const audio = audioResult.value;
    if (video?.type === "cannot-decode") {
      return {
        type: "cannot-decode",
        error: video.error,
        durationInSeconds: video.durationInSeconds
      };
    }
    if (video?.type === "cannot-decode-prores") {
      return {
        type: "cannot-decode-prores",
        durationInSeconds: video.durationInSeconds
      };
    }
    if (video?.type === "unknown-container-format") {
      return { type: "unknown-container-format", error: null };
    }
    if (video?.type === "cannot-decode-alpha") {
      return {
        type: "cannot-decode-alpha",
        durationInSeconds: video.durationInSeconds
      };
    }
    if (video?.type === "network-error") {
      return { type: "network-error", error: null };
    }
    if (audio === "unknown-container-format") {
      return { type: "unknown-container-format", error: null };
    }
    if (audio === "network-error") {
      return { type: "network-error", error: null };
    }
    if (audio === "cannot-decode") {
      return {
        type: "cannot-decode",
        error: new Error(`The ${audioStreamIndex === null ? "automatically selected audio track" : `audio track at index ${audioStreamIndex}`} in ${src} could not be decoded by the browser.`),
        durationInSeconds: video?.type === "success" ? video.durationInSeconds : null
      };
    }
    return {
      type: "success",
      frame: video?.frame ? await rotateFrame({
        frame: video.frame,
        rotation: video.rotation
      }) : null,
      audio: audio?.data ?? null,
      durationInSeconds: audio?.durationInSeconds ?? null
    };
  } catch (err) {
    const error = err;
    if (isNetworkError(error)) {
      return { type: "network-error", error };
    }
    throw err;
  } finally {
    extractedFrame?.close();
  }
};

// src/video-extraction/add-broadcast-channel-listener.ts
var emitReadiness = (channel) => {
  const message = {
    type: "main-tab-ready",
    mainTabId: crypto.getRandomValues(new Uint32Array(4)).join("-")
  };
  channel.postMessage(message);
  setInterval(() => {
    channel.postMessage(message);
  }, 300);
};
var addBroadcastChannelListener = () => {
  if (!(typeof window !== "undefined" && window.remotion_broadcastChannel && window.remotion_isMainTab)) {
    return;
  }
  window.remotion_broadcastChannel.addEventListener("message", async (event) => {
    const data = event.data;
    if (data.type === "request") {
      try {
        const result = await extractFrameAndAudio({
          sampleRate: data.sampleRate,
          src: data.src,
          timeInSeconds: data.timeInSeconds,
          logLevel: data.logLevel,
          durationInSeconds: data.durationInSeconds,
          playbackRate: data.playbackRate,
          includeAudio: data.includeAudio,
          includeVideo: data.includeVideo,
          loop: data.loop,
          audioStreamIndex: data.audioStreamIndex,
          trimAfter: data.trimAfter,
          trimBefore: data.trimBefore,
          fps: data.fps,
          maxCacheSize: data.maxCacheSize,
          credentials: data.credentials,
          requestInit: data.requestInit,
          mediaCache: globalMediaCache
        });
        if (result.type === "cannot-decode") {
          const cannotDecodeResponse = {
            type: "response-cannot-decode",
            error: result.error,
            id: data.id,
            durationInSeconds: result.durationInSeconds
          };
          window.remotion_broadcastChannel.postMessage(cannotDecodeResponse);
          return;
        }
        if (result.type === "cannot-decode-prores") {
          const cannotDecodeProresResponse = {
            type: "response-cannot-decode-prores",
            id: data.id,
            durationInSeconds: result.durationInSeconds
          };
          window.remotion_broadcastChannel.postMessage(cannotDecodeProresResponse);
          return;
        }
        if (result.type === "cannot-decode-alpha") {
          const cannotDecodeAlphaResponse = {
            type: "response-cannot-decode-alpha",
            id: data.id,
            durationInSeconds: result.durationInSeconds
          };
          window.remotion_broadcastChannel.postMessage(cannotDecodeAlphaResponse);
          return;
        }
        if (result.type === "network-error" || result.type === "unknown-container-format") {
          const mediaErrorResponse = {
            type: result.type === "network-error" ? "response-network-error" : "response-unknown-container-format",
            id: data.id,
            error: result.error ? {
              name: result.error.name,
              message: result.error.message,
              stack: result.error.stack ?? null
            } : null
          };
          window.remotion_broadcastChannel.postMessage(mediaErrorResponse);
          return;
        }
        const { frame, audio, durationInSeconds } = result;
        const imageBitmap = frame ? await createImageBitmap(frame) : null;
        if (frame) {
          frame.close();
        }
        const response = {
          type: "response-success",
          id: data.id,
          frame: imageBitmap,
          audio,
          durationInSeconds: durationInSeconds ?? null
        };
        try {
          window.remotion_broadcastChannel.postMessage(response);
        } finally {
          imageBitmap?.close();
        }
      } catch (error) {
        const response = {
          type: "response-error",
          id: data.id,
          errorStack: error.stack ?? "No stack trace"
        };
        window.remotion_broadcastChannel.postMessage(response);
      }
    } else if (data.type === "main-tab-ready") {} else {
      throw new Error("Invalid message: " + JSON.stringify(data));
    }
  });
  emitReadiness(window.remotion_broadcastChannel);
};
var mainTabIsReadyProm = null;
var mainTabId = null;
var waitForMainTabToBeReady = async (channel) => {
  if (!mainTabIsReadyProm) {
    mainTabIsReadyProm = new Promise((resolve) => {
      const onMessage = (event) => {
        const data = event.data;
        if (data.type === "main-tab-ready") {
          mainTabId = data.mainTabId;
          resolve();
        }
      };
      channel.addEventListener("message", onMessage);
    });
  }
  await mainTabIsReadyProm;
  return mainTabId;
};

// src/video-extraction/extract-frame-via-broadcast-channel.ts
addBroadcastChannelListener();
var extractFrameViaBroadcastChannel = async ({
  sampleRate,
  src,
  timeInSeconds,
  logLevel,
  durationInSeconds,
  playbackRate,
  includeAudio,
  includeVideo,
  isClientSideRendering,
  loop,
  audioStreamIndex,
  trimAfter,
  trimBefore,
  fps,
  maxCacheSize,
  credentials,
  requestInit,
  mediaCache
}) => {
  if (isClientSideRendering || window.remotion_isMainTab) {
    return extractFrameAndAudio({
      sampleRate,
      logLevel,
      src,
      timeInSeconds,
      durationInSeconds,
      playbackRate,
      includeAudio,
      includeVideo,
      loop,
      audioStreamIndex,
      trimAfter,
      trimBefore,
      fps,
      maxCacheSize,
      credentials,
      requestInit,
      mediaCache
    });
  }
  const channel = window.remotion_broadcastChannel;
  let mainTabId = await waitForMainTabToBeReady(channel);
  const request = {
    type: "request",
    sampleRate,
    src,
    timeInSeconds,
    id: crypto.randomUUID(),
    logLevel,
    durationInSeconds,
    playbackRate,
    includeAudio,
    includeVideo,
    loop,
    audioStreamIndex,
    trimAfter,
    trimBefore,
    fps,
    maxCacheSize,
    credentials,
    requestInit: normalizeMediaRequestInit(requestInit)
  };
  let cleanup = () => {};
  const resolvePromise = new Promise((resolve, reject) => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data) {
        return;
      }
      if (data.type === "main-tab-ready") {
        if (data.mainTabId !== mainTabId) {
          mainTabId = data.mainTabId;
          channel.postMessage(request);
        }
        return;
      }
      if (data.id !== request.id) {
        return;
      }
      if (data.type === "response-success") {
        resolve({
          type: "success",
          frame: data.frame ? data.frame : null,
          audio: data.audio ? data.audio : null,
          durationInSeconds: data.durationInSeconds ? data.durationInSeconds : null
        });
        return;
      }
      if (data.type === "response-error") {
        reject(data.errorStack);
        return;
      }
      if (data.type === "response-cannot-decode") {
        resolve({
          type: "cannot-decode",
          error: data.error,
          durationInSeconds: data.durationInSeconds
        });
        return;
      }
      if (data.type === "response-cannot-decode-prores") {
        resolve({
          type: "cannot-decode-prores",
          durationInSeconds: data.durationInSeconds
        });
        return;
      }
      if (data.type === "response-network-error" || data.type === "response-unknown-container-format") {
        const error = data.error ? new Error(data.error.message) : null;
        if (error && data.error) {
          error.name = data.error.name;
          error.stack = data.error.stack ?? undefined;
        }
        resolve({
          type: data.type === "response-network-error" ? "network-error" : "unknown-container-format",
          error
        });
        return;
      }
      if (data.type === "response-cannot-decode-alpha") {
        resolve({
          type: "cannot-decode-alpha",
          durationInSeconds: data.durationInSeconds
        });
        return;
      }
      throw new Error(`Invalid message: ${JSON.stringify(data)}`);
    };
    channel.addEventListener("message", onMessage);
    cleanup = () => channel.removeEventListener("message", onMessage);
  });
  let timeoutId = null;
  try {
    channel.postMessage(request);
    return await Promise.race([
      resolvePromise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`Timeout while extracting frame at time ${timeInSeconds}sec from ${src}`));
        }, Math.max(3000, window.remotion_puppeteerTimeout - 5000));
      })
    ]);
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    cleanup();
  }
};

// src/audio/audio-for-rendering.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var AudioForRendering = ({
  volume: volumeProp,
  playbackRate,
  src,
  muted,
  loopVolumeCurveBehavior,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  logLevel: overriddenLogLevel,
  loop,
  fallbackHtml5AudioProps,
  audioStreamIndex,
  showInTimeline,
  style,
  name,
  disallowFallbackToHtml5Audio,
  toneFrequency,
  preservePitch,
  trimAfter,
  trimBefore,
  onError,
  credentials,
  requestInit
}) => {
  const audioContext = useContext3(Internals19.SharedAudioContext);
  const sampleRate = audioContext?.sampleRate ?? 48000;
  const defaultLogLevel = Internals19.useLogLevel();
  const logLevel = overriddenLogLevel ?? defaultLogLevel;
  const frame = useCurrentFrame2();
  const absoluteFrame = Internals19.useTimelinePosition();
  const videoConfig = Internals19.useUnsafeVideoConfig();
  const { registerRenderAsset, unregisterRenderAsset } = useContext3(Internals19.RenderAssetManager);
  const startsAt = Internals19.useMediaStartsAt();
  const environment = useRemotionEnvironment();
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  if (!src) {
    throw new TypeError("No `src` was passed to <Audio>.");
  }
  const { fps } = videoConfig;
  const { delayRender, continueRender, cancelRender } = useDelayRender();
  const [replaceWithHtml5Audio, setReplaceWithHtml5Audio] = useState2(false);
  const [initialRequestInit] = useState2(requestInit);
  const sequenceContext = useContext3(Internals19.SequenceContext);
  const sequencePlaybackRate = sequenceContext?.playbackRate ?? 1;
  const startInVideo = sequenceContext ? sequenceContext.cumulatedFrom + sequenceContext.relativeFrom : 0;
  const id = useMemo2(() => `media-audio-${random(src)}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.relativeFrom}-${sequenceContext?.durationInFrames}`, [
    src,
    sequenceContext?.cumulatedFrom,
    sequenceContext?.relativeFrom,
    sequenceContext?.durationInFrames
  ]);
  const maxCacheSize = useMaxMediaCacheSize(logLevel);
  const mediaCache = useRenderMediaCache(logLevel);
  const audioEnabled = Internals19.useAudioEnabled();
  const { isMutedForPlayback, shouldUseAudio } = Internals19.useMediaAudioState({
    muted: muted ?? false,
    volume: null,
    audioEnabled
  });
  useLayoutEffect2(() => {
    const timestamp = frame / sequencePlaybackRate / fps;
    const durationInSeconds = 1 / fps;
    if (!shouldUseAudio) {
      return;
    }
    if (replaceWithHtml5Audio) {
      return;
    }
    let cancelled = false;
    const newHandle = delayRender(`Extracting audio for frame ${frame}`, {
      retries: delayRenderRetries ?? undefined,
      timeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined
    });
    extractFrameViaBroadcastChannel({
      sampleRate,
      src,
      timeInSeconds: timestamp,
      durationInSeconds,
      playbackRate: (playbackRate ?? 1) * sequencePlaybackRate,
      logLevel,
      includeAudio: shouldUseAudio,
      includeVideo: false,
      isClientSideRendering: environment.isClientSideRendering,
      loop: loop ?? false,
      audioStreamIndex: audioStreamIndex ?? null,
      trimAfter,
      trimBefore,
      fps,
      maxCacheSize,
      credentials,
      requestInit: initialRequestInit,
      mediaCache
    }).then((result) => {
      if (cancelled || mediaCache.isDisposed()) {
        return;
      }
      const handleError = (error, clientSideError, fallbackMessage) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error,
          disallowFallback: disallowFallbackToHtml5Audio ?? false,
          isClientSideRendering: environment.isClientSideRendering,
          clientSideError
        });
        if (action === "fail") {
          cancelRender(errorToUse);
        }
        Internals19.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
        setReplaceWithHtml5Audio(true);
      };
      if ((result.type === "unknown-container-format" || result.type === "network-error") && result.error) {
        handleError(result.error, result.error, `Failed to read ${src}: ${result.error.message}, falling back to <Html5Audio>`);
        return;
      }
      if (result.type === "unknown-container-format") {
        handleError(new Error(`Unknown container format ${src}.`), new Error(`Cannot render audio "${src}": Unknown container format. See supported formats: https://www.remotion.dev/docs/mediabunny/formats`), `Unknown container format for ${src} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <Html5Audio>`);
        return;
      }
      if (result.type === "cannot-decode") {
        handleError(result.error, new Error(`Cannot render audio "${src}": ${result.error.message}`, {
          cause: result.error
        }), `Cannot decode ${src}, falling back to <Html5Audio>`);
        return;
      }
      if (result.type === "cannot-decode-alpha") {
        throw new Error(`Cannot decode alpha component for ${src}, and 'disallowFallbackToHtml5Audio' was set. But this should never happen, since you used the <Audio> tag. Please report this as a bug.`);
      }
      if (result.type === "cannot-decode-prores") {
        throw new Error(`Encountered ProRes media for ${src}. But this should never happen, since you used the <Audio> tag. Please report this as a bug.`);
      }
      if (result.type === "network-error") {
        handleError(new Error(`Network error fetching ${src}.`), new Error(`Cannot render audio "${src}": Network error while fetching the audio (possibly CORS).`), `Network error fetching ${src}, falling back to <Html5Audio>`);
        return;
      }
      const { audio, durationInSeconds: assetDurationInSeconds } = result;
      const volumePropsFrame = frameForVolumeProp({
        behavior: loopVolumeCurveBehavior ?? "repeat",
        loop: loop ?? false,
        assetDurationInSeconds: assetDurationInSeconds ?? 0,
        fps,
        frame,
        startsAt,
        playbackRate: playbackRate ?? 1,
        trimBefore,
        trimAfter
      });
      const volume = Internals19.evaluateVolume({
        volume: volumeProp,
        frame: volumePropsFrame,
        mediaVolume: 1
      });
      Internals19.warnAboutTooHighVolume(volume);
      if (audio && volume > 0) {
        applyVolume(audio.data, volume);
        registerRenderAsset({
          type: "inline-audio",
          id,
          audio: environment.isClientSideRendering ? audio.data : Array.from(audio.data),
          frame: absoluteFrame,
          startInVideo,
          timestamp: audio.timestamp,
          duration: audio.durationInMicroSeconds,
          toneFrequency: (toneFrequency ?? 1) / (preservePitch ? (playbackRate ?? 1) * sequencePlaybackRate : 1)
        });
      }
      continueRender(newHandle);
    }).catch((error) => {
      if (cancelled || mediaCache.isDisposed()) {
        return;
      }
      cancelRender(error);
    });
    return () => {
      cancelled = true;
      continueRender(newHandle);
      unregisterRenderAsset(id);
    };
  }, [
    sampleRate,
    absoluteFrame,
    continueRender,
    cancelRender,
    delayRender,
    delayRenderRetries,
    delayRenderTimeoutInMilliseconds,
    disallowFallbackToHtml5Audio,
    environment.isClientSideRendering,
    fps,
    frame,
    id,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    shouldUseAudio,
    playbackRate,
    sequencePlaybackRate,
    registerRenderAsset,
    src,
    startInVideo,
    startsAt,
    unregisterRenderAsset,
    volumeProp,
    audioStreamIndex,
    toneFrequency,
    preservePitch,
    trimAfter,
    trimBefore,
    replaceWithHtml5Audio,
    maxCacheSize,
    onError,
    credentials,
    initialRequestInit,
    mediaCache
  ]);
  if (replaceWithHtml5Audio) {
    return /* @__PURE__ */ jsx2(Html5Audio, {
      src,
      playbackRate,
      muted: isMutedForPlayback,
      loop,
      volume: volumeProp,
      delayRenderRetries,
      delayRenderTimeoutInMilliseconds,
      style,
      loopVolumeCurveBehavior,
      audioStreamIndex,
      useWebAudioApi: fallbackHtml5AudioProps?.useWebAudioApi,
      onError: fallbackHtml5AudioProps?.onError,
      toneFrequency,
      acceptableTimeShiftInSeconds: fallbackHtml5AudioProps?.acceptableTimeShiftInSeconds,
      preservePitch: fallbackHtml5AudioProps?.preservePitch ?? preservePitch ?? true,
      name,
      showInTimeline
    });
  }
  return null;
};

// src/audio/audio.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var { validateMediaProps } = Internals20;
var audioSchema = {
  src: {
    type: "asset",
    assetType: "audio",
    default: undefined,
    description: "Source",
    keyframable: false
  },
  ...Internals20.baseSchema,
  trimAfter: Internals20.trimAfterField,
  ...Internals20.premountSchema,
  volume: {
    type: "number",
    min: 0,
    max: 20,
    step: 0.01,
    default: 1,
    description: "Volume",
    hiddenFromList: false
  },
  playbackRate: {
    type: "number",
    min: 0.1,
    step: 0.01,
    default: 1,
    description: "Playback rate",
    hiddenFromList: false,
    keyframable: false
  },
  preservePitch: {
    type: "boolean",
    default: false,
    description: "Preserve pitch",
    keyframable: false
  },
  toneFrequency: {
    type: "number",
    min: 0.01,
    max: 2,
    step: 0.01,
    default: 1,
    description: "Pitch",
    hiddenFromList: false,
    keyframable: false
  },
  muted: { type: "boolean", default: false, description: "Muted" },
  loop: { type: "boolean", default: false, description: "Loop" }
};
var AudioInner = (props) => {
  const {
    name,
    showInTimeline,
    controls,
    from,
    durationInFrames,
    freeze,
    hidden,
    style,
    premountFor,
    postmountFor,
    ...otherProps
  } = props;
  const environment = useRemotionEnvironment2();
  const sourceStack = controls ? Internals20.getStackForControls(controls) : null;
  const mediaStartsAt = Internals20.useMediaStartsAt();
  const videoConfig = useVideoConfig2();
  const effectiveTrimAfter = getMediaTrimAfter({
    durationInFrames,
    trimAfter: props.trimAfter,
    trimBefore: props.trimBefore
  });
  const audioSequenceDuration = getVideoSequenceDuration({
    durationInFrames,
    loop: props.loop ?? false,
    playbackRate: props.playbackRate ?? 1,
    trimAfter: effectiveTrimAfter,
    trimBefore: props.trimBefore
  });
  const sequenceDurationInFrames = Math.min(audioSequenceDuration ?? Infinity, Math.max(0, videoConfig.durationInFrames - (from ?? 0)));
  const [mediaDurationInSeconds, setMediaDurationInSeconds] = useState3(null);
  const basicInfo = Internals20.useBasicMediaInTimeline({
    src: props.src,
    playbackRate: props.playbackRate ?? 1,
    trimBefore: props.trimBefore,
    trimAfter: effectiveTrimAfter,
    sequenceDurationInFrames,
    mediaStartsAt,
    displayName: name ?? "<Audio>",
    loop: props.loop ?? false,
    muted: props.muted ?? false
  });
  const loopDisplay = useMemo3(() => getLoopDisplay({
    loop: props.loop ?? false,
    mediaDurationInSeconds,
    playbackRate: props.playbackRate ?? 1,
    trimAfter: effectiveTrimAfter,
    trimBefore: props.trimBefore,
    sequenceDurationInFrames,
    compFps: videoConfig.fps
  }), [
    props.loop,
    mediaDurationInSeconds,
    props.playbackRate,
    effectiveTrimAfter,
    props.trimBefore,
    sequenceDurationInFrames,
    videoConfig.fps
  ]);
  const isMedia = useMemo3(() => ({
    type: "audio",
    data: basicInfo
  }), [basicInfo]);
  const {
    effectivePostmountFor,
    effectivePremountFor,
    freezeFrame,
    isPremountingOrPostmounting,
    postmountingActive,
    premountingActive,
    premountingStyle
  } = Internals20.usePremounting({
    from: from ?? 0,
    durationInFrames: basicInfo.duration,
    premountFor: premountFor ?? null,
    postmountFor: postmountFor ?? null,
    style: style ?? null,
    styleWhilePremounted: null,
    styleWhilePostmounted: null,
    hideWhilePremounted: "display-none"
  });
  if (typeof props.src !== "string") {
    throw new TypeError(`The \`<Audio>\` tag requires a string for \`src\`, but got ${JSON.stringify(props.src)} instead.`);
  }
  validateMediaProps({
    playbackRate: props.playbackRate,
    preservePitch: props.preservePitch,
    volume: props.volume
  }, "Audio");
  validateToneFrequency({
    toneFrequency: props.toneFrequency,
    component: "Audio"
  });
  if (sequenceDurationInFrames === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx3(Freeze, {
    frame: freezeFrame,
    active: isPremountingOrPostmounting,
    _remotionInternalIsPremounting: premountingActive,
    children: /* @__PURE__ */ jsx3(Sequence, {
      layout: "none",
      from: from ?? 0,
      durationInFrames: basicInfo.duration,
      freeze,
      _remotionInternalIsMedia: isMedia,
      _remotionInternalPremountDisplay: effectivePremountFor || null,
      _remotionInternalPostmountDisplay: effectivePostmountFor || null,
      _remotionInternalIsPremounting: premountingActive,
      _remotionInternalIsPostmounting: postmountingActive,
      name: name ?? "<Audio>",
      _remotionInternalDocumentationLink: "https://www.remotion.dev/docs/media/audio",
      controls,
      _remotionInternalLoopDisplay: loopDisplay,
      showInTimeline: showInTimeline ?? true,
      hidden,
      children: /* @__PURE__ */ jsx3(Internals20.SequenceContent, {
        children: environment.isRendering ? /* @__PURE__ */ jsx3(AudioForRendering, {
          ...otherProps,
          trimAfter: effectiveTrimAfter,
          style: premountingStyle ?? undefined
        }) : /* @__PURE__ */ jsx3(AudioForPreview, {
          name,
          ...otherProps,
          preservePitch: props.preservePitch ?? null,
          trimAfter: effectiveTrimAfter,
          style: premountingStyle,
          _remotionInternalStack: sourceStack,
          setMediaDurationInSeconds
        })
      })
    })
  });
};
var Audio = Interactive.withSchema({
  Component: AudioInner,
  componentName: "<Audio>",
  componentIdentity: "dev.remotion.media.Audio",
  schema: audioSchema,
  supportsEffects: false
});

// src/video/video.tsx
import { useMemo as useMemo6, useState as useState6 } from "react";
import {
  Freeze as Freeze2,
  Internals as Internals24,
  Interactive as Interactive2,
  Sequence as Sequence2,
  useRemotionEnvironment as useRemotionEnvironment5,
  useVideoConfig as useVideoConfig5
} from "remotion";
import { NoReactInternals } from "remotion/no-react";

// src/video/video-for-preview.tsx
import {
  useContext as useContext4,
  useEffect as useEffect2,
  useLayoutEffect as useLayoutEffect3,
  useMemo as useMemo4,
  useRef as useRef3,
  useState as useState4
} from "react";
import {
  Html5Video,
  Internals as Internals22,
  useBufferState as useBufferState2,
  useCurrentFrame as useCurrentFrame3,
  useRemotionEnvironment as useRemotionEnvironment3,
  useVideoConfig as useVideoConfig3
} from "remotion";

// src/prores-error.ts
var PRORES_DOCS_URL = "https://www.remotion.dev/docs/videos/prores";
var proresDecoderNotEnabledMessage = (src) => {
  return `Cannot decode "${src}": it is encoded with Apple ProRes, which WebCodecs does not support natively. ` + `ProRes decoding is not enabled by default. Register the ProRes decoder by calling ` + `registerProresDecoder() from "@mediabunny/prores" in your entry point, before registerRoot(). ` + `See ${PRORES_DOCS_URL}.`;
};

class ProResDecoderNotEnabledError extends Error {
  constructor(src) {
    super(proresDecoderNotEnabledMessage(src));
    this.name = "ProResDecoderNotEnabledError";
  }
}

// src/video/video-frame-cache.ts
var cache = new Map;
var MAX_CACHE_BYTES = 64 * 1024 * 1024;
var cacheBytes = 0;
var removeCachedFrame = (src, canvas) => {
  cache.delete(src);
  cacheBytes -= canvas.width * canvas.height * 4;
  canvas.width = 0;
  canvas.height = 0;
};
var cacheVideoFrame = (src, sourceCanvas) => {
  const { width, height } = sourceCanvas;
  if (width === 0 || height === 0) {
    return;
  }
  let cached = cache.get(src);
  if (!cached || cached.width !== width || cached.height !== height) {
    if (cached) {
      removeCachedFrame(src, cached);
    }
    const bytes = width * height * 4;
    if (bytes > MAX_CACHE_BYTES) {
      return;
    }
    for (const [key, canvas] of cache) {
      if (cacheBytes + bytes <= MAX_CACHE_BYTES) {
        break;
      }
      removeCachedFrame(key, canvas);
    }
    cached = new OffscreenCanvas(width, height);
    cache.set(src, cached);
    cacheBytes += bytes;
  }
  cache.delete(src);
  cache.set(src, cached);
  const ctx = cached.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(sourceCanvas, 0, 0);
};
var getCachedVideoFrame = (src) => {
  const cached = cache.get(src);
  if (!cached) {
    return null;
  }
  cache.delete(src);
  cache.set(src, cached);
  return cached;
};

// src/video/warn-object-fit-css.ts
import { Internals as Internals21 } from "remotion";
var OBJECT_FIT_CLASS_PATTERN = /\bobject-(contain|cover|fill|none|scale-down)\b/;
var warnedStyle = false;
var warnedClassName = false;
var warnAboutObjectFitInStyleOrClassName = ({
  style,
  className,
  logLevel
}) => {
  if (!warnedStyle && style?.objectFit) {
    warnedStyle = true;
    Internals21.Log.warn({ logLevel, tag: "@remotion/media" }, "Use the `objectFit` prop instead of the `style` prop.");
  }
  if (!warnedClassName && className && OBJECT_FIT_CLASS_PATTERN.test(className)) {
    warnedClassName = true;
    Internals21.Log.warn({ logLevel, tag: "@remotion/media" }, "Use the `objectFit` prop instead of `object-*` CSS class names.");
  }
};

// src/video/video-for-preview.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var {
  useUnsafeVideoConfig: useUnsafeVideoConfig2,
  SharedAudioContext: SharedAudioContext2,
  useMediaVolumeState: useMediaVolumeState2,
  useFrameForVolumeProp: useFrameForVolumeProp2,
  evaluateVolume: evaluateVolume2,
  warnAboutTooHighVolume: warnAboutTooHighVolume2,
  usePreload: usePreload2,
  SequenceContext: SequenceContext2,
  useEffectChainState,
  usePlaying: usePlaying2,
  useBuffering: useBuffering2,
  useMediaAudioState: useMediaAudioState2
} = Internals22;
var studioVideoCanvases = new Map;
var VideoForPreviewAssertedShowing = ({
  src: unpreloadedSrc,
  mediaTime,
  style,
  playbackRate,
  toneFrequency,
  preservePitch,
  logLevel,
  className,
  muted,
  volume,
  loopVolumeCurveBehavior,
  onVideoFrame,
  showInTimeline,
  loop,
  trimAfter,
  trimBefore,
  _remotionInternalStack,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  audioStreamIndex,
  debugOverlay,
  headless,
  onError,
  credentials,
  requestInit,
  objectFit: objectFitProp,
  maxCanvasSinkFrameSize,
  _experimentalInitiallyDrawCachedFrame,
  effects,
  effectsOutputSize,
  setMediaDurationInSeconds,
  ...props
}) => {
  const src = usePreload2(unpreloadedSrc);
  const canvasRef = useRef3(null);
  const { isStudio } = useRemotionEnvironment3();
  const videoConfig = useUnsafeVideoConfig2();
  const frame = useCurrentFrame3();
  const mediaPlayerRef = useRef3(null);
  const initialTrimBeforeRef = useRef3(trimBefore);
  const initialTrimAfterRef = useRef3(trimAfter);
  const initialOnVideoFrameRef = useRef3(onVideoFrame);
  const [initialRequestInit] = useState4(requestInit);
  const [mediaPlayerReady, setMediaPlayerReady] = useState4(false);
  const [knownDuration, setKnownDuration] = useState4(null);
  const [terminalError, setTerminalError] = useState4(null);
  const [shouldFallbackToNativeVideo, setShouldFallbackToNativeVideo] = useState4(false);
  const playing = usePlaying2();
  const { playbackRate: globalPlaybackRate } = Internals22.usePlaybackRate();
  const preservePitchOnPlayback = useContext4(Internals22.PlaybackPitchContext);
  const sharedAudioContext = useContext4(SharedAudioContext2);
  const buffer = useBufferState2();
  const [mediaVolume] = useMediaVolumeState2();
  const unloopedVolumePropFrame = useFrameForVolumeProp2(loopVolumeCurveBehavior);
  const volumePropFrame = loop && videoConfig ? frameForVolumeProp({
    behavior: loopVolumeCurveBehavior,
    loop,
    assetDurationInSeconds: knownDuration,
    fps: videoConfig.fps,
    frame,
    startsAt: unloopedVolumePropFrame - frame,
    playbackRate,
    trimBefore,
    trimAfter
  }) : unloopedVolumePropFrame;
  const userPreferredVolume = evaluateVolume2({
    frame: volumePropFrame,
    volume,
    mediaVolume
  });
  if (!videoConfig) {
    throw new Error("No video config found");
  }
  warnAboutTooHighVolume2(userPreferredVolume);
  const effectChainState = useEffectChainState(effectsOutputSize);
  const effectsRef = useRef3(effects);
  effectsRef.current = effects;
  const onErrorRef = useRef3(onError);
  onErrorRef.current = onError;
  const disallowFallbackRef = useRef3(disallowFallbackToOffthreadVideo);
  disallowFallbackRef.current = disallowFallbackToOffthreadVideo;
  const effectChainStateRef = useRef3(effectChainState);
  effectChainStateRef.current = effectChainState;
  const maxCanvasSinkFrameSizeRef = useRef3(maxCanvasSinkFrameSize);
  maxCanvasSinkFrameSizeRef.current = maxCanvasSinkFrameSize;
  const parentSequence = useContext4(SequenceContext2);
  const isPremounting = Boolean(parentSequence?.premounting);
  const isPostmounting = Boolean(parentSequence?.postmounting);
  const sequenceOffset = (parentSequence?.absoluteFrom ?? 0) / videoConfig.fps;
  const sequencePlaybackRate = parentSequence?.playbackRate ?? 1;
  const effectivePlaybackRate = playbackRate * sequencePlaybackRate;
  const effectiveToneFrequency = (toneFrequency ?? 1) / (preservePitch ? effectivePlaybackRate : 1) / (preservePitchOnPlayback && globalPlaybackRate > 0 ? globalPlaybackRate : 1);
  const sequenceDurationInFrames = videoConfig.durationInFrames / sequencePlaybackRate;
  const currentTime = frame / sequencePlaybackRate / videoConfig.fps;
  const currentTimeRef = useRef3(currentTime);
  currentTimeRef.current = currentTime;
  const preloadedSrc = usePreload2(src);
  const { isMutedForPlayback: effectiveMuted } = useMediaAudioState2({
    muted,
    volume: userPreferredVolume,
    audioEnabled: true
  });
  const mediaPlayerMuted = useMediaPlayerMuted({
    muted,
    volume: userPreferredVolume
  });
  const isPlayerBuffering = useBuffering2();
  const initialPlaying = useRef3(playing && !isPlayerBuffering);
  const initialIsPremounting = useRef3(isPremounting);
  const initialIsPostmounting = useRef3(isPostmounting);
  const initialGlobalPlaybackRate = useRef3(globalPlaybackRate);
  const initialPlaybackRate = useRef3(effectivePlaybackRate);
  const initialToneFrequency = useRef3(effectiveToneFrequency);
  const initialMuted = useRef3(mediaPlayerMuted);
  const initialVolume = useRef3(userPreferredVolume);
  const initialSequenceDuration = useRef3(sequenceDurationInFrames);
  const initialSequenceOffset = useRef3(sequenceOffset);
  const hasDrawnRealFrameRef = useRef3(false);
  const isPremountingRef = useRef3(isPremounting);
  isPremountingRef.current = isPremounting;
  const maxWidth = maxCanvasSinkFrameSize?.width;
  const maxHeight = maxCanvasSinkFrameSize?.height;
  const studioSourceKey = useMemo4(() => isStudio ? JSON.stringify([
    getSharedInputCacheKey({
      src: preloadedSrc,
      credentials,
      requestInit: initialRequestInit
    }),
    maxWidth,
    maxHeight
  ]) : null, [
    credentials,
    initialRequestInit,
    isStudio,
    maxHeight,
    maxWidth,
    preloadedSrc
  ]);
  const copiedFrameRef = useRef3(null);
  const canSharePausedFrame = !shouldFallbackToNativeVideo && terminalError === null && !playing && !loop && !debugOverlay && !onVideoFrame && effects.length === 0 && !_experimentalInitiallyDrawCachedFrame;
  const canSharePausedFrameRef = useRef3(canSharePausedFrame);
  canSharePausedFrameRef.current = canSharePausedFrame;
  const mediaTimeRef = useRef3(mediaTime);
  mediaTimeRef.current = mediaTime;
  const canReceivePausedFrame = canSharePausedFrame && !isPremounting && !isPostmounting && !headless;
  useLayoutEffect3(() => {
    const canvas = canvasRef.current;
    if (!canvas || !studioSourceKey || !canReceivePausedFrame || mediaPlayerRef.current) {
      return;
    }
    for (const [source, previous] of studioVideoCanvases) {
      if (source !== canvas && previous.sourceKey === studioSourceKey && previous.getPlainFrameTime() === mediaTimeRef.current) {
        const context = canvas.getContext("2d");
        if (!context) {
          return;
        }
        canvas.width = source.width;
        canvas.height = source.height;
        context.drawImage(source, 0, 0);
        copiedFrameRef.current = {
          canvas,
          sourceKey: studioSourceKey,
          mediaTime: mediaTimeRef.current,
          width: canvas.width,
          height: canvas.height
        };
        break;
      }
    }
  }, [canReceivePausedFrame, studioSourceKey]);
  useLayoutEffect3(() => {
    if (!_experimentalInitiallyDrawCachedFrame) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const cached = getCachedVideoFrame(src);
    if (!cached) {
      return;
    }
    canvas.width = cached.width;
    canvas.height = cached.height;
    const ctx = canvas.getContext("2d", {
      alpha: true
    });
    if (!ctx) {
      return;
    }
    ctx.drawImage(cached, 0, 0);
  }, [_experimentalInitiallyDrawCachedFrame, src]);
  useLayoutEffect3(() => {
    if (!_experimentalInitiallyDrawCachedFrame) {
      return;
    }
    const currentCanvasRef = canvasRef;
    return () => {
      const canvas = currentCanvasRef.current;
      if (!canvas || !hasDrawnRealFrameRef.current || isPremountingRef.current) {
        return;
      }
      cacheVideoFrame(src, canvas);
    };
  }, [_experimentalInitiallyDrawCachedFrame, src]);
  useEffect2(() => {
    if (sharedAudioContext?.audioContextPending) {
      return;
    }
    const sharedAudioContextForMediaPlayer = sharedAudioContext?.audioContext && sharedAudioContext.gainNode ? {
      audioContext: sharedAudioContext.audioContext,
      gainNode: sharedAudioContext.gainNode,
      audioSyncAnchor: sharedAudioContext.audioSyncAnchor,
      scheduleAudioNode: sharedAudioContext.scheduleAudioNode,
      unscheduleAudioNode: sharedAudioContext.unscheduleAudioNode
    } : null;
    const canvas = canvasRef.current;
    try {
      const player = new MediaPlayer({
        canvas,
        src: preloadedSrc,
        logLevel,
        sharedAudioContext: sharedAudioContextForMediaPlayer,
        loop,
        trimAfter: initialTrimAfterRef.current,
        trimBefore: initialTrimBeforeRef.current,
        fps: videoConfig.fps,
        playbackRate: initialPlaybackRate.current,
        toneFrequency: initialToneFrequency.current,
        audioStreamIndex,
        debugOverlay,
        bufferState: buffer,
        isPremounting: initialIsPremounting.current,
        isPostmounting: initialIsPostmounting.current,
        globalPlaybackRate: initialGlobalPlaybackRate.current,
        durationInFrames: initialSequenceDuration.current,
        onVideoFrameCallback: initialOnVideoFrameRef.current ?? null,
        playing: initialPlaying.current,
        sequenceOffset: initialSequenceOffset.current,
        credentials,
        requestInit: initialRequestInit,
        tagType: "video",
        getEffects: () => effectsRef.current,
        getEffectChainState: (width, height) => effectChainStateRef.current?.get(width, height),
        maxCanvasSinkFrameSize: maxCanvasSinkFrameSizeRef.current,
        onError: (error) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError: onErrorRef.current,
            error,
            disallowFallback: disallowFallbackRef.current,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fallback") {
            setShouldFallbackToNativeVideo(true);
          } else {
            setTerminalError(errorToUse);
          }
        }
      });
      if (canvas && studioSourceKey) {
        const copied = copiedFrameRef.current;
        copiedFrameRef.current = null;
        studioVideoCanvases.set(canvas, {
          sourceKey: studioSourceKey,
          getPlainFrameTime: () => {
            if (!canSharePausedFrameRef.current) {
              return null;
            }
            const copiedTime = copied?.canvas === canvas && copied.sourceKey === studioSourceKey && copied.mediaTime === mediaTimeRef.current && copied.width === canvas.width && copied.height === canvas.height ? copied.mediaTime : null;
            return player.videoIteratorManager ? player.videoIteratorManager.getPlainFrameTime(copiedTime) : copiedTime;
          }
        });
      }
      mediaPlayerRef.current = player;
      player.initialize(currentTimeRef.current, initialMuted.current, initialVolume.current).then((result) => {
        if (result.type === "disposed") {
          return;
        }
        const handleError = (error, fallbackMessage) => {
          const [action, errorToUse] = callOnErrorAndResolve({
            onError: onErrorRef.current,
            error,
            disallowFallback: disallowFallbackToOffthreadVideo,
            isClientSideRendering: false,
            clientSideError: error
          });
          if (action === "fail") {
            throw errorToUse;
          }
          Internals22.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
          setShouldFallbackToNativeVideo(true);
        };
        if (result.type === "unknown-container-format" || result.type === "network-error") {
          throw result.error;
        }
        if (result.type === "cannot-decode") {
          handleError(result.error, `Cannot decode ${preloadedSrc}, falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "cannot-decode-prores") {
          throw new ProResDecoderNotEnabledError(preloadedSrc);
        }
        if (result.type === "no-tracks") {
          handleError(new Error(`No video or audio tracks found for ${preloadedSrc}.`), `No video or audio tracks found for ${preloadedSrc}, falling back to <OffthreadVideo>`);
          return;
        }
        if (result.type === "success") {
          setMediaPlayerReady(true);
          setMediaDurationInSeconds(result.durationInSeconds);
          setKnownDuration(result.durationInSeconds);
          hasDrawnRealFrameRef.current = true;
        }
      }).catch((error) => {
        if (error instanceof ProResDecoderNotEnabledError) {
          onErrorRef.current?.(error);
          throw error;
        }
        const [action, errorToUse] = callOnErrorAndResolve({
          onError: onErrorRef.current,
          error,
          disallowFallback: disallowFallbackToOffthreadVideo,
          isClientSideRendering: false,
          clientSideError: error
        });
        if (action === "fail") {
          throw errorToUse;
        }
        Internals22.Log.error({ logLevel, tag: "@remotion/media" }, "[VideoForPreview] Failed to initialize MediaPlayer", errorToUse);
        setShouldFallbackToNativeVideo(true);
      });
    } catch (error) {
      const [action, errorToUse] = callOnErrorAndResolve({
        error,
        onError: onErrorRef.current,
        disallowFallback: disallowFallbackToOffthreadVideo,
        isClientSideRendering: false,
        clientSideError: error
      });
      if (action === "fail") {
        throw errorToUse;
      }
      Internals22.Log.error({ logLevel, tag: "@remotion/media" }, "[VideoForPreview] MediaPlayer initialization failed", errorToUse);
      setShouldFallbackToNativeVideo(true);
    }
    return () => {
      if (canvas && studioSourceKey) {
        studioVideoCanvases.delete(canvas);
      }
      if (mediaPlayerRef.current) {
        Internals22.Log.trace({ logLevel, tag: "@remotion/media" }, `[VideoForPreview] Disposing MediaPlayer`);
        mediaPlayerRef.current.dispose();
        mediaPlayerRef.current = null;
      }
      setMediaPlayerReady(false);
      setShouldFallbackToNativeVideo(false);
      hasDrawnRealFrameRef.current = false;
    };
  }, [
    audioStreamIndex,
    buffer,
    debugOverlay,
    disallowFallbackToOffthreadVideo,
    logLevel,
    loop,
    preloadedSrc,
    sharedAudioContext,
    videoConfig.fps,
    credentials,
    initialRequestInit,
    setMediaDurationInSeconds,
    studioSourceKey,
    maxCanvasSinkFrameSize?.width,
    maxCanvasSinkFrameSize?.height
  ]);
  warnAboutObjectFitInStyleOrClassName({ style, className, logLevel });
  const classNameValue = useMemo4(() => {
    return [Internals22.OBJECTFIT_CONTAIN_CLASS_NAME, className].filter(Internals22.truthy).join(" ");
  }, [className]);
  useCommonEffects({
    mediaPlayerRef,
    mediaPlayerReady,
    currentTimeRef,
    playing,
    isPlayerBuffering,
    frame,
    trimBefore,
    trimAfter,
    effectiveMuted: mediaPlayerMuted,
    userPreferredVolume,
    playbackRate: effectivePlaybackRate,
    toneFrequency: effectiveToneFrequency,
    globalPlaybackRate,
    fps: videoConfig.fps,
    sequenceOffset,
    loop,
    durationInFrames: sequenceDurationInFrames,
    isPremounting,
    isPostmounting,
    currentTime,
    logLevel,
    label: "VideoForPreview"
  });
  useLayoutEffect3(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setDebugOverlay(debugOverlay);
  }, [debugOverlay, mediaPlayerReady]);
  useLayoutEffect3(() => {
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady) {
      return;
    }
    mediaPlayer.setVideoFrameCallback(onVideoFrame ?? null);
  }, [onVideoFrame, mediaPlayerReady]);
  const lastEffectsTime = useRef3(currentTime);
  useLayoutEffect3(() => {
    const timeChanged = lastEffectsTime.current !== currentTime;
    lastEffectsTime.current = currentTime;
    const mediaPlayer = mediaPlayerRef.current;
    if (!mediaPlayer || !mediaPlayerReady || timeChanged) {
      return;
    }
    mediaPlayer.redrawVideoEffects().catch(() => {});
  }, [
    currentTime,
    effects,
    effectsOutputSize?.width,
    effectsOutputSize?.height,
    mediaPlayerReady,
    mediaPlayerRef
  ]);
  const actualStyle = useMemo4(() => {
    return {
      ...style,
      objectFit: objectFitProp
    };
  }, [objectFitProp, style]);
  if (terminalError) {
    throw terminalError;
  }
  if (shouldFallbackToNativeVideo && !disallowFallbackToOffthreadVideo) {
    return /* @__PURE__ */ jsx4(Html5Video, {
      ...props,
      src,
      style: actualStyle,
      className,
      muted: effectiveMuted,
      volume,
      trimAfter,
      trimBefore,
      playbackRate,
      toneFrequency,
      loopVolumeCurveBehavior,
      name: "<Html5Video> (fallback)",
      loop,
      showInTimeline,
      _remotionInternalStack: _remotionInternalStack ?? undefined,
      ...fallbackOffthreadVideoProps,
      preservePitch: fallbackOffthreadVideoProps?.preservePitch ?? preservePitch ?? true
    });
  }
  if (headless) {
    return null;
  }
  return /* @__PURE__ */ jsx4("canvas", {
    ...props,
    ref: canvasRef,
    style: actualStyle,
    className: classNameValue
  });
};
var VideoForPreview = (props) => {
  const frame = useCurrentFrame3();
  const videoConfig = useVideoConfig3();
  const currentTime = frame / videoConfig.fps;
  const mediaTime = useMemo4(() => getTimeInSeconds({
    unloopedTimeInSeconds: currentTime,
    playbackRate: props.playbackRate,
    loop: props.loop,
    trimBefore: props.trimBefore,
    trimAfter: props.trimAfter,
    mediaDurationInSeconds: Infinity,
    fps: videoConfig.fps,
    ifNoMediaDuration: "infinity",
    src: props.src
  }), [
    currentTime,
    props.loop,
    props.playbackRate,
    props.src,
    videoConfig.fps,
    props.trimBefore,
    props.trimAfter
  ]);
  if (mediaTime === null) {
    return null;
  }
  return /* @__PURE__ */ jsx4(VideoForPreviewAssertedShowing, {
    ...props,
    mediaTime
  });
};

// src/video/video-for-rendering.tsx
import {
  useContext as useContext5,
  useLayoutEffect as useLayoutEffect4,
  useMemo as useMemo5,
  useRef as useRef4,
  useState as useState5
} from "react";
import {
  Internals as Internals23,
  Loop,
  random as random2,
  useCurrentFrame as useCurrentFrame4,
  useDelayRender as useDelayRender2,
  useRemotionEnvironment as useRemotionEnvironment4,
  useVideoConfig as useVideoConfig4
} from "remotion";
import { jsx as jsx5 } from "react/jsx-runtime";
var VideoForRendering = ({
  volume: volumeProp,
  playbackRate,
  src,
  muted,
  loopVolumeCurveBehavior,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  onVideoFrame,
  logLevel,
  loop,
  style,
  className,
  fallbackOffthreadVideoProps,
  audioStreamIndex,
  disallowFallbackToOffthreadVideo,
  _remotionInternalStack,
  toneFrequency,
  preservePitch,
  trimAfterValue,
  trimBeforeValue,
  headless,
  onError,
  credentials,
  requestInit,
  objectFit: objectFitProp,
  effects,
  effectsOutputSize,
  ...props
}) => {
  if (!src) {
    throw new TypeError("No `src` was passed to <Video>.");
  }
  const audioContext = useContext5(Internals23.SharedAudioContext);
  const sampleRate = audioContext?.sampleRate ?? 48000;
  const frame = useCurrentFrame4();
  const absoluteFrame = Internals23.useTimelinePosition();
  const { fps } = useVideoConfig4();
  const { registerRenderAsset, unregisterRenderAsset } = useContext5(Internals23.RenderAssetManager);
  const startsAt = Internals23.useMediaStartsAt();
  const sequenceContext = useContext5(Internals23.SequenceContext);
  const sequencePlaybackRate = sequenceContext?.playbackRate ?? 1;
  const startInVideo = sequenceContext ? sequenceContext.cumulatedFrom + sequenceContext.relativeFrom : 0;
  const id = useMemo5(() => `media-video-${random2(src)}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.relativeFrom}-${sequenceContext?.durationInFrames}`, [
    src,
    sequenceContext?.cumulatedFrom,
    sequenceContext?.relativeFrom,
    sequenceContext?.durationInFrames
  ]);
  const environment = useRemotionEnvironment4();
  const { delayRender, continueRender, cancelRender } = useDelayRender2();
  const canvasRef = useRef4(null);
  const [replaceWithOffthreadVideo, setReplaceWithOffthreadVideo] = useState5(false);
  const [initialRequestInit] = useState5(requestInit);
  const audioEnabled = Internals23.useAudioEnabled();
  const videoEnabled = Internals23.useVideoEnabled();
  const { isMutedForPlayback, shouldUseAudio } = Internals23.useMediaAudioState({
    muted,
    volume: null,
    audioEnabled
  });
  const maxCacheSize = useMaxMediaCacheSize(logLevel);
  const mediaCache = useRenderMediaCache(logLevel);
  const effectChainState = Internals23.useEffectChainState(effectsOutputSize);
  const [error, setError] = useState5(null);
  if (error) {
    throw error;
  }
  useLayoutEffect4(() => {
    if (!canvasRef.current && !headless) {
      return;
    }
    if (replaceWithOffthreadVideo) {
      return;
    }
    if (!canvasRef.current?.getContext && !headless) {
      return setError(new Error("Canvas does not have .getContext() method available. This could be because <Video> was mounted inside an <svg> tag."));
    }
    const timestamp = frame / sequencePlaybackRate / fps;
    const durationInSeconds = 1 / fps;
    let cancelled = false;
    const newHandle = delayRender(`Extracting frame at time ${timestamp} from ${src}`, {
      retries: delayRenderRetries ?? undefined,
      timeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined
    });
    extractFrameViaBroadcastChannel({
      sampleRate,
      src,
      timeInSeconds: timestamp,
      durationInSeconds,
      playbackRate: playbackRate * sequencePlaybackRate,
      logLevel,
      includeAudio: shouldUseAudio,
      includeVideo: videoEnabled,
      isClientSideRendering: environment.isClientSideRendering,
      loop,
      audioStreamIndex,
      trimAfter: trimAfterValue,
      trimBefore: trimBeforeValue,
      fps,
      maxCacheSize,
      credentials,
      requestInit: initialRequestInit,
      mediaCache
    }).then(async (result) => {
      if (cancelled || mediaCache.isDisposed()) {
        if (result.type === "success") {
          result.frame?.close();
        }
        return;
      }
      const handleError = (err, clientSideError, fallbackMessage, mediaDurationInSeconds) => {
        const [action, errorToUse] = callOnErrorAndResolve({
          onError,
          error: err,
          disallowFallback: disallowFallbackToOffthreadVideo,
          isClientSideRendering: environment.isClientSideRendering,
          clientSideError
        });
        if (action === "fail") {
          cancelRender(errorToUse);
          return;
        }
        if (window.remotion_isMainTab) {
          Internals23.Log.warn({ logLevel, tag: "@remotion/media" }, fallbackMessage);
        }
        setReplaceWithOffthreadVideo({
          durationInSeconds: mediaDurationInSeconds
        });
      };
      if ((result.type === "unknown-container-format" || result.type === "network-error") && result.error) {
        handleError(result.error, result.error, `Failed to read ${src}: ${result.error.message}, falling back to <OffthreadVideo>`, null);
        return;
      }
      if (result.type === "unknown-container-format") {
        handleError(new Error(`Unknown container format ${src}.`), new Error(`Cannot render video "${src}": Unknown container format. See supported formats: https://www.remotion.dev/docs/mediabunny/formats`), `Unknown container format for ${src} (Supported formats: https://www.remotion.dev/docs/mediabunny/formats), falling back to <OffthreadVideo>`, null);
        return;
      }
      if (result.type === "cannot-decode") {
        handleError(result.error, new Error(`Cannot render video "${src}": ${result.error.message}`, {
          cause: result.error
        }), `Cannot decode ${src}, falling back to <OffthreadVideo>`, result.durationInSeconds);
        return;
      }
      if (result.type === "cannot-decode-prores") {
        const proresError = new ProResDecoderNotEnabledError(src);
        onError?.(proresError);
        cancelRender(proresError);
        return;
      }
      if (result.type === "cannot-decode-alpha") {
        handleError(new Error(`Cannot decode alpha component for ${src}.`), new Error(`Cannot render video "${src}": The alpha channel could not be decoded by the browser.`), `Cannot decode alpha component for ${src}, falling back to <OffthreadVideo>`, result.durationInSeconds);
        return;
      }
      if (result.type === "network-error") {
        handleError(new Error(`Network error fetching ${src}.`), new Error(`Cannot render video "${src}": Network error while fetching the video (possibly CORS).`), `Network error fetching ${src} (no CORS?), falling back to <OffthreadVideo>`, null);
        return;
      }
      const {
        frame: imageBitmap,
        audio,
        durationInSeconds: assetDurationInSeconds
      } = result;
      if (imageBitmap) {
        onVideoFrame?.(imageBitmap);
        const context = canvasRef.current?.getContext("2d", {
          alpha: true
        });
        if (context) {
          context.canvas.width = imageBitmap.width;
          context.canvas.height = imageBitmap.height;
          context.canvas.style.aspectRatio = `${context.canvas.width} / ${context.canvas.height}`;
          context.drawImage(imageBitmap, 0, 0);
          if (effects.length > 0) {
            const completed = await Internals23.runEffectChain({
              state: effectChainState.get(imageBitmap.width, imageBitmap.height),
              source: context.canvas,
              effects,
              output: context.canvas,
              width: imageBitmap.width,
              height: imageBitmap.height
            });
            if (!completed || cancelled || mediaCache.isDisposed()) {
              imageBitmap.close();
              return;
            }
          }
        }
        imageBitmap.close();
      } else if (videoEnabled) {
        const context = canvasRef.current?.getContext("2d", {
          alpha: true
        });
        if (context) {
          context.clearRect(0, 0, context.canvas.width, context.canvas.height);
        }
      }
      const volumePropsFrame = frameForVolumeProp({
        behavior: loopVolumeCurveBehavior,
        loop,
        assetDurationInSeconds: assetDurationInSeconds ?? 0,
        fps,
        frame,
        startsAt,
        playbackRate,
        trimBefore: trimBeforeValue,
        trimAfter: trimAfterValue
      });
      const volume = Internals23.evaluateVolume({
        volume: volumeProp,
        frame: volumePropsFrame,
        mediaVolume: 1
      });
      Internals23.warnAboutTooHighVolume(volume);
      if (audio && volume > 0) {
        applyVolume(audio.data, volume);
        registerRenderAsset({
          type: "inline-audio",
          id,
          audio: environment.isClientSideRendering ? audio.data : Array.from(audio.data),
          frame: absoluteFrame,
          startInVideo,
          timestamp: audio.timestamp,
          duration: audio.durationInMicroSeconds,
          toneFrequency: toneFrequency / (preservePitch ? playbackRate * sequencePlaybackRate : 1)
        });
      }
      continueRender(newHandle);
    }).catch((err) => {
      if (cancelled || mediaCache.isDisposed()) {
        return;
      }
      cancelRender(err);
    });
    return () => {
      cancelled = true;
      continueRender(newHandle);
      unregisterRenderAsset(id);
    };
  }, [
    sampleRate,
    absoluteFrame,
    continueRender,
    delayRender,
    delayRenderRetries,
    delayRenderTimeoutInMilliseconds,
    environment.isClientSideRendering,
    fps,
    frame,
    id,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    shouldUseAudio,
    onVideoFrame,
    playbackRate,
    sequencePlaybackRate,
    registerRenderAsset,
    src,
    startInVideo,
    startsAt,
    unregisterRenderAsset,
    volumeProp,
    replaceWithOffthreadVideo,
    audioStreamIndex,
    disallowFallbackToOffthreadVideo,
    toneFrequency,
    preservePitch,
    trimAfterValue,
    trimBeforeValue,
    videoEnabled,
    maxCacheSize,
    cancelRender,
    headless,
    onError,
    credentials,
    effectChainState,
    effects,
    initialRequestInit,
    mediaCache
  ]);
  warnAboutObjectFitInStyleOrClassName({ style, className, logLevel });
  const classNameValue = useMemo5(() => {
    return [Internals23.OBJECTFIT_CONTAIN_CLASS_NAME, className].filter(Internals23.truthy).join(" ");
  }, [className]);
  const styleWithObjectFit = useMemo5(() => {
    return {
      ...style,
      objectFit: objectFitProp
    };
  }, [objectFitProp, style]);
  if (replaceWithOffthreadVideo) {
    const fallback = /* @__PURE__ */ jsx5(Internals23.InnerOffthreadVideo, {
      ...props,
      src,
      playbackRate: playbackRate ?? 1,
      muted: isMutedForPlayback,
      acceptableTimeShiftInSeconds: fallbackOffthreadVideoProps?.acceptableTimeShiftInSeconds,
      loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
      delayRenderRetries: delayRenderRetries ?? undefined,
      delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? undefined,
      name: "Fallback to <OffthreadVideo>",
      style: styleWithObjectFit,
      allowAmplificationDuringRender: true,
      transparent: fallbackOffthreadVideoProps?.transparent ?? true,
      toneMapped: fallbackOffthreadVideoProps?.toneMapped ?? true,
      audioStreamIndex: audioStreamIndex ?? 0,
      className,
      onVideoFrame,
      volume: volumeProp,
      id,
      onError: fallbackOffthreadVideoProps?.onError,
      toneFrequency,
      showInTimeline: false,
      crossOrigin: fallbackOffthreadVideoProps?.crossOrigin,
      onAutoPlayError: fallbackOffthreadVideoProps?.onAutoPlayError ?? null,
      pauseWhenBuffering: fallbackOffthreadVideoProps?.pauseWhenBuffering ?? false,
      trimAfter: trimAfterValue,
      durationInFrames: undefined,
      trimBefore: trimBeforeValue,
      useWebAudioApi: fallbackOffthreadVideoProps?.useWebAudioApi ?? false,
      preservePitch: fallbackOffthreadVideoProps?.preservePitch ?? preservePitch ?? true,
      startFrom: undefined,
      endAt: undefined,
      _remotionInternalStack,
      _remotionInternalNativeLoopPassed: false
    });
    if (loop) {
      if (!replaceWithOffthreadVideo.durationInSeconds) {
        const err = new Error(`Cannot render video ${src}: @remotion/media was unable to render, and fell back to <OffthreadVideo>. Also, "loop" was set, but <OffthreadVideo> does not support looping and @remotion/media could also not determine the duration of the video.`);
        cancelRender(err);
        throw err;
      }
      return /* @__PURE__ */ jsx5(Loop, {
        layout: "none",
        durationInFrames: Internals23.calculateMediaDuration({
          trimAfter: trimAfterValue,
          mediaDurationInFrames: replaceWithOffthreadVideo.durationInSeconds * fps,
          playbackRate,
          trimBefore: trimBeforeValue
        }),
        children: fallback
      });
    }
    return fallback;
  }
  if (headless) {
    return null;
  }
  return /* @__PURE__ */ jsx5("canvas", {
    ...props,
    ref: canvasRef,
    style: styleWithObjectFit,
    className: classNameValue
  });
};

// src/video/video.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var {
  validateMediaTrimProps,
  resolveTrimProps,
  validateMediaProps: validateMediaProps2,
  useCropStyle
} = Internals24;
var videoSchema = {
  src: {
    type: "asset",
    assetType: "video",
    default: undefined,
    description: "Source",
    keyframable: false
  },
  ...Internals24.baseSchema,
  trimAfter: Internals24.trimAfterField,
  ...Internals24.premountSchema,
  volume: {
    type: "number",
    min: 0,
    max: 20,
    step: 0.01,
    default: 1,
    description: "Volume",
    hiddenFromList: false
  },
  playbackRate: {
    type: "number",
    min: 0.1,
    step: 0.01,
    default: 1,
    description: "Playback rate",
    hiddenFromList: false,
    keyframable: false
  },
  preservePitch: {
    type: "boolean",
    default: false,
    description: "Preserve pitch",
    keyframable: false
  },
  toneFrequency: {
    type: "number",
    min: 0.01,
    max: 2,
    step: 0.01,
    default: 1,
    description: "Pitch",
    hiddenFromList: false,
    keyframable: false
  },
  muted: { type: "boolean", default: false, description: "Muted" },
  loop: { type: "boolean", default: false, description: "Loop" },
  ...Internals24.transformSchema,
  ...Interactive2.backgroundSchema,
  ...Interactive2.borderSchema,
  ...Interactive2.borderRadiusSchema,
  ...Interactive2.cropSchema
};
var InnerVideo = ({
  src,
  audioStreamIndex,
  className,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  logLevel,
  loop,
  loopVolumeCurveBehavior,
  muted,
  onVideoFrame,
  playbackRate,
  style,
  trimAfter,
  trimBefore,
  volume,
  _remotionInternalStack,
  toneFrequency,
  preservePitch,
  showInTimeline,
  debugOverlay,
  headless,
  onError,
  credentials,
  requestInit,
  controls,
  objectFit,
  maxCanvasSinkFrameSize,
  _experimentalInitiallyDrawCachedFrame,
  effects,
  effectsOutputSize = null,
  setMediaDurationInSeconds,
  ...props
}) => {
  const environment = useRemotionEnvironment5();
  if (typeof src !== "string") {
    throw new TypeError(`The \`<Video>\` tag requires a string for \`src\`, but got ${JSON.stringify(src)} instead.`);
  }
  validateMediaTrimProps({
    startFrom: undefined,
    endAt: undefined,
    trimBefore,
    trimAfter
  });
  const { trimBeforeValue, trimAfterValue } = resolveTrimProps({
    startFrom: undefined,
    endAt: undefined,
    trimBefore,
    trimAfter
  });
  validateToneFrequency({ toneFrequency, component: "Video" });
  if (environment.isRendering) {
    return /* @__PURE__ */ jsx6(VideoForRendering, {
      ...props,
      audioStreamIndex: audioStreamIndex ?? null,
      className,
      delayRenderRetries: delayRenderRetries ?? null,
      delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? null,
      disallowFallbackToOffthreadVideo: disallowFallbackToOffthreadVideo ?? false,
      fallbackOffthreadVideoProps,
      logLevel,
      loop,
      loopVolumeCurveBehavior,
      muted,
      onVideoFrame,
      playbackRate,
      src,
      _remotionInternalStack,
      style,
      volume,
      toneFrequency,
      preservePitch,
      trimAfterValue,
      trimBeforeValue,
      headless,
      onError,
      credentials,
      requestInit,
      objectFit,
      effects,
      effectsOutputSize
    });
  }
  if (maxCanvasSinkFrameSize?.width !== undefined) {
    NoReactInternals.validateDimension(maxCanvasSinkFrameSize.width, "maxCanvasSinkFrameSize.width", "of the <Video /> component");
  }
  if (maxCanvasSinkFrameSize?.height !== undefined) {
    NoReactInternals.validateDimension(maxCanvasSinkFrameSize.height, "maxCanvasSinkFrameSize.height", "of the <Video /> component");
  }
  return /* @__PURE__ */ jsx6(VideoForPreview, {
    ...props,
    setMediaDurationInSeconds,
    audioStreamIndex: audioStreamIndex ?? null,
    className,
    logLevel,
    loop,
    loopVolumeCurveBehavior,
    muted,
    onVideoFrame,
    playbackRate,
    toneFrequency: toneFrequency ?? 1,
    preservePitch,
    src,
    style,
    volume,
    showInTimeline,
    trimAfter: trimAfterValue,
    trimBefore: trimBeforeValue,
    _remotionInternalStack: _remotionInternalStack ?? null,
    disallowFallbackToOffthreadVideo,
    fallbackOffthreadVideoProps,
    debugOverlay: debugOverlay ?? false,
    headless: headless ?? false,
    onError,
    credentials,
    requestInit,
    controls,
    objectFit,
    maxCanvasSinkFrameSize,
    effects,
    effectsOutputSize,
    _experimentalInitiallyDrawCachedFrame
  });
};
var VideoInner = ({
  src,
  audioStreamIndex,
  className,
  delayRenderRetries,
  delayRenderTimeoutInMilliseconds,
  disallowFallbackToOffthreadVideo,
  fallbackOffthreadVideoProps,
  logLevel,
  loop,
  loopVolumeCurveBehavior,
  muted,
  name,
  onVideoFrame,
  playbackRate,
  showInTimeline,
  style,
  trimAfter,
  trimBefore,
  volume,
  toneFrequency,
  preservePitch,
  debugOverlay,
  headless,
  onError,
  credentials,
  requestInit,
  controls,
  objectFit,
  maxCanvasSinkFrameSize,
  _experimentalInitiallyDrawCachedFrame,
  effects,
  effectsOutputSize = null,
  durationInFrames,
  from,
  freeze,
  hidden,
  premountFor,
  postmountFor,
  styleWhilePremounted,
  styleWhilePostmounted,
  cropLeft,
  cropRight,
  cropTop,
  cropBottom,
  ...props
}) => {
  validateMediaProps2({ playbackRate, preservePitch, volume }, "Video");
  const sourceStack = controls ? Internals24.getStackForControls(controls) ?? undefined : undefined;
  const fallbackLogLevel = Internals24.useLogLevel();
  const mediaStartsAt = Internals24.useMediaStartsAt();
  const videoConfig = useVideoConfig5();
  const effectiveTrimAfter = getMediaTrimAfter({
    durationInFrames,
    trimAfter,
    trimBefore
  });
  const videoSequenceDuration = getVideoSequenceDuration({
    durationInFrames,
    loop: loop ?? false,
    playbackRate: playbackRate ?? 1,
    trimAfter: effectiveTrimAfter,
    trimBefore
  });
  const sequenceDurationInFrames = Math.min(videoSequenceDuration ?? Infinity, Math.max(0, videoConfig.durationInFrames - (from ?? 0)));
  const [mediaDurationInSeconds, setMediaDurationInSeconds] = useState6(null);
  const basicInfo = Internals24.useBasicMediaInTimeline({
    src,
    playbackRate: playbackRate ?? 1,
    trimBefore,
    trimAfter: effectiveTrimAfter,
    sequenceDurationInFrames,
    mediaStartsAt,
    displayName: name ?? "<Video>",
    loop: loop ?? false,
    muted: muted ?? false
  });
  const loopDisplay = useMemo6(() => getLoopDisplay({
    loop: loop ?? false,
    mediaDurationInSeconds,
    playbackRate: playbackRate ?? 1,
    trimAfter: effectiveTrimAfter,
    trimBefore,
    sequenceDurationInFrames,
    compFps: videoConfig.fps
  }), [
    loop,
    mediaDurationInSeconds,
    playbackRate,
    effectiveTrimAfter,
    trimBefore,
    sequenceDurationInFrames,
    videoConfig.fps
  ]);
  const isMedia = useMemo6(() => ({
    type: "video",
    data: basicInfo
  }), [basicInfo]);
  const memoizedEffects = Internals24.useMemoizedEffects({
    effects: effects ?? [],
    overrideId: controls?.overrideId ?? null,
    videoConfigValues: controls?.videoConfigValues ?? null
  });
  const memoizedEffectDefinitions = Internals24.useMemoizedEffectDefinitions(effects ?? []);
  const {
    effectivePostmountFor,
    effectivePremountFor,
    freezeFrame,
    isPremountingOrPostmounting,
    postmountingActive,
    premountingActive,
    premountingStyle
  } = Internals24.usePremounting({
    from: from ?? 0,
    durationInFrames: videoSequenceDuration ?? Infinity,
    premountFor: premountFor ?? null,
    postmountFor: postmountFor ?? null,
    style: style ?? null,
    styleWhilePremounted: styleWhilePremounted ?? null,
    styleWhilePostmounted: styleWhilePostmounted ?? null,
    hideWhilePremounted: "display-none"
  });
  const croppedStyle = useCropStyle({
    cropLeft,
    cropRight,
    cropTop,
    cropBottom,
    style: premountingStyle,
    componentName: "<Video />"
  });
  if (sequenceDurationInFrames === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx6(Freeze2, {
    frame: freezeFrame,
    active: isPremountingOrPostmounting,
    _remotionInternalIsPremounting: premountingActive,
    children: /* @__PURE__ */ jsx6(Sequence2, {
      layout: "none",
      from: from ?? 0,
      durationInFrames: videoSequenceDuration,
      freeze,
      _remotionInternalIsMedia: isMedia,
      _remotionInternalPremountDisplay: effectivePremountFor || null,
      _remotionInternalPostmountDisplay: effectivePostmountFor || null,
      _remotionInternalIsPremounting: premountingActive,
      _remotionInternalIsPostmounting: postmountingActive,
      name: name ?? "<Video>",
      _remotionInternalDocumentationLink: "https://www.remotion.dev/docs/media/video",
      controls,
      _remotionInternalLoopDisplay: loopDisplay,
      _remotionInternalEffects: memoizedEffectDefinitions,
      showInTimeline: showInTimeline ?? true,
      hidden,
      children: /* @__PURE__ */ jsx6(Internals24.SequenceContent, {
        children: /* @__PURE__ */ jsx6(InnerVideo, {
          ...props,
          audioStreamIndex: audioStreamIndex ?? null,
          className,
          delayRenderRetries: delayRenderRetries ?? null,
          delayRenderTimeoutInMilliseconds: delayRenderTimeoutInMilliseconds ?? null,
          disallowFallbackToOffthreadVideo: disallowFallbackToOffthreadVideo ?? false,
          fallbackOffthreadVideoProps: fallbackOffthreadVideoProps ?? {},
          logLevel: logLevel ?? fallbackLogLevel,
          loop: loop ?? false,
          loopVolumeCurveBehavior: loopVolumeCurveBehavior ?? "repeat",
          muted: muted ?? false,
          onVideoFrame,
          playbackRate: playbackRate ?? 1,
          showInTimeline: false,
          src,
          style: croppedStyle ?? {},
          trimAfter: effectiveTrimAfter,
          trimBefore,
          volume: volume ?? 1,
          toneFrequency: toneFrequency ?? 1,
          preservePitch: preservePitch ?? null,
          _remotionInternalStack: sourceStack,
          debugOverlay: debugOverlay ?? false,
          headless: headless ?? false,
          onError,
          credentials,
          requestInit,
          controls,
          objectFit: objectFit ?? "contain",
          maxCanvasSinkFrameSize: maxCanvasSinkFrameSize ?? null,
          _experimentalInitiallyDrawCachedFrame: _experimentalInitiallyDrawCachedFrame ?? false,
          effects: memoizedEffects,
          effectsOutputSize,
          setMediaDurationInSeconds
        })
      })
    })
  });
};
var Video = Interactive2.withSchema({
  Component: VideoInner,
  componentName: "<Video>",
  componentIdentity: "dev.remotion.media.Video",
  schema: videoSchema,
  supportsEffects: true
});

// src/index.ts
var experimental_Audio = Audio;
var experimental_Video = Video;
export {
  Audio,
  AudioForPreview,
  Video,
  experimental_Audio,
  experimental_Video,
  getTargetSampleRate
};
